import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Drawer, Modal } from "antd";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import GooglePlacesAutocompleteInput from "../../common/GooglePlacesAutocompleteInput";
import { CareShell, Panel } from "../../components/care/CareLayout";
import CareRichTextEditor from "../../components/care/CareRichTextEditor";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import useAppConfig from "../../hooks/access-control/useAppConfig";
import useWorkspaceMembers, {
  getWorkspaceMemberDisplayName,
  getWorkspaceMemberEmail,
} from "../../hooks/workspaces/useWorkspaceMembers";
import { compressImageToTarget, useFileUpload } from "../../hooks/useFileUpload";
import { careService } from "../../services/careService";
import { addressFieldsFromPlaceDetails } from "../../utils/googlePlaces";
import { conversionWarningFor, convertHeicToJpegResult } from "../../utils/heicConversion";
import {
  downloadWeeklyReportPdf,
  weeklyReportEditorValuesFor,
  weeklyReportParagraphFor,
  weeklyReportSectionDefinitions,
  weeklyReportSectionsFor,
  weeklyReportTitleFor,
  weeklyReportWithEdits,
} from "../../utils/weeklyReportPdf";
import AssessmentRecordDetailPage from "./AssessmentRecordDetailPage";
import AssessmentExtractionDrawer from "../../components/care/AssessmentExtractionDrawer";
import DailyNoteList from "../../components/care/DailyNoteList";
import NewResidentPage from "./NewResidentPage";
import {
  formatDate,
  processId,
  processResidentId,
  processResidentName,
  processSocialWorkerName,
  processStatusClass,
} from "./AssessmentsPage";

const normalizeStageKey = (value) => String(value || "").trim().replace(/[\s-]+/g, "_").toUpperCase();
const stageBookmarkSlug = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const assessmentStageHashKey = (stage = {}, patientType = "NEW") =>
  stageBookmarkSlug(patientType === "RESIDENT" && stage.existingLabel ? stage.existingLabel : stage.label || stage.stageKeys?.[0]);

const assessmentStageKeyFromHash = (hashValue = "", stages = [], patientType = "NEW") => {
  const hash = stageBookmarkSlug(String(hashValue).replace(/^#/, ""));
  if (!hash) return "";
  const match = stages.find((stage) => {
    const labels = [
      stage.label,
      stage.existingLabel,
      assessmentStageHashKey(stage, patientType),
      ...(stage.stageKeys || []),
    ];
    return labels.some((label) => stageBookmarkSlug(label) === hash);
  });
  return match ? normalizeStageKey(match.stageKeys?.[0]) : "";
};

const wait = (milliseconds) => new Promise((resolve) => {
  setTimeout(resolve, milliseconds);
});

const SavedNotice = ({ children }) => (
  <p className="form-success transient-save-success">
    <i className="ti ti-circle-check" />
    <span>{children}</span>
  </p>
);

const setAssessmentStageHash = (stage = {}, patientType = "NEW") => {
  if (typeof window === "undefined") return;
  const slug = assessmentStageHashKey(stage, patientType);
  if (!slug) return;
  const nextUrl = `${window.location.pathname}${window.location.search}#${slug}`;
  window.history.replaceState(null, "", nextUrl);
};

const linkedAssessmentProcessIdFrom = (assessment = {}, assessmentId = "", resourceType = "", preview = null) =>
  assessment?.assessmentProcessId ||
  assessment?.processId ||
  preview?.assessmentProcessId ||
  preview?.previewPayload?.assessmentProcessId ||
  (resourceType === "assessmentProcess" ? processId(assessment) || assessmentId : "");

const assessmentDateValue = (assessment = {}) =>
  assessment?.processStartDate ||
  assessment?.assessmentDate ||
  assessment?.startDate ||
  assessment?.createdAt ||
  assessment?.updatedAt ||
  "";

const latestAssessmentForResident = (assessments = []) =>
  [...(Array.isArray(assessments) ? assessments : [])].sort((a, b) => {
    const dateA = new Date(assessmentDateValue(a) || 0).getTime();
    const dateB = new Date(assessmentDateValue(b) || 0).getTime();
    return dateB - dateA;
  })[0] || null;

const residentNameForCurrentAssessment = (resident = {}) =>
  resident.name ||
  resident.fullName ||
  resident.residentName ||
  resident.residentFullName ||
  resident.clientName ||
  [resident.firstName, resident.middleName, resident.lastName].filter(Boolean).join(" ") ||
  "Unnamed resident";

const activeAssessmentProcessForResident = (processes = []) =>
  latestAssessmentForResident(
    (Array.isArray(processes) ? processes : []).filter((process) =>
      process && !closedWorkflowStatuses.includes(workflowStatusValueFor(process))
    ),
  );

const meetAndGreetQuestions = [
  "What daily routines make the resident feel most comfortable?",
  "What support does the resident need with transfers, mobility, or walking?",
  "What medications, allergies, behaviors, or health concerns should the provider know?",
  "What foods, activities, sleep routines, or care approaches does the resident prefer?",
  "Who should the care team contact for urgent family or guardian updates?",
];

const meetAndGreetRelationshipOptions = [
  "Spouse",
  "Daughter",
  "Son",
  "Parent",
  "Sibling",
  "Grandchild",
  "Emergency Contact",
  "Power of Attorney",
  "Legal Guardian",
  "Friend",
  "Case Manager",
  "Social Worker / Case Manager",
  "Financial Worker",
  "Nurse Delegator",
  "Pharmacy",
  "Primary Care Doctor",
  "Specialist",
  "Other",
];

const emptyMeetAndGreetForm = {
  meetingDate: "",
  location: "",
  facilitator: "",
  facilitatorUserId: "",
  notes: "",
};

const dailyNoteCategories = [
  "Behavior",
  "Medication",
  "Health monitoring",
  "Mobility",
  "ADLs and personal care",
  "Toileting and continence",
  "Meals and hydration",
  "Safety and supervision",
  "Communication and sensory",
  "Activities and engagement",
  "Resident preferences",
  "Family update",
  "Care discrepancy",
  "Other",
];

const dailyNoteFrequencyOptions = [
  "N/A",
  "1 time/day",
  "2 times/day",
  "3 times/day",
  "More than 3 times/day",
  "1 day/week",
  "2 days/week",
  "3 days/week",
  "More than 3 days/week",
  "1 time/week",
  "2 times/week",
  "3 times/week",
  "More than 3 times/week",
  "1 time/month",
  "2 times/month",
  "3 times/month",
  "More than 3 times/month",
];

const dailyNoteMagnitudeOptions = [
  "N/A",
  "None",
  "Mild",
  "Moderate",
  "Significant",
  "Severe",
];

const dailyNoteTrendOptions = [
  "N/A",
  "Improved",
  "Worsened",
  "No change",
  "New",
  "Resolved",
];

const cbhsBehaviorSelectionItems = [
  "Multiple assaults related to a behavioral-health condition",
  "Physical aggression",
  "Physically aggressive",
  "Hitting, kicking, punching or biting",
  "Pushing, grabbing or scratching",
  "Throwing objects",
  "Attempts or threats to injure others",
  "Self-endangering behavior likely to cause bodily harm",
  "Self-harm or suicidal behavior",
  "Head banging, cutting or striking self",
  "Unsafe elopement or entering traffic",
  "Ingesting unsafe substances",
  "Removing medically necessary equipment",
  "Intrusiveness that places the person at risk of assault by others",
  "Rummaging",
  "Entering other residents' rooms",
  "Taking belongings",
  "Unawareness of personal boundaries",
  "Following, touching or provoking others",
  "Chronic psychiatric symptoms requiring monitoring or redirection",
  "Hallucinations, delusions or paranoia",
  "Psychosis or responding to internal stimuli",
  "Severe agitation, mania or anxiety",
  "Disorganized thinking or emotional dysregulation",
  "Symptoms escalating toward crisis",
  "Sexual inappropriateness compromising safety",
  "Unwanted sexual touching",
  "Sexual aggression or intrusive sexual conduct",
  "Public exposure or masturbation",
  "Sexual boundary violations",
  "Entering another resident's room for sexual purposes",
];

const dailyNoteQuickSelectionsByCategory = {
  Behavior: [
    "Calm/cooperative",
    "Confused",
    "Agitated",
    "Moody",
    "Forgetful",
    "Verbally aggressive",
    "Physically aggressive",
    "Wandering",
    "Exit-seeking/elopement concern",
    "Refused care",
    "Responded well to redirection",
    "Required reassurance",
    "One-on-one support helpful",
    ...cbhsBehaviorSelectionItems,
  ],
  Medication: [
    "Medication taken",
    "Medication refused",
    "Taken with prompting",
    "Side effects observed",
    "Nurse delegation completed",
    "PCP notified",
    "Family notified",
    "No medication concerns",
  ],
  "Health monitoring": [
    "Pain reported",
    "No pain reported",
    "Vitals checked",
    "Blood sugar checked",
    "Weight monitored",
    "Wound care completed",
    "Oxygen used",
    "Therapy visit completed",
    "New health concern observed",
  ],
  Mobility: [
    "Ambulated independently",
    "Ambulated with 1 assist",
    "Ambulated with 2 assist",
    "Used walker",
    "Used wheelchair",
    "Transfer with 1 assist",
    "Transfer with 2 assist",
    "Bed mobility with assist",
    "Fatigued during mobility",
    "Unsteady gait observed",
    "No mobility change",
  ],
  "ADLs and personal care": [
    "Dressing assisted",
    "Bathing assisted",
    "Total care provided",
    "Grooming completed",
    "Mouth care completed",
    "Shaving completed",
    "Skin check completed",
    "Refused personal care",
    "Needed prompts for ADLs",
    "No ADL concerns",
  ],
  "Toileting and continence": [
    "Incontinent episode",
    "Toileting assistance provided",
    "Bathroom used",
    "Bedside commode used",
    "Bowel movement recorded",
    "Constipation concern",
    "Diarrhea concern",
    "Toileting schedule followed",
    "Skin cleaned/protected",
    "No toileting concerns",
  ],
  "Meals and hydration": [
    "Ate well",
    "Poor appetite",
    "Needed feeding assistance",
    "Total feeding support",
    "Fluids encouraged",
    "Hydration offered",
    "Favorite food accepted",
    "Refused meal",
    "Snack provided",
    "Special diet followed",
    "Difficulty chewing/swallowing",
    "No nutrition concerns",
  ],
  "Safety and supervision": [
    "Fall risk observed",
    "Required evacuation assistance",
    "Door alarm used",
    "Location checks completed",
    "Needed close supervision",
    "Unsafe transfer attempt",
    "Room/environment checked",
    "Clear pathways maintained",
    "Bed low/call light within reach",
    "No safety concerns observed",
  ],
  "Communication and sensory": [
    "Communicated clearly",
    "Confused conversation",
    "Mumbled speech",
    "Hearing difficulty observed",
    "Vision support needed",
    "Glasses used",
    "Did not use phone",
    "Responded to simple cues",
    "Responded better to calm approach",
    "No communication concerns",
  ],
  "Activities and engagement": [
    "Participated in activity",
    "Preferred one-on-one interaction",
    "Enjoyed music",
    "Enjoyed singing",
    "Took walk",
    "Visited with others",
    "Pet-related interest noted",
    "Declined group activity",
    "Quiet time preferred",
  ],
  "Resident preferences": [
    "Preferred dining room",
    "Preferred bedroom/rest",
    "Requested specific food/snack",
    "Requested music",
    "Requested walk/outdoor time",
    "Religious/spiritual preference honored",
    "Preferred family contact",
    "Routine preference noted",
    "New preference discovered",
  ],
  "Family update": [
    "Family visit",
    "Family call",
    "PoA/guardian update",
    "Care coordinator update",
    "Family concern reported",
    "Family preference captured",
    "Follow-up needed",
  ],
  "Care discrepancy": [
    "Change from assessment observed",
    "New care need identified",
    "Behavior differs from assessment",
    "Mobility differs from assessment",
    "Eating pattern differs from assessment",
    "Medication concern differs from assessment",
    "Family/provider follow-up needed",
    "Add to preliminary plan",
    "Add to negotiated care plan",
  ],
  Other: [],
};

const DAILY_NOTE_VISIBLE_SELECTION_COUNT = 12;

const dailyNoteCategorySearchTags = {
  Behavior: ["cbhs", "mental health", "psych", "psychiatric", "mood", "risk", "aggression", "behavioral"],
  Medication: ["emar", "mar", "meds", "rx", "drug", "pharmacy", "refusal"],
  "Health monitoring": ["vitals", "blood pressure", "bp", "pulse", "temperature", "blood sugar", "glucose", "pain", "weight"],
  Mobility: ["walking", "walk", "transfer", "ambulation", "gait", "wheelchair", "walker", "fall"],
  "ADLs and personal care": ["activities of daily living", "personal hygiene", "bathing", "dressing", "grooming", "skin"],
  "Toileting and continence": ["bathroom", "bowel", "bladder", "incontinence", "continence", "commode", "bm"],
  "Meals and hydration": ["food", "nutrition", "eating", "drink", "fluids", "swallowing", "appetite"],
  "Safety and supervision": ["supervision", "monitoring", "risk", "fall", "alarm", "environment", "evacuation"],
  "Communication and sensory": ["speech", "hearing", "vision", "glasses", "cues", "language"],
  "Activities and engagement": ["activity", "music", "walk", "social", "group", "engagement"],
  "Resident preferences": ["preference", "routine", "likes", "choice", "favorite"],
  "Family update": ["family", "poa", "guardian", "representative", "call", "visit"],
  "Care discrepancy": ["change", "difference", "assessment", "mag", "care plan", "ncp", "new need"],
};

const dailyNoteSelectionSearchTags = {
  "Calm/cooperative": ["stable", "settled", "pleasant", "cooperative", "baseline"],
  Confused: ["disoriented", "not oriented", "forgetful", "cognitive", "memory"],
  Agitated: ["restless", "upset", "irritable", "angry", "escalated"],
  Moody: ["mood swings", "irritable", "tearful", "sad", "emotional"],
  Forgetful: ["memory", "short-term", "repeats", "cognitive"],
  "Verbally aggressive": ["yelling", "shouting", "threatening", "cussing", "verbal abuse"],
  "Physically aggressive": ["assault", "hit", "kick", "punch", "bite", "push", "grab", "scratch"],
  Wandering: ["pacing", "roaming", "walkabout", "exit seeking", "elopement"],
  "Exit-seeking/elopement concern": ["elope", "left home", "door", "outside", "traffic", "unsafe exit"],
  "Refused care": ["refusal", "declined", "resistive", "noncompliance", "would not"],
  "Responded well to redirection": ["redirect", "cue", "de-escalate", "calmed", "reassurance"],
  "Required reassurance": ["comfort", "support", "calming", "anxiety", "cueing"],
  "One-on-one support helpful": ["1:1", "one to one", "dedicated staffing", "close supervision", "line of sight"],
  "Multiple assaults related to a behavioral-health condition": ["assaultive", "attack", "violent", "physical aggression", "harm others"],
  "Physical aggression": ["assault", "violent", "combative", "hit", "kick", "punch", "bite"],
  "Hitting, kicking, punching or biting": ["hit", "kick", "punch", "bite", "strike", "assault"],
  "Pushing, grabbing or scratching": ["push", "grab", "scratch", "shove", "pull"],
  "Throwing objects": ["throw", "threw", "items", "objects", "property"],
  "Attempts or threats to injure others": ["threat", "harm others", "injure", "attack", "assault"],
  "Self-endangering behavior likely to cause bodily harm": ["self danger", "unsafe", "harm self", "danger to self"],
  "Self-harm or suicidal behavior": ["suicide", "suicidal", "self injury", "self injurious", "harm self"],
  "Head banging, cutting or striking self": ["headbang", "cutting", "strike self", "hitting self", "self injury"],
  "Unsafe elopement or entering traffic": ["elope", "exit", "run away", "traffic", "road", "street"],
  "Ingesting unsafe substances": ["eat unsafe", "swallow", "poison", "chemicals", "pica"],
  "Removing medically necessary equipment": ["pulling tube", "oxygen", "iv", "catheter", "medical device"],
  "Intrusiveness that places the person at risk of assault by others": ["intrusive", "provoking", "boundaries", "peer conflict"],
  Rummaging: ["searching", "digging", "going through belongings"],
  "Entering other residents' rooms": ["wrong room", "room intrusion", "resident room", "bedroom"],
  "Taking belongings": ["stealing", "property", "items", "possessions"],
  "Unawareness of personal boundaries": ["boundaries", "too close", "personal space", "intrusive"],
  "Following, touching or provoking others": ["follow", "touch", "provoke", "harass", "peer"],
  "Chronic psychiatric symptoms requiring monitoring or redirection": ["mental health", "psych", "psychiatric", "monitoring"],
  "Hallucinations, delusions or paranoia": ["hallucinating", "seeing things", "hearing voices", "delusional", "paranoid"],
  "Psychosis or responding to internal stimuli": ["psychotic", "internal stimuli", "voices", "talking to self"],
  "Severe agitation, mania or anxiety": ["manic", "anxious", "panic", "restless", "escalation"],
  "Disorganized thinking or emotional dysregulation": ["disorganized", "dysregulated", "labile", "confused thoughts"],
  "Symptoms escalating toward crisis": ["crisis", "escalation", "decompensation", "unsafe"],
  "Sexual inappropriateness compromising safety": ["sexual behavior", "inappropriate", "boundary", "safety"],
  "Unwanted sexual touching": ["sexual touch", "groping", "unwanted contact"],
  "Sexual aggression or intrusive sexual conduct": ["sexual aggression", "sexual intrusion", "assaultive sexual"],
  "Public exposure or masturbation": ["disrobing", "exposure", "masturbation", "undressing"],
  "Sexual boundary violations": ["sexual boundary", "inappropriate sexual", "privacy"],
  "Entering another resident's room for sexual purposes": ["sexual room intrusion", "bedroom", "resident room"],
};

const dailyNoteSearchText = (...values) =>
  values.flatMap((value) => (Array.isArray(value) ? value : [value])).filter(Boolean).join(" ").toLowerCase();

const dailyNoteSelectionGroups = (groups, category, query, showAll) => {
  const text = String(query || "").trim().toLowerCase();
  const groupRows = Object.entries(groups).map(([groupCategory, selections]) => {
    const categoryMatches = text && dailyNoteSearchText(groupCategory, dailyNoteCategorySearchTags[groupCategory]).includes(text);
    const matchingSelections = (selections || [])
      .filter((selection) => !text || categoryMatches || dailyNoteSearchText(selection, dailyNoteSelectionSearchTags[selection]).includes(text))
      .map((selection) => ({
        category: groupCategory,
        label: selection,
        path: `${groupCategory} > ${selection}`,
      }));
    return {
      category: groupCategory,
      selections: text ? matchingSelections : matchingSelections.filter((selection) => selection.category === category),
    };
  }).filter((group) => group.selections.length);

  if (text || showAll) return groupRows;

  let remaining = DAILY_NOTE_VISIBLE_SELECTION_COUNT;
  return groupRows.map((group) => {
    const selections = group.selections.slice(0, remaining);
    remaining -= selections.length;
    return { ...group, selections };
  }).filter((group) => group.selections.length);
};

const dailyNoteSelectionCount = (groups, category, query) => {
  const text = String(query || "").trim().toLowerCase();
  return dailyNoteSelectionGroups(groups, category, text, true)
    .reduce((total, group) => total + group.selections.length, 0);
};

const normalizeDailyNoteQuickSelections = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) => {
      if (typeof item === "string") {
        return {
          label: item,
          category: "",
          frequency: "",
          magnitude: "",
          trend: "",
        };
      }
      return {
        label: item.label || item.value || item.name || "",
        category: item.category || item.group || item.section || "",
        frequency: item.frequency || "",
        magnitude: item.magnitude || "",
        trend: item.trend || "",
      };
    })
    .filter((item) => item.label);

const dailyNoteEvidenceSelections = (note = {}) => {
  const rawSelections = [
    note.quickSelections,
    note.selections,
    note.selectedSelections,
    note.selectedCategories,
    note.categorySelections,
    note.behaviorSelections,
    note.qualifyingSelections,
  ].flatMap((value) => Array.isArray(value) ? value : []);
  const fallbackCategory = note.category || note.type || "";
  const seen = new Set();
  return normalizeDailyNoteQuickSelections(rawSelections)
    .map((selection) => ({
      ...selection,
      category: selection.category || fallbackCategory,
    }))
    .filter((selection) => {
      const key = `${selection.category}::${selection.label}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const dailyNoteQuickSelectionText = (selection = {}) =>
  [
    selection.label,
    selection.frequency,
    selection.magnitude,
    selection.trend,
  ].filter(Boolean).join(" - ");

const cbhsTierStandards = [
  { tier: 6, hours: "20.1-24.0 hours/day", label: "Constant monitoring", language: "Constant one-to-one monitoring and intervention, or regular episodes requiring multiple staff; imminent risk of harm without this level of staffing." },
  { tier: 5, hours: "15.1-20.0 hours/day", label: "Daily high-risk intervention", language: "Daily one-to-one intervention; imminent risk of harm without this level of staffing, even in a structured setting." },
  { tier: 4, hours: "10.1-15.0 hours/day", label: "Structured-setting intervention", language: "Multiple qualifying behaviors requiring one-to-one intervention even in a structured setting; behavior may increase in frequency, severity, or both without supportive supervision." },
  { tier: 3, hours: "6.1-10.0 hours/day", label: "Frequent redirection and cueing", language: "Multiple qualifying behaviors at a frequency and intensity requiring one-to-one staffing to redirect, engage, de-escalate and cue; risk of recurrence or increased frequency/severity without support." },
];

const highRiskBehaviorLabels = new Set([
  "Multiple assaults related to a behavioral-health condition",
  "Physical aggression",
  "Physically aggressive",
  "Hitting, kicking, punching or biting",
  "Pushing, grabbing or scratching",
  "Attempts or threats to injure others",
  "Self-endangering behavior likely to cause bodily harm",
  "Self-harm or suicidal behavior",
  "Head banging, cutting or striking self",
  "Unsafe elopement or entering traffic",
  "Removing medically necessary equipment",
  "Symptoms escalating toward crisis",
  "Sexual inappropriateness compromising safety",
  "Unwanted sexual touching",
  "Sexual aggression or intrusive sexual conduct",
  "Public exposure or masturbation",
  "Sexual boundary violations",
  "Entering another resident's room for sexual purposes",
]);

const qualifyingDailyNoteBehaviorLabels = new Set([
  ...cbhsBehaviorSelectionItems,
  "Verbally aggressive",
  "Physically aggressive",
  "Wandering",
  "Exit-seeking/elopement concern",
  "Refused care",
  "One-on-one support helpful",
]);

const dailyNoteBehaviorSelectionLabels = new Set(dailyNoteQuickSelectionsByCategory.Behavior || []);

const behaviorAnalysisKeywords = {
  oneToOne: ["1:1", "one-to-one", "one to one", "dedicated staff", "dedicated staffing", "line-of-sight", "line of sight", "close supervision", "constant monitoring"],
  multipleStaff: ["two staff", "2 staff", "multiple staff", "second caregiver", "two caregivers", "2 caregivers"],
  imminentRisk: ["imminent", "harm", "injure", "traffic", "unsafe", "crisis", "hospital", "police", "911", "emergency"],
  nighttime: ["night", "overnight", "bedtime", "sleep", "late evening"],
};

const cbhsTierDocumentationIndicators = [
  {
    tier: 3,
    indicators: [
      { key: "multipleQualifyingBehaviors", label: "Multiple qualifying behaviors throughout the day", weight: 25 },
      { key: "frequentRedirection", label: "Frequent redirection and dedicated one-to-one engagement", weight: 25 },
      { key: "recurrenceWithoutSupport", label: "Behaviors recur or worsen when direct staff support stops", weight: 25 },
      { key: "dailyTimeRecordsTier3", label: "Daily time records substantiate 6.1-10.0 hours", weight: 25 },
    ],
  },
  {
    tier: 4,
    indicators: [
      { key: "muchOfWakingDay", label: "One-to-one intervention is needed for much of the waking day", weight: 25 },
      { key: "structuredEnvironmentPersistence", label: "Behavior persists even in a highly structured environment", weight: 25 },
      { key: "generalStaffingInsufficient", label: "General household staffing cannot safely meet the need", weight: 25 },
      { key: "dailyTimeRecordsTier4", label: "Daily time records substantiate 10.1-15.0 hours", weight: 25 },
    ],
  },
  {
    tier: 5,
    indicators: [
      { key: "nearlyAllWakingHours", label: "Daily one-to-one intervention during nearly all waking hours", weight: 25 },
      { key: "highFrequencyImminentRisk", label: "High-frequency or high-intensity behavior creates imminent risk", weight: 25 },
      { key: "structuredSettingDoesNotControlRisk", label: "Structured setting alone does not control the risk", weight: 25 },
      { key: "dailyTimeRecordsTier5", label: "Daily time records substantiate 15.1-20.0 hours", weight: 25 },
    ],
  },
  {
    tier: 6,
    indicators: [
      { key: "constantMonitoring", label: "Constant one-to-one monitoring and intervention", weight: 20 },
      { key: "daytimeAndNighttime", label: "Behavior occurs during daytime and nighttime hours", weight: 20 },
      { key: "multipleStaffEpisodes", label: "Regular episodes require two or more staff", weight: 20 },
      { key: "cannotSafelyBeLeft", label: "The person cannot safely be left without dedicated supervision", weight: 20 },
      { key: "dailyTimeRecordsTier6", label: "Daily time records substantiate 20.1-24.0 hours", weight: 20 },
    ],
  },
];

const behaviorTierIndicatorMatchers = {
  multipleQualifyingBehaviors: ({ qualifyingLabels, analyzedNotes }) => qualifyingLabels.size >= 2 && analyzedNotes.length >= 2,
  frequentRedirection: ({ oneToOneHits, analyzedNotes }) => oneToOneHits > 0 || analyzedNotes.some((item) => item.text.includes("redirection") || item.text.includes("redirect") || item.text.includes("cue")),
  recurrenceWithoutSupport: ({ analyzedNotes, severeCount }) => severeCount > 0 || analyzedNotes.some((item) => item.text.includes("recur") || item.text.includes("worsen") || item.text.includes("without support") || item.text.includes("support stops")),
  dailyTimeRecordsTier3: ({ documentedSupportHours }) => documentedSupportHours >= 6.1 && documentedSupportHours <= 10,
  muchOfWakingDay: ({ oneToOneHits, dayCount, documentedSupportHours }) => oneToOneHits >= 2 || dayCount >= 3 || documentedSupportHours >= 10.1,
  structuredEnvironmentPersistence: ({ analyzedNotes }) => analyzedNotes.some((item) => item.text.includes("structured") || item.text.includes("routine") || item.text.includes("environment") || item.text.includes("still") || item.text.includes("persists")),
  generalStaffingInsufficient: ({ multipleStaffHits, analyzedNotes }) => multipleStaffHits > 0 || analyzedNotes.some((item) => item.text.includes("cannot safely") || item.text.includes("general staffing") || item.text.includes("staffing cannot") || item.text.includes("additional staff")),
  dailyTimeRecordsTier4: ({ documentedSupportHours }) => documentedSupportHours >= 10.1 && documentedSupportHours <= 15,
  nearlyAllWakingHours: ({ oneToOneHits, dayCount, documentedSupportHours }) => documentedSupportHours >= 15.1 || (oneToOneHits >= 3 && dayCount >= 4),
  highFrequencyImminentRisk: ({ imminentRiskHits, highRiskCount, severeCount }) => imminentRiskHits >= 2 || highRiskCount >= 3 || severeCount >= 3,
  structuredSettingDoesNotControlRisk: ({ analyzedNotes }) => analyzedNotes.some((item) => item.text.includes("structured setting") || item.text.includes("does not control") || item.text.includes("despite structure") || item.text.includes("continues despite")),
  dailyTimeRecordsTier5: ({ documentedSupportHours }) => documentedSupportHours >= 15.1 && documentedSupportHours <= 20,
  constantMonitoring: ({ oneToOneHits, documentedSupportHours, analyzedNotes }) => documentedSupportHours >= 20.1 || oneToOneHits >= 4 || analyzedNotes.some((item) => item.text.includes("constant monitoring") || item.text.includes("continuous one-to-one") || item.text.includes("continuous 1:1")),
  daytimeAndNighttime: ({ nighttimeHits, hasDaytimeEvidence }) => nighttimeHits > 0 && hasDaytimeEvidence,
  multipleStaffEpisodes: ({ multipleStaffHits }) => multipleStaffHits >= 2,
  cannotSafelyBeLeft: ({ analyzedNotes, imminentRiskHits }) => imminentRiskHits >= 3 || analyzedNotes.some((item) => item.text.includes("cannot safely be left") || item.text.includes("unsafe alone") || item.text.includes("left unattended")),
  dailyTimeRecordsTier6: ({ documentedSupportHours }) => documentedSupportHours >= 20.1,
};

const supportMinutesFromNote = (note = {}) => {
  const explicitMinutes = Number(note.oneToOneMinutes || note.dedicatedSupportMinutes || note.supportMinutes || note.staffingMinutes || 0);
  if (Number.isFinite(explicitMinutes) && explicitMinutes > 0) return explicitMinutes;
  const start = new Date(note.startTimestamp || note.rangeStartTimestamp || note.startedAt || note.date || note.happenedAt || note.occurredAt || "");
  const end = new Date(note.endTimestamp || note.rangeEndTimestamp || note.endedAt || "");
  if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end > start) {
    return Math.round((end.getTime() - start.getTime()) / 60000);
  }
  return 0;
};

const noteTextForBehaviorAnalysis = (note = {}) =>
  [
    note.category,
    note.title,
    note.summary,
    stripHtmlText(note.notes),
    stripHtmlText(note.interventionAction || note.intervention || note.actionTaken),
    dailyNoteEvidenceSelections(note).map(dailyNoteQuickSelectionText).join(" "),
  ].filter(Boolean).join(" ").toLowerCase();

const behaviorAnalysisKeywordHits = (text = "") =>
  Object.fromEntries(Object.entries(behaviorAnalysisKeywords).map(([key, words]) => [key, words.filter((word) => text.includes(word))]));

const behaviorAnalysisTopCounts = (values = [], limit = 8) =>
  Object.entries(values.reduce((counts, value) => {
    const label = String(value || "").trim();
    if (!label) return counts;
    return { ...counts, [label]: (counts[label] || 0) + 1 };
  }, {}))
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([label, count]) => ({ label, count }));

const behaviorLabelsFoundInText = (text = "") =>
  [...qualifyingDailyNoteBehaviorLabels].filter((label) => dailyNoteSearchText(label).includes("aggressive")
    ? text.includes(label.toLowerCase())
    : text.includes(label.toLowerCase()) || text.includes(`behavior > ${label.toLowerCase()}`));

const behaviorTierAnalysisForNotes = (notes = []) => {
  const analyzedNotes = (Array.isArray(notes) ? notes : []).map((note) => {
    const selections = dailyNoteEvidenceSelections(note);
    const labels = selections.map((selection) => selection.label);
    const text = noteTextForBehaviorAnalysis(note);
    const category = String(note.category || "").toLowerCase();
    const selectionCategories = selections.map((selection) => String(selection.category || "").toLowerCase());
    const behaviorSelections = labels.filter((label) => dailyNoteBehaviorSelectionLabels.has(label));
    const qualifyingSelections = [...new Set([
      ...labels.filter((label) =>
        qualifyingDailyNoteBehaviorLabels.has(label) ||
        highRiskBehaviorLabels.has(label) ||
      dailyNoteSearchText(label, dailyNoteSelectionSearchTags[label]).includes("aggression") ||
      dailyNoteSearchText(label, dailyNoteSelectionSearchTags[label]).includes("elopement") ||
      dailyNoteSearchText(label, dailyNoteSelectionSearchTags[label]).includes("harm"),
      ),
      ...behaviorLabelsFoundInText(text),
    ])];
    const keywordHits = behaviorAnalysisKeywordHits(text);
    const hasBehaviorEvidence =
      category.includes("behavior") ||
      selectionCategories.some((selectionCategory) => selectionCategory.includes("behavior")) ||
      qualifyingSelections.length ||
      Object.values(keywordHits).some((hits) => hits.length);
    const highRiskSelections = qualifyingSelections.filter((label) => highRiskBehaviorLabels.has(label));
    const severeSelections = selections.filter((selection) =>
      ["SIGNIFICANT", "SEVERE"].includes(String(selection.magnitude || "").toUpperCase()) ||
      ["WORSENED", "NEW"].includes(String(selection.trend || "").toUpperCase()),
    );
    const score =
      qualifyingSelections.length * 2 +
      highRiskSelections.length * 3 +
      severeSelections.length * 2 +
      keywordHits.oneToOne.length * 2 +
      keywordHits.multipleStaff.length * 4 +
      keywordHits.imminentRisk.length * 3 +
      keywordHits.nighttime.length;
    return { note, selections, behaviorSelections, qualifyingSelections, highRiskSelections, severeSelections, keywordHits, hasBehaviorEvidence, score, text };
  }).filter((item) => item.hasBehaviorEvidence);

  const behaviorDates = new Set(analyzedNotes.map((item) => normalizeDateInputValue(item.note.noteDate || item.note.date || item.note.startTimestamp || item.note.rangeStartTimestamp || item.note.happenedAt || item.note.occurredAt)).filter(Boolean));
  const sortedBehaviorDates = [...behaviorDates].sort();
  const qualifyingLabels = new Set(analyzedNotes.flatMap((item) => item.qualifyingSelections));
  const behaviorSelectionLabels = new Set(analyzedNotes.flatMap((item) => item.behaviorSelections));
  const oneToOneHits = analyzedNotes.reduce((total, item) => total + item.keywordHits.oneToOne.length, 0);
  const multipleStaffHits = analyzedNotes.reduce((total, item) => total + item.keywordHits.multipleStaff.length, 0);
  const imminentRiskHits = analyzedNotes.reduce((total, item) => total + item.keywordHits.imminentRisk.length, 0);
  const nighttimeHits = analyzedNotes.reduce((total, item) => total + item.keywordHits.nighttime.length, 0);
  const highRiskCount = analyzedNotes.reduce((total, item) => total + item.highRiskSelections.length, 0);
  const severeCount = analyzedNotes.reduce((total, item) => total + item.severeSelections.length, 0);
  const supportMinutesByDate = analyzedNotes.reduce((totals, item) => {
    const dateKey = normalizeDateInputValue(item.note.noteDate || item.note.date || item.note.startTimestamp || item.note.rangeStartTimestamp || item.note.happenedAt || item.note.occurredAt) || "undated";
    return { ...totals, [dateKey]: (totals[dateKey] || 0) + supportMinutesFromNote(item.note) };
  }, {});
  const documentedSupportHours = Math.max(0, ...Object.values(supportMinutesByDate).map((minutes) => minutes / 60));
  const hasDaytimeEvidence = analyzedNotes.some((item) => {
    const date = new Date(item.note.noteDate || item.note.date || item.note.startTimestamp || item.note.rangeStartTimestamp || item.note.happenedAt || item.note.occurredAt || "");
    const hour = date.getHours();
    return !Number.isNaN(hour) && hour >= 6 && hour < 20;
  });
  const indicatorContext = {
    analyzedNotes,
    dayCount: behaviorDates.size,
    documentedSupportHours,
    hasDaytimeEvidence,
    highRiskCount,
    imminentRiskHits,
    multipleStaffHits,
    nighttimeHits,
    oneToOneHits,
    qualifyingLabels,
    severeCount,
  };
  const tierScores = cbhsTierDocumentationIndicators.map((tierDefinition) => {
    const indicators = tierDefinition.indicators.map((indicator) => {
      const matched = Boolean(behaviorTierIndicatorMatchers[indicator.key]?.(indicatorContext));
      return { ...indicator, matched };
    });
    const score = indicators.reduce((total, indicator) => total + (indicator.matched ? indicator.weight : 0), 0);
    return { tier: tierDefinition.tier, score, indicators };
  });
  const strongestTierScore = [...tierScores].reverse().find((tierScore) => tierScore.score >= 50);
  const tier = strongestTierScore?.tier || null;
  const topBehaviorSelections = behaviorAnalysisTopCounts(analyzedNotes.flatMap((item) => item.qualifyingSelections));
  const topAllBehaviorSelections = behaviorAnalysisTopCounts(analyzedNotes.flatMap((item) => item.behaviorSelections));
  const topCategories = behaviorAnalysisTopCounts(
    analyzedNotes.flatMap((item) => [
      item.note.category || "Behavior",
      ...item.selections.map((selection) => selection.category).filter(Boolean),
    ]),
    5,
  );
  const strongestObservations = analyzedNotes.sort((a, b) => b.score - a.score).slice(0, 6);
  const evidenceScore = Math.min(100, Math.round(
    analyzedNotes.length * 6 +
    behaviorDates.size * 6 +
    qualifyingLabels.size * 10 +
    highRiskCount * 8 +
    severeCount * 8 +
    oneToOneHits * 8 +
    multipleStaffHits * 10 +
    imminentRiskHits * 8 +
    Math.min(documentedSupportHours, 24) * 2,
  ));
  const selectedTierScore = strongestTierScore?.score || 0;
  const totalScore = Math.max(evidenceScore, selectedTierScore, ...tierScores.map((tierScore) => tierScore.score));
  return {
    tier,
    tierStandard: cbhsTierStandards.find((standard) => standard.tier === tier),
    score: Math.min(100, totalScore),
    scorePercent: Math.min(100, totalScore),
    confidence: !analyzedNotes.length ? "Needs more documentation" : !tier ? "Needs tier documentation" : totalScore >= 75 ? "High" : totalScore >= 50 ? "Moderate" : "Low",
    documentedSupportHours,
    noteCount: analyzedNotes.length,
    dayCount: behaviorDates.size,
    qualifyingBehaviorCount: qualifyingLabels.size,
    behaviorSelectionCount: behaviorSelectionLabels.size,
    oneToOneHits,
    multipleStaffHits,
    imminentRiskHits,
    highRiskCount,
    severeCount,
    dateRange: {
      start: sortedBehaviorDates[0] || "",
      end: sortedBehaviorDates[sortedBehaviorDates.length - 1] || "",
    },
    topBehaviorSelections,
    topAllBehaviorSelections,
    topCategories,
    tierScores,
    observations: strongestObservations,
  };
};

const dateInputValue = (date) => new Date(date).toISOString().slice(0, 10);
const timeInputValue = (date) => new Date(date).toTimeString().slice(0, 5);
const shiftMinutes = (date, minutes) => {
  const nextDate = new Date(date);
  nextDate.setMinutes(nextDate.getMinutes() + minutes);
  return nextDate;
};

const normalizeDateInputValue = (value) => {
  if (!value) return "";
  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day] = value;
    return [year, String(month).padStart(2, "0"), String(day).padStart(2, "0")].join("-");
  }
  if (typeof value === "object") {
    const year = value.year ?? value.y;
    const month = value.month ?? value.monthValue ?? value.m;
    const day = value.day ?? value.dayOfMonth ?? value.d;
    if (year && month && day) return [year, String(month).padStart(2, "0"), String(day).padStart(2, "0")].join("-");
  }
  const raw = String(value);
  const dateOnlyMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (dateOnlyMatch) return dateOnlyMatch[1];
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : dateInputValue(parsed);
};

const monthStartInputValue = (date) => {
  const current = new Date(date);
  return dateInputValue(new Date(current.getFullYear(), current.getMonth(), 1));
};

const monthEndInputValue = (date) => {
  const current = new Date(date);
  return dateInputValue(new Date(current.getFullYear(), current.getMonth() + 1, 0));
};

const monthInputValue = (date) => dateInputValue(date).slice(0, 7);

const monthLabel = (monthValue = "") => {
  const [year, month] = String(monthValue || "").split("-").map(Number);
  if (!year || !month) return "";
  return new Date(year, month - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

const calendarDaysForMonth = (monthValue = "", notes = []) => {
  const [year, month] = String(monthValue || "").split("-").map(Number);
  if (!year || !month) return [];
  const monthIndex = month - 1;
  const firstOfMonth = new Date(year, monthIndex, 1);
  const lastOfMonth = new Date(year, monthIndex + 1, 0);
  const firstCalendarDay = new Date(firstOfMonth);
  firstCalendarDay.setDate(firstCalendarDay.getDate() - firstCalendarDay.getDay());
  const lastCalendarDay = new Date(lastOfMonth);
  lastCalendarDay.setDate(lastCalendarDay.getDate() + (6 - lastCalendarDay.getDay()));
  const notesByDay = notes.reduce((groups, note) => {
    const key = normalizeDateInputValue(note.noteDate || note.date);
    if (!key) return groups;
    return { ...groups, [key]: [...(groups[key] || []), note] };
  }, {});
  const days = [];
  for (const cursor = new Date(firstCalendarDay); cursor <= lastCalendarDay; cursor.setDate(cursor.getDate() + 1)) {
    const date = dateInputValue(cursor);
    days.push({
      date,
      dayNumber: cursor.getDate(),
      inMonth: cursor.getMonth() === monthIndex,
      notes: notesByDay[date] || [],
      today: date === dateInputValue(new Date()),
    });
  }
  return days;
};

const oneYearEstimatedEndDate = (startDate) => {
  const normalizedStart = normalizeDateInputValue(startDate);
  if (!normalizedStart) return "";
  const current = new Date(`${normalizedStart}T12:00:00`);
  if (Number.isNaN(current.getTime())) return "";
  return dateInputValue(new Date(current.getFullYear() + 1, current.getMonth(), current.getDate()));
};

const formatDateTime = (date) =>
  date ? new Date(date).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "Not set";

const dailyNoteTimeRangeLabel = (note = {}) => {
  const start = note.startTimestamp || note.rangeStartTimestamp || note.startedAt || note.date || note.happenedAt || note.occurredAt;
  const end = note.endTimestamp || note.rangeEndTimestamp || note.endedAt;
  if (!end || end === start) return formatDateTime(start);
  return `${formatDateTime(start)} - ${formatDateTime(end)}`;
};

const formatMonthDay = (date) =>
  date ? new Date(`${normalizeDateInputValue(date)}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";

const isWithinDateRange = (date, startDate, endDate) => {
  if (!date) return false;
  const target = dateInputValue(date);
  return target >= startDate && target <= endDate;
};

const dailyNoteKey = (note = {}) => {
  const value = note || {};
  return value.id || `${value.title || ""}-${value.date || value.noteDate || ""}`;
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.content)) return value.content;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const compactObject = (value = {}) =>
  Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined && entry !== null && entry !== ""));

const isLocalDailyNoteId = (noteId = "") => String(noteId || "").startsWith("daily-note-");

const mergeDailyNotes = (recordNotes = [], databaseNotes = []) => {
  const merged = [];
  const seen = new Set();
  [...databaseNotes, ...recordNotes].forEach((note) => {
    const key = dailyNoteKey(note);
    if (!key || seen.has(key)) return;
    seen.add(key);
    merged.push(note);
  });
  return merged;
};

const assessmentFlowStages = [
  {
    label: "State Initial Assessment",
    existingLabel: "State Initial Assessment",
    description: "State assessment, medication behavior, diagnosis, history, medications, and care needs.",
    existingDescription: "State assessment and resident changes that may trigger a new care plan.",
    icon: "ti-clipboard-text",
    appliesTo: ["NEW", "RESIDENT"],
    stageKeys: ["STATE_INITIAL_ASSESSMENT", "YEARLY_STATE_ASSESSMENT", "STATE_ASSESSMENT_RECEIVED"],
  },
  {
    label: "Meet and Greet",
    description: "Visit, meet the client, and capture MaG evaluation details.",
    icon: "ti-user-check",
    appliesTo: ["NEW"],
    stageKeys: ["MEET_AND_GREET"],
  },
  {
    label: "Provider Review",
    description: "Provider reviews state assessment and MaG findings, then decides whether to accept.",
    icon: "ti-search",
    appliesTo: ["NEW"],
    stageKeys: ["PROVIDER_REVIEW", "PROVIDER_ACCEPTED"],
  },
  {
    label: "Onboarding",
    description: "Signed paperwork, family discovery, care snapshot, preliminary plan, and initial resident information.",
    icon: "ti-clipboard-list",
    appliesTo: ["NEW"],
    stageKeys: ["ADMISSION_AND_PAPERWORK", "CLIENT_ONBOARDING", "PRELIMINARY_CARE_PLAN"],
  },
  {
    label: "30 Days Probation",
    existingLabel: "Behavioral Notes",
    description: "Probation notes, discrepancies, behavior, health, welfare, and care changes.",
    existingDescription: "Review behavioral notes, health, welfare, and care changes.",
    icon: "ti-notes",
    appliesTo: ["NEW", "RESIDENT"],
    stageKeys: ["THIRTY_DAY_PROBATION", "THIRTY_DAY_OBSERVATION", "NEW_RESIDENT_ASSESSMENT", "ANNUAL_ASSESSMENT", "YEARLY_REVIEW"],
  },
  {
    label: "Negotiated Care Plan",
    description: "Generate, review, edit, negotiate pay, and finalize the NCP.",
    icon: "ti-file-pencil",
    appliesTo: ["NEW", "RESIDENT"],
    stageKeys: ["NEGOTIATED_CARE_PLAN", "NEGOTIATED_CARE_PLAN_DRAFT", "PAYMENT_NEGOTIATION"],
  },
  {
    label: "Final Assessment",
    description: "Upload final assessment pages, confirm approval data, and complete the resident care process.",
    icon: "ti-file-check",
    appliesTo: ["NEW", "RESIDENT"],
    stageKeys: ["FINAL_ASSESSMENT_UPDATE"],
  },
];

const assessmentPatientType = (assessment = {}) => {
  const raw = normalizeStageKey(assessment.patientType || assessment.assessmentType || assessment.type);
  if (raw.includes("NEW")) return "NEW";
  return "RESIDENT";
};

const stageIndexFor = (stages, stage) => {
  const normalized = normalizeStageKey(stage);
  return stages.findIndex((item) => item.stageKeys.some((key) => normalizeStageKey(key) === normalized));
};

const displayStageIndexFor = (stages = [], stage = {}) => {
  const keys = (stage.stageKeys || []).map(normalizeStageKey);
  return stages.findIndex((item) => (item.stageKeys || []).some((key) => keys.includes(normalizeStageKey(key))));
};

const workflowTypeFor = (assessment = {}, patientType = "NEW") => {
  const normalized = normalizeStageKey(assessment.type || assessment.workflowType || assessment.patientType || patientType);
  if (normalized === "EXISTING_CLIENT" || normalized === "RESIDENT" || normalized.includes("ANNUAL")) return "EXISTING_CLIENT";
  return "NEW_CLIENT";
};

const workflowStatusFor = (status) => {
  const normalized = normalizeStageKey(status || "OPEN");
  if (normalized === "COMPLETE") return "COMPLETED";
  if (normalized === "CANCELLED") return "CANCELED";
  if (normalized === "ON_HOLD") return "PAUSED";
  return ["ARCHIVED", "COMPLETED", "DECLINED", "CANCELED", "IN_PROGRESS", "PAUSED", "OPEN", "DELETED"].includes(normalized) ? normalized : "OPEN";
};
const closedWorkflowStatuses = ["COMPLETED", "COMPLETE", "CANCELLED", "CANCELED", "ARCHIVED", "DELETED"];
const workflowStatusValueFor = (assessment = {}) =>
  workflowStatusFor(assessment.workflowStatus || assessment.processStatus || assessment.status);
const statusLabelFor = (status) =>
  String(status || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const isStateAssessmentStage = (stage = {}) =>
  (stage.stageKeys || []).some((key) => ["STATE_INITIAL_ASSESSMENT", "YEARLY_STATE_ASSESSMENT", "STATE_ASSESSMENT_RECEIVED"].includes(normalizeStageKey(key)));

const isProviderReviewStage = (stage = {}) =>
  (stage.stageKeys || []).some((key) => ["PROVIDER_REVIEW", "PROVIDER_ACCEPTED"].includes(normalizeStageKey(key)));

const isOnboardingStage = (stage = {}) =>
  (stage.stageKeys || []).some((key) => ["ADMISSION_AND_PAPERWORK", "CLIENT_ONBOARDING", "PRELIMINARY_CARE_PLAN"].includes(normalizeStageKey(key)));

const isPreliminaryPlanStage = () => false;

const isProbationNotesStage = (stage = {}) =>
  (stage.stageKeys || []).some((key) => ["THIRTY_DAY_PROBATION", "THIRTY_DAY_OBSERVATION", "NEW_RESIDENT_ASSESSMENT", "ANNUAL_ASSESSMENT", "YEARLY_REVIEW"].includes(normalizeStageKey(key)));

const isNegotiatedCarePlanStage = (stage = {}) =>
  (stage.stageKeys || []).some((key) => ["NEGOTIATED_CARE_PLAN", "NEGOTIATED_CARE_PLAN_DRAFT", "PAYMENT_NEGOTIATION"].includes(normalizeStageKey(key)));

const isFinalAssessmentStage = (stage = {}) =>
  (stage.stageKeys || []).some((key) => normalizeStageKey(key) === "FINAL_ASSESSMENT_UPDATE");

const generatedPreliminaryPlanHtml = `
  <h3>Preliminary Service Plan</h3>
  <p><strong>Generated from the preliminary service plan form screens.</strong></p>
  <h4>Resident Profile and Responsible Parties</h4>
  <p>Confirm resident demographics, move-in date, responsible party, DPOA/DPOH/guardian/POA/POH status, family history, close friends, and significant relatives.</p>
  <h4>Personal History, Religion, Interests, and Social Preferences</h4>
  <p>Document celebrations, special events, religious participation, church visits, dietary practices, hobbies, entertainment, music, reading, group preferences, outdoor activities, and pets.</p>
  <h4>Food Preferences and Dietary Needs</h4>
  <p>Capture meal times, favorite snacks, dining location preference, alcohol permission, physician diet orders, favorite foods, dislikes, beverages, meats, vegetables, fruits, and comfort foods.</p>
  <h4>Communication, Sensory, and Safety</h4>
  <p>Document speech, hearing, eyesight, phone use, bedroom lock/privacy preference, allergies, evacuation level, and evacuation assistance instructions.</p>
  <h4>Medication, Treatments, and Health Monitoring</h4>
  <p>Record medication management level, nurse delegation, injections, oxygen, pain, vital signs, blood sugar checks, weight monitoring, wound care, home health, PT/OT/speech/RN, and hospice.</p>
  <h4>Mental Capacity, Attitude, Behavior, and Interventions</h4>
  <p>Summarize orientation, memory, mood, behavior risks, wandering/elopement, psycho-pharmacological medications, door alarms, location checks, and intervention strategies.</p>
  <h4>Mobility, Transfers, ADLs, Continence, and Equipment</h4>
  <ul>
    <li>Ambulation, transfer, bed mobility, restorative support, repositioning, and paralysis status.</li>
    <li>Continence, toileting, dressing, bathing, skin care, grooming, mouth care, eating, fluids, and hydration plan.</li>
    <li>Equipment such as walker, hospital bed, oxygen, bath bench, gait belt, lift, transfer board, blood pressure machine, and O2 saturation meter.</li>
  </ul>
  <h4>Goals, Preferences, and Refusal Plan</h4>
  <p>Document resident goals, preferences, medication refusal plan, other refusal behaviors, and how staff should respond.</p>
`;

const preliminaryYesNoOptions = ["Yes", "No"];
const preliminaryResponsiblePartyRoleOptions = ["DPOA", "DPOH", "Guardian", "POA", "POH", "Other"];
const preliminaryMaritalStatusOptions = ["Married", "Never Married", "Divorced", "Widowed", "Separated", "Unknown"];
const preliminaryParticipationOptions = ["Active", "Fairly active", "Less active", "Not active", "Unknown"];
const preliminaryGroupPreferenceOptions = ["Groups of 15 or more", "Groups of 2-15", "One-on-one", "Does not like mixing with others"];
const preliminaryDiningLocationOptions = ["Dining Room", "Bedroom", "Either"];
const preliminaryBreakfastPreferenceOptions = ["Hot", "Cold", "Either"];
const preliminaryEvacuationOptions = ["Independent", "Assistance Required"];
const preliminarySpeechOptions = ["Speaks well", "Deficit", "Aphasic", "Mumbles", "Confused conversation"];
const preliminaryHearingAidOptions = ["No", "Left Ear", "Right Ear", "Both"];
const preliminaryEyeSightOptions = ["Good", "Poor"];
const preliminaryMedicationManagementOptions = ["Self-administration", "Self-medication with assistance", "Nurse delegation required"];
const preliminaryMentalCapacityOptions = ["Alert & oriented", "Mild cognitive impairment", "Dementia"];
const preliminaryAmbulationOptions = ["No assistance required", "Wheelchair", "Cane", "Walker", "Crutches", "1 Assist", "2 Assist"];
const preliminaryTransferOptions = ["Assist transfer", "Bed to chair", "Lift to chair", "Hoyer lift", "1 Assist", "2 Assist", "Sit to stand"];
const preliminaryBedMobilityOptions = ["Change by self", "With 1 assist", "With 2 assist", "Every 1/2 hour", "Every 1 hour", "Every 2 hours"];
const preliminaryContinenceOptions = ["Continent", "Incontinent", "Total care", "Assist", "Toilet Program"];
const preliminaryToiletingUseOptions = ["Bathroom", "Bedside commode", "Colostomy"];
const preliminaryCareSupportOptions = ["Self", "Assist", "Supervise", "Total Care", "Other"];
const preliminaryBathTypeOptions = ["Shower", "Bed bath", "Total care", "Assist", "Supervise"];
const preliminaryEatingOptions = ["Feeds self", "Total dependent", "Tube feeding", "Assist and/or prompt"];
const preliminaryFluidsOptions = ["Food supplement", "Restrict", "Encourage/offer", "Thickened liquids"];
const preliminaryEquipmentOptions = [
  "Hospital Bed",
  "Oxygen",
  "Trapeze",
  "Special Mattress",
  "Bath Bench",
  "Alternating Pressure Mattress",
  "Prosthesis",
  "Ted Hose",
  "Hoyer Lift",
  "Sit to Stand Lift",
  "Transfer Board",
  "Gait Belt",
  "Bedside Rail",
  "Blood Glucose Machine",
  "Blood Pressure Machine",
  "O2 Saturation Meter",
  "Other",
];

const preliminaryTextField = (key, label, helper = "", inputType = "text") => ({ key, label, helper, type: inputType });
const preliminarySelectField = (key, label, options, helper = "") => ({ key, label, helper, options, type: "select" });
const preliminaryTextareaField = (key, label, helper = "", rows = 3) => ({ key, label, helper, rows, type: "textarea" });
const preliminaryFieldKey = (field) => (Array.isArray(field) ? field[0] : field.key);
const preliminaryFieldLabel = (field) => (Array.isArray(field) ? field[1] : field.label);
const preliminaryFieldHelper = (field) => (Array.isArray(field) ? field[2] : field.helper);

const emptyPreliminaryPlanForm = {
  planDate: "",
  residentName: "",
  residentAge: "",
  residentBirthDate: "",
  responsiblePartyName: "",
  responsiblePartyRole: "",
  spouseName: "",
  spouseAge: "",
  maritalStatus: "",
  yearsMarried: "",
  siblingsAlive: "",
  siblingDetails: "",
  hasChildren: "",
  numberOfChildren: "",
  childrenDetails: "",
  grandchildrenDetails: "",
  petHistory: "",
  closeSupports: "",
  celebrations: "",
  leavesForSpecialEvents: "",
  religionDenomination: "",
  religiousParticipation: "",
  churchName: "",
  churchAddress: "",
  communionSupport: "",
  churchMemberVisits: "",
  religiousDietaryPractices: "",
  religiousDietDetails: "",
  craftInterests: "",
  outingInterests: "",
  entertainmentInterests: "",
  lifeSkillInterests: "",
  groupInteractionInterests: "",
  physicalInterests: "",
  readingInterests: "",
  groupSettingPreference: "",
  outdoorInterests: "",
  musicPreference: "",
  breakfastTime: "",
  lunchTime: "",
  dinnerTime: "",
  favoriteSnacks: "",
  favoriteSnackTime: "",
  diningLocationPreference: "",
  alcoholAllowed: "",
  specializedDietOrder: "",
  specializedDietType: "",
  breakfastPreference: "",
  favoriteBreakfast: "",
  foodDislikes: "",
  breadPreference: "",
  beveragePreferences: "",
  meatPreferences: "",
  vegetablePreferences: "",
  fruitPreferences: "",
  comfortFoods: "",
  favoriteMeals: "",
  speechTherapyAssessment: "",
  speechEvaluationDate: "",
  bedroomLockPreference: "",
  privacyCurtainPreference: "",
  evacuationLevel: "",
  allergies: "",
  allergyDetails: "",
  speechStatus: "",
  languageSpoken: "",
  hearingAid: "",
  deafStatus: "",
  eyeSight: "",
  wearsGlasses: "",
  blindStatus: "",
  cataracts: "",
  telephoneUse: "",
  medicationManagementLevel: "",
  injections: "",
  oxygenUse: "",
  painIssues: "",
  communityPrograms: "",
  vitalSigns: "",
  bloodSugarChecks: "",
  weightMonitoring: "",
  woundCare: "",
  therapyServices: "",
  homeHealthAgency: "",
  hospiceAgency: "",
  mentalCapacity: "",
  mentalAttitude: "",
  behaviorRisks: "",
  wanderingRisk: "",
  doorAlarms: "",
  locationChecks: "",
  behaviorAssessmentPages: "",
  behaviorInterventions: "",
  ambulationSupport: "",
  transferSupport: "",
  paralysisSupport: "",
  restorativeSupport: "",
  bedMobilitySupport: "",
  continenceLevel: "",
  toiletingUse: "",
  toiletingLevel: "",
  bowelMovementFrequency: "",
  equipmentBathroom: "",
  dressingSupport: "",
  dressingDetails: "",
  bathSupport: "",
  bathDays: "",
  skinCare: "",
  skinCareDetails: "",
  groomingSupport: "",
  groomingDetails: "",
  mouthCare: "",
  mouthCareSupport: "",
  eatingSupport: "",
  eatingDetails: "",
  fluidsSupport: "",
  fluidsDetails: "",
  hydrationSchedule: "",
  equipmentUsed: "",
  equipmentDetails: "",
  residentGoals: "",
  residentPreferences: "",
  medicationRefusalPlan: "",
  otherRefusalBehaviors: "",
  refusalManagementPlan: "",
};

const stateAssessmentBioFields = [
  ["firstName", "First name", "text"],
  ["middleName", "Middle name", "text"],
  ["lastName", "Last name", "text"],
  ["preferredName", "Preferred name", "text"],
  ["dateOfBirth", "Date of birth", "date"],
  ["socialSecurityNumber", "Social Security Number", "text"],
];

const stateAssessmentSexOptions = [
  "Female",
  "Male",
  "Intersex",
  "Non-binary",
  "Transgender female",
  "Transgender male",
  "Two-Spirit",
  "Questioning",
  "Not listed",
  "Prefer not to say",
  "Unknown",
  "Other",
];
const stateAssessmentRaceOptions = ["American Indian or Alaska Native", "Asian", "Black or African American", "Hispanic or Latino", "Middle Eastern or North African", "Native Hawaiian or Other Pacific Islander", "White", "Multiracial", "Other", "Prefer not to say", "Unknown"];

const stateAssessmentCodeStatusOptions = ["CPR", "DNR", "Full Code", "Comfort Care", "Unknown"];
const stateAssessmentYesNoOptions = ["Yes", "No"];
const stateAssessmentPhoneFields = [
  ["mobilePhone", "Mobile phone"],
  ["officePhone", "Office phone"],
  ["homePhone", "Home phone"],
  ["fax", "Fax"],
];
const stateAssessmentPreferredPhoneOptions = [
  ["mobilePhone", "Mobile"],
  ["officePhone", "Office"],
  ["homePhone", "Home"],
];
const addressTypeOptions = ["Home", "Office", "Business", "Permanent", "Other"];
const stateAssessmentContactRelationships = [
  "Spouse",
  "Daughter",
  "Son",
  "Parent",
  "Sibling",
  "Grandchild",
  "Emergency Contact",
  "Power of Attorney",
  "Legal Guardian",
  "Friend",
  "Case Manager",
  "Social Worker / Case Manager",
  "Financial Worker",
  "Nurse Delegator",
  "Pharmacy",
  "Primary Care Doctor",
  "Specialist",
  "Other",
];

const emptyStateAssessmentBioForm = {
  firstName: "",
  middleName: "",
  lastName: "",
  preferredName: "",
  dateOfBirth: "",
  socialSecurityNumber: "",
  sex: "",
  primaryLanguage: "",
  heightFeet: "",
  heightInches: "",
  weightLbs: "",
  race: "",
  raceOther: "",
  maritalStatus: "",
  placeOfBirth: "",
  religion: "",
  moveInDate: "",
  codeStatus: "",
  advancedDirectives: "",
  medicareNumber: "",
  medicaidNumber: "",
  supplementaryInsurances: "",
  mobilePhone: "",
  officePhone: "",
  homePhone: "",
  fax: "",
  preferredPhone: "",
  email: "",
  addressSearch: "",
  address: "",
  addressLine2: "",
  city: "",
  county: "",
  state: "",
  country: "",
  zip: "",
  latitude: "",
  longitude: "",
  placeId: "",
  addressType: "",
  addressTypeOther: "",
  backgroundNotes: "",
};

const emptyStateAssessmentContact = () => ({
  id: `state-assessment-contact-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  name: "",
  relationship: "",
  relationshipOther: "",
  mobilePhone: "",
  officePhone: "",
  homePhone: "",
  fax: "",
  preferredPhone: "",
  email: "",
  addressSearch: "",
  address: "",
  addressLine2: "",
  city: "",
  county: "",
  state: "",
  country: "",
  zip: "",
  latitude: "",
  longitude: "",
  placeId: "",
  addressType: "",
  addressTypeOther: "",
  notes: "",
});

const formatStateAssessmentSsn = (value = "") => {
  const digits = String(value).replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

const preliminaryPlanFieldSections = [
  {
    title: "Resident Profile and Relationships",
    tone: "blue",
    fields: [
      preliminaryTextField("planDate", "Plan date", "Date the preliminary service plan was completed.", "date"),
      preliminaryTextField("residentName", "Resident name"),
      preliminaryTextField("residentAge", "Age"),
      preliminaryTextField("residentBirthDate", "Birth date", "", "date"),
      preliminaryTextField("responsiblePartyName", "Responsible party"),
      preliminarySelectField("responsiblePartyRole", "Responsible party role", preliminaryResponsiblePartyRoleOptions),
      preliminaryTextField("spouseName", "Spouse name"),
      preliminaryTextField("spouseAge", "Spouse age"),
      preliminarySelectField("maritalStatus", "Marital status", preliminaryMaritalStatusOptions),
      preliminaryTextField("yearsMarried", "Years married"),
      preliminarySelectField("siblingsAlive", "Siblings alive", preliminaryYesNoOptions),
      preliminaryTextareaField("siblingDetails", "Sibling details", "Names, status, contact involvement, and notes."),
      preliminarySelectField("hasChildren", "Children", preliminaryYesNoOptions),
      preliminaryTextField("numberOfChildren", "Number of children"),
      preliminaryTextareaField("childrenDetails", "Children details", "Names, ages, state of residence, and involvement."),
      preliminaryTextareaField("grandchildrenDetails", "Grandchildren/great-grandchildren", "Number, names, ages, and involvement."),
      preliminaryTextareaField("petHistory", "Pet history", "Pet type, pet name, and importance to resident."),
      preliminaryTextareaField("closeSupports", "Close friends/significant relatives", "Names, how resident knows them, where they live, and role in support."),
    ],
  },
  {
    title: "Religion, Interests, and Social Preferences",
    tone: "green",
    fields: [
      preliminaryTextareaField("celebrations", "Important celebrations", "Family dinners, special days, birthdays, Christmas, religious/cultural celebrations."),
      preliminarySelectField("leavesForSpecialEvents", "Leaves for special events", preliminaryYesNoOptions),
      preliminaryTextField("religionDenomination", "Religion/denomination"),
      preliminarySelectField("religiousParticipation", "Participation", preliminaryParticipationOptions),
      preliminaryTextField("churchName", "Church/temple/community name"),
      preliminaryTextareaField("churchAddress", "Church/temple/community address"),
      preliminarySelectField("communionSupport", "Someone brings communion or equivalent support", preliminaryYesNoOptions),
      preliminarySelectField("churchMemberVisits", "Faith community members visit", preliminaryYesNoOptions),
      preliminarySelectField("religiousDietaryPractices", "Religious dietary practices", preliminaryYesNoOptions),
      preliminaryTextField("religiousDietDetails", "Religious food required"),
      preliminaryTextareaField("craftInterests", "Craft interests", "Painting/arts or other crafts."),
      preliminaryTextareaField("outingInterests", "Outings", "Shopping, movies, music, other outings."),
      preliminaryTextareaField("entertainmentInterests", "Entertainment/games", "TV, cards, checkers/chess, other games."),
      preliminaryTextareaField("lifeSkillInterests", "Life skills", "Folding, sweeping, or other familiar tasks."),
      preliminaryTextareaField("groupInteractionInterests", "Group interaction", "Singing, instruments, visiting, children, pets, books/stories/newspapers."),
      preliminaryTextareaField("physicalInterests", "Physical interests", "Individual exercise, walks, and activity tolerance."),
      preliminaryTextareaField("readingInterests", "Reading interests", "Books, audio books, picture books, braille, newspapers."),
      preliminarySelectField("groupSettingPreference", "Group setting preference", preliminaryGroupPreferenceOptions),
      preliminaryTextareaField("outdoorInterests", "Outdoor opportunities", "Visit, exercise, go to movies/events, walks, and outdoor preferences."),
      preliminaryTextField("musicPreference", "Musical interests/favorite artist or band"),
    ],
  },
  {
    title: "Daily Preferences and Nutrition",
    tone: "yellow",
    fields: [
      preliminaryTextField("breakfastTime", "Breakfast time"),
      preliminaryTextField("lunchTime", "Lunch time"),
      preliminaryTextField("dinnerTime", "Dinner time"),
      preliminaryTextField("favoriteSnacks", "Favorite snacks"),
      preliminaryTextField("favoriteSnackTime", "Favorite snack time"),
      preliminarySelectField("diningLocationPreference", "Prefers to eat meals", preliminaryDiningLocationOptions),
      preliminarySelectField("alcoholAllowed", "Physician allows alcohol", preliminaryYesNoOptions),
      preliminarySelectField("specializedDietOrder", "Physician order for specialized diet", preliminaryYesNoOptions),
      preliminaryTextField("specializedDietType", "Specialized diet type"),
      preliminarySelectField("breakfastPreference", "Breakfast preference", preliminaryBreakfastPreferenceOptions),
      preliminaryTextField("favoriteBreakfast", "Favorite breakfast"),
      preliminaryTextareaField("foodDislikes", "Food dislikes"),
      preliminaryTextField("breadPreference", "Bread preference"),
      preliminaryTextareaField("beveragePreferences", "Beverages", "Milk, coffee, tea type, juice type, sugar free, water, or other preferences."),
      preliminaryTextareaField("meatPreferences", "Meats"),
      preliminaryTextareaField("vegetablePreferences", "Vegetables"),
      preliminaryTextareaField("fruitPreferences", "Fruit"),
      preliminaryTextareaField("comfortFoods", "Favorite comfort foods/recipes"),
      preliminaryTextareaField("favoriteMeals", "Favorite meals"),
      preliminarySelectField("eatingSupport", "Eating habits/support", preliminaryEatingOptions),
      preliminaryTextareaField("eatingDetails", "Eating support details", "Prompts, clothing protector, special equipment, or feeding notes."),
      preliminarySelectField("fluidsSupport", "Fluids support", preliminaryFluidsOptions),
      preliminaryTextareaField("fluidsDetails", "Fluids details", "Supplement, restriction, encourage/offer details, or thickened liquid type."),
      preliminaryTextField("hydrationSchedule", "Hydration schedule"),
    ],
  },
  {
    title: "Health, Safety, and Communication",
    tone: "green",
    fields: [
      preliminarySelectField("speechTherapyAssessment", "Speech therapy assessment", preliminaryYesNoOptions),
      preliminaryTextField("speechEvaluationDate", "Speech evaluation date", "", "date"),
      preliminarySelectField("bedroomLockPreference", "Wants lock with key on door", preliminaryYesNoOptions),
      preliminarySelectField("privacyCurtainPreference", "Wants privacy curtain if sharing room", preliminaryYesNoOptions),
      preliminarySelectField("evacuationLevel", "Evacuation level", preliminaryEvacuationOptions),
      preliminarySelectField("allergies", "Allergies", preliminaryYesNoOptions),
      preliminaryTextareaField("allergyDetails", "Allergy details"),
      preliminarySelectField("speechStatus", "Speech", preliminarySpeechOptions),
      preliminaryTextField("languageSpoken", "Language spoken"),
      preliminarySelectField("hearingAid", "Uses hearing aid", preliminaryHearingAidOptions),
      preliminarySelectField("deafStatus", "Deaf/partially deaf", ["No", "Totally deaf", "Partially deaf - right ear", "Partially deaf - left ear", "Partially deaf - both"]),
      preliminarySelectField("eyeSight", "Eye sight", preliminaryEyeSightOptions),
      preliminarySelectField("wearsGlasses", "Wears glasses", preliminaryYesNoOptions),
      preliminarySelectField("blindStatus", "Blind/legal blindness", ["No", "Blind - right eye", "Blind - left eye", "Blind - both eyes", "Legally blind"]),
      preliminarySelectField("cataracts", "Cataracts", preliminaryYesNoOptions),
      preliminarySelectField("telephoneUse", "Telephone use", ["Uses telephone", "Does not use telephone"]),
      preliminarySelectField("medicationManagementLevel", "Medication management", preliminaryMedicationManagementOptions),
      preliminarySelectField("injections", "Injections", preliminaryYesNoOptions),
      preliminarySelectField("oxygenUse", "Uses oxygen", preliminaryYesNoOptions),
      preliminaryTextareaField("painIssues", "Pain issues"),
      preliminarySelectField("communityPrograms", "Attends programs in community", preliminaryYesNoOptions),
      preliminarySelectField("vitalSigns", "Vital signs monitoring", preliminaryYesNoOptions),
      preliminarySelectField("bloodSugarChecks", "Blood sugar checks", preliminaryYesNoOptions),
      preliminarySelectField("weightMonitoring", "Weight monitoring", preliminaryYesNoOptions),
      preliminarySelectField("woundCare", "Wound care", preliminaryYesNoOptions),
      preliminaryTextareaField("therapyServices", "Therapy/RN services", "PT, OT, speech, RN, and service notes."),
      preliminaryTextField("homeHealthAgency", "Home health agency"),
      preliminaryTextField("hospiceAgency", "Hospice agency"),
    ],
  },
  {
    title: "Behavior, Mobility, and ADLs",
    tone: "blue",
    fields: [
      preliminarySelectField("mentalCapacity", "Resident mental capacity", preliminaryMentalCapacityOptions),
      preliminaryTextareaField("mentalAttitude", "Mental attitude", "Oriented, well-adjusted, moody, cheerful, depressed, confused, alert, agitated, forgetful."),
      preliminaryTextareaField("behaviorRisks", "Behavior risks", "Verbally abusive, physically abusive, combative, psycho-pharmacological drugs, elopement potential, or other risks."),
      preliminarySelectField("wanderingRisk", "Wanderer", preliminaryYesNoOptions),
      preliminarySelectField("doorAlarms", "Door alarms", preliminaryYesNoOptions),
      preliminarySelectField("locationChecks", "Location check every 15 minutes", preliminaryYesNoOptions),
      preliminaryTextField("behaviorAssessmentPages", "Behavior assessment pages"),
      preliminaryTextareaField("behaviorInterventions", "Behavior interventions"),
      preliminarySelectField("ambulationSupport", "Ambulation", preliminaryAmbulationOptions),
      preliminarySelectField("transferSupport", "Transfer", preliminaryTransferOptions),
      preliminaryTextareaField("paralysisSupport", "Paralysis"),
      preliminaryTextareaField("restorativeSupport", "Restorative support", "Active/passive range of motion and related instructions."),
      preliminarySelectField("bedMobilitySupport", "Bed mobility", preliminaryBedMobilityOptions),
      preliminarySelectField("continenceLevel", "Continence issues", preliminaryContinenceOptions),
      preliminarySelectField("toiletingUse", "Toileting client uses", preliminaryToiletingUseOptions),
      preliminarySelectField("toiletingLevel", "Toileting support level", preliminaryContinenceOptions),
      preliminaryTextField("bowelMovementFrequency", "Bowel movement frequency"),
      preliminaryTextareaField("equipmentBathroom", "Bathroom/toileting equipment"),
      preliminarySelectField("dressingSupport", "Dressing", preliminaryCareSupportOptions),
      preliminaryTextareaField("dressingDetails", "Dressing details", "Shoes, stockings, clothes, or other support."),
      preliminarySelectField("bathSupport", "Bath", preliminaryBathTypeOptions),
      preliminaryTextField("bathDays", "Bath days"),
      preliminarySelectField("skinCare", "Skin care", ["Routine", "Special", "Decubitus site", "Contractures site"]),
      preliminaryTextareaField("skinCareDetails", "Skin care details"),
      preliminarySelectField("groomingSupport", "Grooming support", preliminaryCareSupportOptions),
      preliminaryTextareaField("groomingDetails", "Grooming details", "Comb/brush hair, shave, nails, feet, hairdresser, podiatrist."),
      preliminarySelectField("mouthCare", "Mouth care", ["Upper dentures", "Lower dentures", "No dentures", "No teeth", "Partial denture - upper", "Partial denture - lower"]),
      preliminarySelectField("mouthCareSupport", "Mouth care support", ["Self", "Assist", "Total Care"]),
    ],
  },
  {
    title: "Equipment, Goals, and Refusals",
    tone: "yellow",
    fields: [
      preliminarySelectField("equipmentUsed", "Equipment used", preliminaryEquipmentOptions),
      preliminaryTextareaField("equipmentDetails", "Equipment details", "Type/details for braces, splints, prosthesis, special mattress, rails, or other equipment."),
      preliminaryTextareaField("residentGoals", "Resident goals"),
      preliminaryTextareaField("residentPreferences", "Resident preferences"),
      preliminaryTextareaField("medicationRefusalPlan", "Medication refusal plan", "Example: PCP notification at time of refusal and documentation expectations."),
      preliminarySelectField("otherRefusalBehaviors", "Other refusal type behavior", preliminaryYesNoOptions),
      preliminaryTextareaField("refusalManagementPlan", "How refusal behaviors will be managed"),
    ],
  },
];

const preliminaryPlanValueFromExtraction = (document = {}) => {
  const fields = extractionFieldsFromDocument(document);
  if (!fields.length) return {};
  const aliases = {
    residentName: [/resident\s+name/i, /client\s+name/i, /full\s+name/i],
    residentAge: [/^age$/i, /resident\s+age/i, /client\s+age/i],
    residentBirthDate: [/date\s+of\s+birth/i, /^dob$/i, /birth\s+date/i],
    responsiblePartyName: [/responsible\s+party/i, /legal\s+guardian/i, /guardian\s+name/i, /power\s+of\s+attorney/i, /representative/i],
    responsiblePartyRole: [/responsible\s+party.*role/i, /guardian/i, /power\s+of\s+attorney/i, /representative/i],
    spouseName: [/spouse/i, /husband/i, /wife/i],
    maritalStatus: [/marital\s+status/i, /married|widowed|divorced|single/i],
    siblingDetails: [/sibling/i, /brother|sister/i],
    childrenDetails: [/children|daughter|son/i],
    grandchildrenDetails: [/grandchild/i],
    closeSupports: [/support\s+system/i, /close\s+friend/i, /significant\s+relative/i, /family\s+support/i],
    celebrations: [/celebration|birthday|holiday/i],
    religionDenomination: [/religion|denomination|faith/i],
    religiousParticipation: [/religious\s+participation|church\s+attendance|faith\s+community/i],
    churchName: [/church|temple|mosque|faith\s+community/i],
    craftInterests: [/craft|art|painting/i],
    outingInterests: [/outing|shopping|movie|community\s+activity/i],
    entertainmentInterests: [/entertainment|game|television|music/i],
    readingInterests: [/reading|book|newspaper/i],
    musicPreference: [/music|favorite\s+artist/i],
    favoriteSnacks: [/snack|favorite\s+food/i],
    breakfastTime: [/breakfast/i],
    lunchTime: [/lunch/i],
    dinnerTime: [/dinner/i],
    notes: [/note|summary|important\s+information/i],
  };
  const result = {};
  preliminaryPlanFieldSections.flatMap((section) => section.fields).forEach((fieldConfig) => {
    const key = preliminaryFieldKey(fieldConfig);
    const label = preliminaryFieldLabel(fieldConfig);
    const labelPattern = String(label).replace(/client|resident|current|preferred/gi, "").trim().split(/\s+/).filter(Boolean).join("\\s+");
    const patterns = aliases[key] || (labelPattern ? [new RegExp(labelPattern, "i")] : []);
    const match = fields.find((field) => patterns.some((pattern) => pattern.test(`${field.label || ""} ${field.value || ""}`)));
    if (!match?.value) return;
    const value = String(match.value).trim();
    result[key] = key === "residentBirthDate" ? dateInputFromExtraction(value) || value : value;
  });
  const summary = document.caregiverSummary || {};
  if (!result.closeSupports && Array.isArray(summary.immediatePriorities)) result.closeSupports = summary.immediatePriorities.join("\\n");
  if (!result.notes && Array.isArray(summary.reportingInstructions)) result.notes = summary.reportingInstructions.join("\\n");
  return result;
};

const stripHtmlText = (value = "") => String(value).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
const escapeHtml = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "\"": "&quot;",
    "'": "&#39;",
  }[character]));
const firstText = (...values) =>
  values.find((value) => value !== null && value !== undefined && String(value).trim() !== "") || "";
const personName = (person = {}) =>
  firstText(
    person.name,
    person.fullName,
    person.displayName,
    [person.firstName, person.middleName, person.lastName].filter(Boolean).join(" "),
    person.username,
    person.email,
  );
const personId = (person = {}) =>
  firstText(person.id, person.userId, person.uid, person.profileId, person.sub);
const dailyNoteReporterName = (note = {}) =>
  firstText(
    note.addedByName,
    note.authorName,
    note.capturedByName,
    note.createdByName,
    note.enteredByName,
    note.recordedByName,
    personName(note.addedByUser),
    personName(note.authorUser),
    personName(note.capturedByUser),
    note.addedBy,
    note.author,
    note.capturedBy,
    note.createdBy,
    "Not recorded",
  );
const dailyNoteUpdaterName = (note = {}) =>
  firstText(
    note.updatedByName,
    note.modifiedByName,
    personName(note.updatedByUser),
    note.updatedBy,
    dailyNoteReporterName(note),
  );

const yesNoFromValue = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  const normalized = String(value || "").trim().toLowerCase();
  if (["yes", "y", "true", "1"].includes(normalized)) return "Yes";
  if (["no", "n", "false", "0"].includes(normalized)) return "No";
  return value || "";
};

const yesNoToBoolean = (value) => {
  if (value === "Yes") return true;
  if (value === "No") return false;
  return null;
};

const stateAssessmentApiSexValue = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "male") return "MALE";
  if (normalized === "female") return "FEMALE";
  return value ? "OTHER" : null;
};

const splitStateAssessmentName = (name = "") => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { firstName: "", middleName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], middleName: "", lastName: "" };
  return {
    firstName: parts[0],
    middleName: parts.length > 2 ? parts.slice(1, -1).join(" ") : "",
    lastName: parts[parts.length - 1],
  };
};

const stateAssessmentContactTypeFor = (relationship = "") => {
  const normalized = String(relationship || "").toLowerCase();
  if (normalized.includes("social worker") || normalized.includes("case manager")) return "SOCIAL_WORKER";
  if (normalized.includes("power of attorney")) return "POWER_OF_ATTORNEY";
  if (normalized.includes("guardian")) return "GUARDIAN";
  if (normalized.includes("emergency")) return "EMERGENCY_CONTACT";
  if (normalized.includes("pharmacy")) return "PHARMACY";
  if (normalized.includes("doctor") || normalized.includes("physician")) return "PRIMARY_CARE_DOCTOR";
  if (normalized.includes("specialist")) return "SPECIALIST";
  return "OTHER";
};

const contactAddressFrom = (contact = {}) => contact.address || contact.locationInformation || contact.location || {};

const normalizeContactAddress = (address = {}) => ({
  addressSearch: address.formattedAddress || address.address || address.addressLine1 || "",
  address: address.address || address.addressLine1 || address.formattedAddress || "",
  addressLine2: address.addressLine2 || address.line2 || "",
  city: address.city || "",
  county: address.county || "",
  state: address.state || "",
  country: address.country || "",
  zip: address.zipCode || address.zip || address.postalCode || "",
  latitude: address.latitude || "",
  longitude: address.longitude || "",
  placeId: address.placeId || "",
  addressType: address.addressType || "",
  addressTypeOther: address.otherAddressType || address.addressTypeOther || "",
});

const contactAddressPayload = (contact = {}) => ({
  address: contact.address || "",
  addressLine2: contact.addressLine2 || "",
  city: contact.city || "",
  county: contact.county || "",
  state: contact.state || "",
  country: contact.country || "",
  zipCode: contact.zip || "",
  latitude: contact.latitude || null,
  longitude: contact.longitude || null,
  placeId: contact.placeId || "",
  addressType: contact.addressType || "",
  otherAddressType: contact.addressType === "Other" ? contact.addressTypeOther || "" : "",
});

const normalizeStateAssessmentContact = (contact = {}, index = 0) => {
  const relationship = contact.relationship || contact.contactTypeLabel || contact.contactType || contact.type || contact.role || "";
  const knownRelationship = stateAssessmentContactRelationships.includes(relationship);
  const phones = contact.phones || {};
  const name = contact.name || contact.fullName || [contact.firstName, contact.middleName, contact.lastName].filter(Boolean).join(" ");
  const preferredPhoneValue = phones.preferred || contact.preferredPhone || "";
  const preferredPhone = stateAssessmentPreferredPhoneOptions.find(([field]) => {
    const phoneKey = field.replace("Phone", "");
    return phones[phoneKey] && phones[phoneKey] === preferredPhoneValue;
  })?.[0] || contact.preferredPhone || "";
  return {
    id: contact.id || `state-assessment-contact-loaded-${index + 1}`,
    residentId: contact.residentId || "",
    name,
    organizationName: contact.organizationName || "",
    relationship: knownRelationship ? relationship : relationship ? "Other" : "",
    relationshipOther: knownRelationship ? "" : relationship,
    mobilePhone: contact.mobilePhone || contact.phone || contact.phoneNumber || contact.primaryPhone || phones.mobile || "",
    officePhone: contact.officePhone || contact.workPhone || phones.office || "",
    homePhone: contact.homePhone || phones.home || "",
    fax: contact.fax || contact.faxNumber || phones.fax || "",
    preferredPhone,
    email: contact.email || contact.primaryEmail || contact.emailAddress || "",
    ...normalizeContactAddress({
      ...contactAddressFrom(contact),
      addressType: contact.addressType || contactAddressFrom(contact).addressType,
      otherAddressType: contact.otherAddressType || contact.addressTypeOther || contactAddressFrom(contact).otherAddressType,
    }),
    notes: contact.notes || contact.description || "",
  };
};

const stateAssessmentContactPayload = (contacts = []) =>
  contacts
    .filter((contact) =>
      contact.name ||
      contact.organizationName ||
      contact.relationship ||
      contact.email ||
      contact.mobilePhone ||
      contact.officePhone ||
      contact.homePhone ||
      contact.fax ||
      contact.address ||
      contact.city ||
      contact.state ||
      contact.zip ||
      contact.notes)
    .map((contact) => {
      const preferredPhone = contact.preferredPhone ? contact[contact.preferredPhone] || "" : "";
      const address = contactAddressPayload(contact);
      const relationship = contact.relationship === "Other" ? contact.relationshipOther || "Other" : contact.relationship || "";
      const nameParts = splitStateAssessmentName(contact.name);
      const isDecisionMaker = ["POWER_OF_ATTORNEY", "GUARDIAN"].includes(stateAssessmentContactTypeFor(relationship));
      return {
        id: contact.id || null,
        residentId: contact.residentId || null,
        name: contact.name || "",
        fullName: contact.name || "",
        organizationName: contact.organizationName || "",
        contactType: stateAssessmentContactTypeFor(relationship),
        relationship,
        ...nameParts,
        email: contact.email || "",
        fax: contact.fax || "",
        faxNumber: contact.fax || "",
        phones: {
          mobile: contact.mobilePhone || "",
          office: contact.officePhone || "",
          home: contact.homePhone || "",
          fax: contact.fax || "",
          preferred: preferredPhone,
        },
        address,
        locationInformation: address,
        primaryContact: false,
        emergencyContact: stateAssessmentContactTypeFor(relationship) === "EMERGENCY_CONTACT" || isDecisionMaker,
        decisionMaker: isDecisionMaker,
        nextOfKin: isDecisionMaker,
        notes: contact.notes || "",
      };
    });

const displaySexValue = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const normalized = raw.replace(/_/g, " ").toLowerCase();
  return stateAssessmentSexOptions.find((option) => option.toLowerCase() === normalized) ||
    normalized.replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const extractionFieldsFromDocument = (document = {}) =>
  (document?.sections || []).flatMap((section) =>
    (section?.subsections || []).flatMap((subsection) =>
      (subsection?.fields || []).map((field) => ({
        ...field,
        sectionKey: section.sectionKey || "",
        sectionTitle: section.title || "",
        subsectionTitle: subsection.title || "",
      })),
    ),
  );

const extractionValueFor = (fields = [], labelPatterns = [], sectionKeys = []) => {
  const patterns = Array.isArray(labelPatterns) ? labelPatterns : [labelPatterns];
  const sections = Array.isArray(sectionKeys) ? sectionKeys : [sectionKeys];
  const field = fields.find((item) => {
    const label = String(item.label || "");
    const sectionKey = String(item.sectionKey || "");
    return (!sections.length || sections.includes(sectionKey)) &&
      patterns.some((pattern) => pattern instanceof RegExp ? pattern.test(label) : label.toLowerCase().includes(String(pattern).toLowerCase()));
  });
  return String(field?.value || "").trim();
};

const extractionEmbeddedValueFor = (fields = [], patterns = []) => {
  const regexes = Array.isArray(patterns) ? patterns : [patterns];
  for (const field of fields) {
    const text = `${field.label || ""}: ${field.value || ""}`;
    for (const pattern of regexes) {
      const match = text.match(pattern);
      if (match?.[1]) return match[1].trim();
    }
  }
  return "";
};

const extractionValuesFor = (fields = [], labelPatterns = [], sectionKeys = [], limit = 8) => {
  const patterns = Array.isArray(labelPatterns) ? labelPatterns : [labelPatterns];
  const sections = Array.isArray(sectionKeys) ? sectionKeys : [sectionKeys];
  const values = fields
    .filter((item) => {
      const label = String(item.label || "");
      const sectionKey = String(item.sectionKey || "");
      return (!sections.length || sections.includes(sectionKey)) &&
        patterns.some((pattern) => pattern instanceof RegExp ? pattern.test(label) : label.toLowerCase().includes(String(pattern).toLowerCase()));
    })
    .map((item) => String(item.value || "").trim())
    .filter(Boolean);
  return [...new Set(values)].slice(0, limit);
};

const cleanExtractionScalar = (value = "") => {
  const text = String(value || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "";
  return text
    .split(/\s+\|\s+|\s{3,}/)[0]
    .replace(/\s+(Preferred language|Interpreter|Admission date|Room|Age)\b.*$/i, "")
    .trim();
};

const dateInputFromExtraction = (value = "") => {
  const text = String(value || "").trim();
  if (!text) return "";
  const iso = text.match(/\b(\d{4}-\d{2}-\d{2})\b/)?.[1];
  if (iso) return iso;
  const parsed = new Date(text.replace(/(\d+)(st|nd|rd|th)/gi, "$1"));
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().slice(0, 10);
};

const codeStatusFromExtraction = (fields = []) => {
  const text = [
    extractionValueFor(fields, [/emergency\s+classification/i]),
    extractionValueFor(fields, [/^polst/i, /polst\s+directives/i]),
    extractionValueFor(fields, [/code.?status/i]),
  ].filter(Boolean).join(" ").toLowerCase();
  if (!text) return "";
  if (/\bdnr\b|do not resuscitate/.test(text)) return "DNR";
  if (/comfort/.test(text)) return "Comfort Care";
  if (/full code/.test(text)) return "Full Code";
  if (/attempt cpr|\bcpr\b/.test(text)) return "CPR";
  return "Unknown";
};

const advancedDirectivesFromExtraction = (fields = []) => {
  const text = [
    extractionValueFor(fields, [/^polst/i, /polst\s+directives/i]),
    extractionValueFor(fields, [/living\s+will/i]),
    extractionValueFor(fields, [/durable\s+power\s+of\s+attorney/i]),
    extractionValueFor(fields, [/guardian/i]),
  ].filter(Boolean).join(" ");
  return text.trim() ? "Yes" : "";
};

const phoneFromText = (value = "") =>
  String(value || "").match(/(?:\+?1[\s.-]?)?(?:\(?\d{3}\)?[\s.-]?)\d{3}[\s.-]?\d{4}/)?.[0]?.trim() || "";

const cleanPhone = (value = "") => phoneFromText(value).replace(/^\+?1[\s.-]?/, "");

const emailFromText = (value = "") =>
  String(value || "").match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i)?.[0] || "";

const relationshipFromText = (value = "", fallback = "") => {
  const text = String(value || "").toLowerCase();
  if (fallback && stateAssessmentContactRelationships.includes(fallback)) return fallback;
  const relationship = stateAssessmentContactRelationships.find((option) => text.includes(option.toLowerCase()));
  if (relationship) return relationship;
  if (text.includes("niece") || text.includes("nephew")) return "Other";
  if (text.includes("physician") || text.includes("doctor") || text.includes("md")) return "Primary Care Doctor";
  if (text.includes("pharmacy")) return "Pharmacy";
  return fallback;
};

const relationshipOtherFromText = (value = "", relationship = "") => {
  const text = String(value || "").toLowerCase();
  if (relationship !== "Other") return "";
  if (text.includes("niece")) return "Niece";
  if (text.includes("nephew")) return "Nephew";
  return "";
};

const contactNameFromExtractedValue = (value = "", relationship = "") => {
  const firstPart = String(value || "")
    .split("|")[0]
    ?.split(/,\s*(?=niece\b|nephew\b|daughter\b|son\b|spouse\b|activated\b|healthcare\b|lives\b|county\b|no\s+court)/i)[0]
    .trim() || "";
  if (!firstPart) return "";
  const relationshipWords = [
    relationship,
    "niece",
    "nephew",
    "daughter",
    "son",
    "spouse",
    "primary emergency contact",
    "secondary contact",
  ].filter(Boolean);
  return relationshipWords.reduce((name, word) =>
    name.replace(new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "ig"), ""), firstPart)
    .replace(/\s*,\s*$/, "")
    .replace(/\s{2,}/g, " ")
    .trim();
};

const contactIdentityKey = (contact = {}) => String(contact.name || contact.mobilePhone || contact.officePhone || contact.homePhone || "")
  .split(",")[0]
  .replace(/\s+/g, " ")
  .trim()
  .toLowerCase();

const isNegativeContactStatement = (contact = {}) =>
  /\b(no|none|not applicable|not known)\b.*\b(contact|guardian|representative|poa)\b/i.test(
    `${contact.name || ""} ${contact.notes || ""}`,
  );

const mergeStateAssessmentContacts = (contacts = []) => {
  const merged = new Map();
  contacts
    .map(normalizeStateAssessmentContact)
    .filter((contact) => !isNegativeContactStatement(contact))
    .forEach((contact) => {
      const key = contactIdentityKey(contact);
      if (!key || !merged.has(key)) {
        merged.set(key, contact);
        return;
      }
      const existing = merged.get(key);
      merged.set(key, {
        ...existing,
        ...Object.fromEntries(Object.entries(contact).filter(([field, value]) =>
          field !== "relationship" && field !== "relationshipOther" && value !== "" && value !== null && value !== undefined,
        )),
        notes: [existing.notes, contact.notes].filter(Boolean).filter((value, index, values) => values.indexOf(value) === index).join(" | "),
      });
    });
  return [...merged.values()];
};

const parseSimpleUsAddress = (value = "") => {
  const text = String(value || "").trim();
  const match = text.match(/^(.+?),\s*([^,]+),\s*([A-Z]{2}|[A-Za-z ]+)\s+(\d{5}(?:-\d{4})?)/);
  if (!match) return {};
  return {
    addressSearch: text,
    address: match[1].trim(),
    city: match[2].trim(),
    state: match[3].trim(),
    zip: match[4].trim(),
    country: "United States",
    addressType: "Home",
  };
};

const stateAssessmentAddressFromExtraction = (fields = []) => {
  const addressValue = firstFilled(
    extractionValueFor(fields, [/resident\s+address/i, /client\s+address/i, /current\s+address/i, /home\s+address/i, /previous\s+address/i, /client\s+previous\s+address/i], ["client_profile"]),
    extractionValueFor(fields, [/address/i], ["client_profile"]),
    extractionValueFor(fields, [/resident\s+address/i, /client\s+address/i, /current\s+address/i, /home\s+address/i, /previous\s+address/i, /client\s+previous\s+address/i]),
  );
  const parsedAddress = parseSimpleUsAddress(addressValue);
  const address = firstFilled(
    parsedAddress.address,
    extractionValueFor(fields, [/^(resident|client)\s+(current\s+)?address\s*(line\s*1|1)?$/i, /^address\s*(line\s*1|1)?$/i]),
  );
  const city = firstFilled(parsedAddress.city, extractionValueFor(fields, [/^(resident|client)\s+(current\s+)?city$/i, /^city$/i]));
  const county = extractionValueFor(fields, [/^(resident|client)\s+(current\s+)?county$/i, /^county$/i]);
  const state = firstFilled(parsedAddress.state, extractionValueFor(fields, [/^(resident|client)\s+(current\s+)?state$/i, /^state$/i]));
  const zip = firstFilled(parsedAddress.zip, extractionValueFor(fields, [/^(resident|client)\s+(current\s+)?(zip|postal)\s*(code)?$/i, /^(zip|postal)\s*(code)?$/i]));
  const country = firstFilled(parsedAddress.country, extractionValueFor(fields, [/^(resident|client)\s+(current\s+)?country$/i, /^country$/i]));
  return {
    ...parsedAddress,
    address,
    addressSearch: parsedAddress.addressSearch || [address, city, state, zip].filter(Boolean).join(", "),
    city,
    county,
    state,
    zip,
    country,
    addressType: firstFilled(parsedAddress.addressType, extractionValueFor(fields, [/^(address|residence)\s+type$/i])) || "Home",
  };
};

const extractionContactFromField = (field = {}, fallbackRelationship = "") => {
  const label = String(field.label || "");
  const value = String(field.value || "").trim();
  const relationship = relationshipFromText(`${label} ${value}`, fallbackRelationship);
  if (/\b(no|none|not applicable|not known)\b.*\b(contact|guardian|representative|poa)\b/i.test(value)) return null;
  const phone = cleanPhone(value);
  const name = contactNameFromExtractedValue(value, relationship);
  if (!name && !phone) return null;
  return normalizeStateAssessmentContact({
    name,
    relationship,
    relationshipOther: relationshipOtherFromText(value, relationship),
    mobilePhone: phone && !/physician|pharmacy|doctor|consultant/i.test(`${label} ${value}`) ? phone : "",
    officePhone: phone && /physician|pharmacy|doctor|consultant/i.test(`${label} ${value}`) ? phone : "",
    preferredPhone: phone && /physician|pharmacy|doctor|consultant/i.test(`${label} ${value}`) ? "officePhone" : "mobilePhone",
    notes: value,
  });
};

const extractionContactsForStateAssessment = (document = {}) => {
  const fields = extractionFieldsFromDocument(document);
  const candidates = [
    ...fields
      .filter((field) => /emergency\s+contact|secondary\s+contact|power\s+of\s+attorney|guardian|physician|doctor|consultant|specialist|pharmacy|case\s+manager|social\s+worker/i.test(field.label || ""))
      .map((field) => [field, relationshipFromText(`${field.label} ${field.value}`)]),
  ];
  const seen = new Set();
  return candidates
    .map(([field, fallback]) => field ? extractionContactFromField(field, fallback) : null)
    .filter((contact) => {
      if (!contact) return false;
      if (isNegativeContactStatement(contact)) return false;
      const key = contactIdentityKey(contact) || `${contact.mobilePhone || contact.officePhone}`.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const stateAssessmentBioPatchFromExtraction = (document = {}) => {
  const fields = extractionFieldsFromDocument(document);
  const profileValues = fields
    .filter((field) => /client_profile/i.test(field.sectionKey || "") || /resident|client|profile/i.test(field.label || ""))
    .map((field) => String(field.value || ""))
    .join(" | ");
  const residentName = firstFilled(
    extractionValueFor(fields, [/resident\s+name/i, /client\s+name/i], ["client_profile"]),
    extractionValueFor(fields, [/resident\s+name/i, /client\s+name/i]),
  );
  const nameParts = splitStateAssessmentName(residentName);
  const residentPhone = firstFilled(
    extractionValueFor(fields, [/resident\s+(phone|telephone|mobile|home)/i, /client\s+(phone|telephone|mobile|home)/i, /mobile\s+(phone|number)/i, /home\s+(phone|number)/i, /phone\s+(number|telephone)/i, /telephone/i]),
    extractionEmbeddedValueFor(fields, [/(?:resident|client)\s+(?:phone|telephone|mobile|home)(?:\s+phone|\s+number)?\s*[:|-]\s*([^|\n]+)/i]),
    phoneFromText(profileValues),
  );
  const phone = cleanPhone(residentPhone);
  const backgroundNotes = extractionValuesFor(fields, [
    /reason\s+for\s+admission/i,
    /resident\s+goal/i,
    /initial\s+impression/i,
    /household/i,
    /support\s+before\s+admission/i,
    /preferred\s+language/i,
    /interpreter/i,
    /room/i,
  ], [], 10)
    .map((value) => cleanExtractionScalar(value))
    .filter(Boolean)
    .join("\n");
  const medicareNumber = firstFilled(
    cleanExtractionScalar(extractionValueFor(fields, [/medicare/i])),
    extractionEmbeddedValueFor(fields, [/medicare\s*(?:number|no\.?|#)?\s*[:|-]\s*([A-Za-z0-9-]+)/i]),
  );
  const medicaidNumber = firstFilled(
    cleanExtractionScalar(extractionValueFor(fields, [/medicaid/i])),
    extractionEmbeddedValueFor(fields, [/medicaid\s*(?:number|no\.?|#)?\s*[:|-]\s*([A-Za-z0-9-]+)/i]),
  );
  const socialSecurityNumber = firstFilled(
    cleanExtractionScalar(extractionValueFor(fields, [/social\s+security/i, /\bssn\b/i])),
    extractionEmbeddedValueFor(fields, [/(?:social\s+security|ssn)\s*(?:number|no\.?|#)?\s*[:|-]\s*([0-9-]+)/i]),
  );
  const email = firstFilled(
    emailFromText(extractionValueFor(fields, [/resident\s+email|client\s+email|email/i])),
    emailFromText(profileValues),
  );
  const addressFromProfile = extractionEmbeddedValueFor(fields, [/(?:resident|client|current|home)\s+address\s*[:|-]\s*([^|\n]+)/i]);
  const addressDocument = addressFromProfile ? { ...document, sections: [{ sectionKey: "client_profile", subsections: [{ fields: [{ label: "Resident address", value: addressFromProfile }] }] }] } : document;
  return {
    firstName: nameParts.firstName,
    middleName: nameParts.middleName,
    lastName: nameParts.lastName,
    preferredName: cleanExtractionScalar(extractionValueFor(fields, [/preferred\s+name/i, /nickname/i])),
    dateOfBirth: dateInputFromExtraction(extractionValueFor(fields, [/date\s+of\s+birth/i, /^dob$/i, /birth\s+date/i])),
    sex: displaySexValue(extractionValueFor(fields, [/client\s+gender/i, /resident\s+gender/i, /client\s+sex/i, /resident\s+sex/i, /^(sex|gender)$/i])),
    primaryLanguage: cleanExtractionScalar(extractionValueFor(fields, [/preferred\s+language/i, /primary\s+language/i, /^language$/i])),
    codeStatus: codeStatusFromExtraction(fields),
    advancedDirectives: advancedDirectivesFromExtraction(fields),
    medicareNumber,
    medicaidNumber,
    socialSecurityNumber,
    supplementaryInsurances: yesNoFromValue(firstFilled(
      extractionValueFor(fields, [/supplementary\s+insurance|supplemental\s+insurance|other\s+insurance/i]),
      extractionEmbeddedValueFor(fields, [/(?:supplementary|supplemental|other)\s+insurance\s*[:|-]\s*([^|\n]+)/i]),
    )),
    mobilePhone: phone,
    preferredPhone: phone ? "mobilePhone" : "",
    email,
    ...stateAssessmentAddressFromExtraction(addressFromProfile ? extractionFieldsFromDocument(addressDocument) : fields),
    backgroundNotes,
  };
};

const mergeExtractionBioPrefill = (current = {}, patch = {}) => ({
  ...current,
  ...Object.fromEntries(Object.entries(patch).filter(([, value]) => value !== null && value !== undefined && value !== "")),
});

const stateAssessmentBioFrom = (assessment = {}, onboardingPreview = null) => {
  const initialPayload = parseAssessmentPayloadObject(assessment.initialStateAssessment || assessment.stateAssessment);
  const previewStages = onboardingStagesFromPreview(onboardingPreview);
  const previewBio = previewStages.bio || {};
  const previewResident = previewResidentFrom(onboardingPreview, previewStages);
  const assessmentBio = initialPayload.bioData || initialPayload.residentBioData || initialPayload.bio || {};
  const savedBio = hasObjectValues(assessmentBio) || hasObjectValues(initialPayload)
    ? mergeFilledObjects(previewBio, initialPayload, assessmentBio)
    : previewBio;
  const sourceForm = savedBio.sourceForm && typeof savedBio.sourceForm === "object" ? savedBio.sourceForm : {};
  const resident = mergeFilledObjects(
    previewResident,
    assessment.residentData,
    assessment.residentDetails,
    assessment.resident,
  );
  const personal = mergeFilledObjects(
    previewBio.personalInformation,
    previewResident.personalInformation,
    resident.personalInformation,
    assessment.personalInformation,
    savedBio.personal,
    savedBio.personalInformation,
  );
  const demographics = mergeFilledObjects(
    previewBio.demographics,
    previewResident.demographics,
    resident.demographics,
    assessment.demographics,
    assessment.residentDemographics,
    savedBio.residentDemographics,
    savedBio.demographics,
  );
  const coverage = mergeFilledObjects(
    resident.healthAndCoverage,
    assessment.healthAndCoverage,
    savedBio.coverage,
    savedBio.healthAndCoverage,
  );
  const address = mergeFilledObjects(
    resident.address,
    resident.locationInformation,
    assessment.address,
    savedBio.locationInformation,
    savedBio.address,
  );
  const contactInfo = mergeFilledObjects(
    savedBio.contactInformation,
    savedBio.residentContactInformation,
    assessment.contactInformation,
    resident.contactInformation,
    {
      primaryEmail: firstFilled(resident.primaryContactEmail, resident.email, resident.primaryEmail),
      primaryPhone: firstFilled(resident.primaryContactPhone, resident.phone, resident.phoneNumber),
      mobilePhone: resident.mobilePhone,
      officePhone: firstFilled(resident.officePhone, resident.workPhone, resident.businessPhone),
      homePhone: resident.homePhone,
      secondaryPhone: resident.secondaryPhone,
      preferredPhone: resident.preferredPhone,
    },
  );
  const phones = mergeFilledObjects(
    savedBio.contactInformation?.phones,
    savedBio.residentContactInformation?.phones,
    savedBio.phones,
    assessment.phones,
    assessment.contactInformation?.phones,
    resident.phones,
    resident.contactInformation?.phones,
    {
      mobile: firstFilled(resident.mobilePhone, resident.contactInformation?.mobilePhone),
      office: firstFilled(resident.officePhone, resident.workPhone, resident.businessPhone, resident.contactInformation?.officePhone),
      home: firstFilled(resident.homePhone, resident.contactInformation?.homePhone),
      primary: firstFilled(resident.primaryContactPhone, resident.phone, resident.contactInformation?.primaryPhone),
      preferred: firstFilled(resident.preferredPhone, resident.contactInformation?.preferredPhone),
      secondary: firstFilled(resident.secondaryPhone, resident.contactInformation?.secondaryPhone),
    },
  );
  const splitResidentName = String(processResidentName(assessment) || "").split(/\s+/).filter(Boolean);
  const middleName = firstFilled(
    savedBio.middleName,
    personal.middleName,
    previewBio.personalInformation?.middleName,
    previewResident.middleName,
    resident.middleName,
    assessment.residentMiddleName,
  );
  const sex = displaySexValue(
    firstFilled(
      savedBio.sex,
      savedBio.gender,
      personal.sex,
      personal.gender,
      previewBio.personalInformation?.sex,
      previewBio.personalInformation?.gender,
      previewResident.sex,
      previewResident.gender,
      demographics.sex,
      demographics.gender,
      resident.sex,
      resident.gender,
      assessment.sex,
      assessment.gender,
      assessment.residentSex,
      assessment.residentGender,
    ),
  );
  const primaryLanguage = firstFilled(
    savedBio.primaryLanguage,
    savedBio.language,
    savedBio.preferredLanguage,
    personal.primaryLanguage,
    personal.language,
    personal.preferredLanguage,
    previewBio.personalInformation?.primaryLanguage,
    previewBio.personalInformation?.language,
    previewBio.personalInformation?.preferredLanguage,
    previewResident.primaryLanguage,
    previewResident.language,
    previewResident.preferredLanguage,
    demographics.primaryLanguage,
    demographics.language,
    demographics.preferredLanguage,
    resident.primaryLanguage,
    resident.language,
    resident.preferredLanguage,
    assessment.primaryLanguage,
    assessment.language,
    assessment.preferredLanguage,
    assessment.residentPrimaryLanguage,
    assessment.residentLanguage,
  );
  const dateOfBirth = normalizeDateInputValue(
    firstFilled(
      savedBio.dateOfBirth,
      savedBio.birthDate,
      savedBio.dob,
      personal.birthDate,
      personal.dateOfBirth,
      personal.dob,
      previewBio.personalInformation?.birthDate,
      previewBio.personalInformation?.dateOfBirth,
      previewBio.personalInformation?.dob,
      previewResident.birthDate,
      previewResident.dateOfBirth,
      previewResident.dob,
      resident.birthDate,
      resident.dateOfBirth,
      resident.dob,
      assessment.dateOfBirth,
      assessment.birthDate,
      assessment.dob,
      assessment.residentDateOfBirth,
      assessment.residentBirthDate,
    ),
  );
  const preferredPhoneValue = firstFilled(savedBio.preferredPhone, phones.preferred, contactInfo.preferredPhone, contactInfo.primaryPhone);
  const preferredPhoneKey = preferredPhoneFieldForStateAssessment(preferredPhoneValue, {
    mobilePhone: firstFilled(savedBio.mobilePhone, phones.mobile, phones.primary, contactInfo.mobilePhone, contactInfo.primaryPhone, resident.mobilePhone, resident.primaryContactPhone),
    officePhone: firstFilled(savedBio.officePhone, phones.office, contactInfo.officePhone, resident.officePhone, resident.workPhone, resident.businessPhone),
    homePhone: firstFilled(savedBio.homePhone, phones.home, contactInfo.homePhone, contactInfo.secondaryPhone, resident.homePhone),
  }) || (contactInfo.primaryPhone ? "mobilePhone" : "");

  return {
    ...emptyStateAssessmentBioForm,
    ...sourceForm,
    firstName: firstFilled(savedBio.firstName, personal.firstName, previewBio.personalInformation?.firstName, previewResident.firstName, resident.firstName, assessment.residentFirstName, splitResidentName[0]),
    middleName,
    lastName: firstFilled(savedBio.lastName, personal.lastName, previewBio.personalInformation?.lastName, previewResident.lastName, resident.lastName, assessment.residentLastName, (middleName ? splitResidentName.slice(2).join(" ") : splitResidentName.slice(1).join(" "))),
    preferredName: firstFilled(savedBio.preferredName, personal.preferredName, previewBio.personalInformation?.preferredName, previewResident.preferredName, resident.preferredName, assessment.preferredName),
    dateOfBirth,
    socialSecurityNumber: savedBio.socialSecurityNumber || personal.socialSecurityNumber || "",
    sex,
    primaryLanguage,
    heightFeet: savedBio.heightFeet ?? personal.heightFeet ?? previewResident.heightFeet ?? resident.heightFeet ?? "",
    heightInches: savedBio.heightInches ?? personal.heightInches ?? previewResident.heightInches ?? resident.heightInches ?? "",
    weightLbs: savedBio.weightLbs ?? personal.weightLbs ?? previewResident.weightLbs ?? resident.weightLbs ?? "",
    race: firstFilled(savedBio.race, personal.race, previewResident.race, resident.race),
    raceOther: firstFilled(savedBio.raceOther, personal.raceOther, previewResident.raceOther, resident.raceOther),
    maritalStatus: firstFilled(savedBio.maritalStatus, personal.maritalStatus, previewResident.maritalStatus, resident.maritalStatus),
    placeOfBirth: firstFilled(savedBio.placeOfBirth, personal.placeOfBirth, previewResident.placeOfBirth, resident.placeOfBirth),
    religion: firstFilled(savedBio.religion, personal.religion, previewResident.religion, resident.religion),
    moveInDate: firstFilled(sourceForm.moveInDate, previewResident.moveInDate, resident.moveInDate, resident.admissionDate)
      ? dateInputValue(firstFilled(sourceForm.moveInDate, previewResident.moveInDate, resident.moveInDate, resident.admissionDate))
      : "",
    codeStatus: firstFilled(sourceForm.codeStatus, savedBio.codeStatus, coverage.codeStatus, previewResident.codeStatus, resident.codeStatus),
    advancedDirectives: yesNoFromValue(firstFilled(
      sourceForm.advancedDirectives,
      savedBio.advancedDirectives,
      coverage.advancedDirectives,
      previewResident.advancedDirectives,
      resident.advancedDirectives,
      assessment.advancedDirectives,
    )),
    medicareNumber: savedBio.medicareNumber || coverage.medicareNumber || "",
    medicaidNumber: savedBio.medicaidNumber || coverage.medicaidNumber || "",
    supplementaryInsurances: yesNoFromValue(firstFilled(
      savedBio.supplementaryInsurances,
      savedBio.otherInsurances,
      coverage.supplementaryInsurances,
      coverage.otherInsurances,
    )),
    mobilePhone: firstFilled(savedBio.mobilePhone, phones.mobile, phones.primary, contactInfo.mobilePhone, contactInfo.primaryPhone, resident.mobilePhone, resident.primaryContactPhone),
    officePhone: firstFilled(savedBio.officePhone, phones.office, contactInfo.officePhone, resident.officePhone, resident.workPhone, resident.businessPhone),
    homePhone: firstFilled(savedBio.homePhone, phones.home, contactInfo.homePhone, contactInfo.secondaryPhone, resident.homePhone),
    preferredPhone: preferredPhoneKey,
    email: firstFilled(savedBio.email, contactInfo.primaryEmail, contactInfo.email, resident.primaryContactEmail, resident.email, assessment.email),
    addressSearch: savedBio.addressSearch || savedBio.addressText || address.formattedAddress || address.address || address.addressLine1 || "",
    address: savedBio.addressText || address.address || address.addressLine1 || "",
    addressLine2: savedBio.addressLine2 || address.addressLine2 || "",
    city: savedBio.city || address.city || "",
    county: savedBio.county || address.county || "",
    state: savedBio.state || address.state || "",
    country: savedBio.country || address.country || "",
    zip: savedBio.zip || address.zipCode || address.zip || "",
    latitude: savedBio.latitude || address.latitude || "",
    longitude: savedBio.longitude || address.longitude || "",
    placeId: savedBio.placeId || address.placeId || "",
    addressType: savedBio.addressType || address.addressType || resident.residentAddressType || "",
    addressTypeOther: savedBio.addressTypeOther || address.otherAddressType || resident.residentOtherAddressType || "",
    backgroundNotes: savedBio.backgroundNotes || savedBio.notes || resident.backgroundNotes || "",
  };
};

const stateAssessmentBioPayload = (form = emptyStateAssessmentBioForm) => {
  const preferredPhone = form.preferredPhone ? form[form.preferredPhone] || "" : "";
  return {
    sourceForm: form,
    firstName: form.firstName || "",
    middleName: form.middleName || "",
    lastName: form.lastName || "",
    preferredName: form.preferredName || "",
    dateOfBirth: form.dateOfBirth || null,
    socialSecurityNumber: form.socialSecurityNumber || "",
    sex: form.sex || "",
    primaryLanguage: form.primaryLanguage || "",
    heightFeet: form.heightFeet === "" || form.heightFeet == null ? null : Number(form.heightFeet),
    heightInches: form.heightInches === "" || form.heightInches == null ? null : Number(form.heightInches),
    weightLbs: form.weightLbs === "" || form.weightLbs == null ? null : Number(form.weightLbs),
    race: form.race || "",
    raceOther: form.race === "Other" ? form.raceOther || "" : "",
    maritalStatus: form.maritalStatus || "",
    placeOfBirth: form.placeOfBirth || "",
    religion: form.religion || "",
    moveInDate: form.moveInDate || null,
    codeStatus: form.codeStatus || "",
    advancedDirectives: form.advancedDirectives || "",
    medicareNumber: form.medicareNumber || "",
    medicaidNumber: form.medicaidNumber || "",
    supplementaryInsurances: form.supplementaryInsurances || "",
    email: form.email || "",
    mobilePhone: form.mobilePhone || "",
    officePhone: form.officePhone || "",
    homePhone: form.homePhone || "",
    preferredPhone: form.preferredPhone || "",
    addressSearch: form.addressSearch || form.address || "",
    addressText: form.address || "",
    addressLine1: form.address || "",
    addressLine2: form.addressLine2 || "",
    city: form.city || "",
    county: form.county || "",
    state: form.state || "",
    country: form.country || "",
    zip: form.zip || "",
    zipCode: form.zip || "",
    latitude: form.latitude || null,
    longitude: form.longitude || null,
    placeId: form.placeId || "",
    addressType: form.addressType || "",
    addressTypeOther: form.addressTypeOther || "",
    otherAddressType: form.addressType === "Other" ? form.addressTypeOther || "" : "",
    phones: {
      mobile: form.mobilePhone || "",
      office: form.officePhone || "",
      home: form.homePhone || "",
      preferred: preferredPhone,
    },
    address: {
      address: form.address || "",
      addressLine1: form.address || "",
      addressLine2: form.addressLine2 || "",
      city: form.city || "",
      county: form.county || "",
      state: form.state || "",
      country: form.country || "",
      zipCode: form.zip || "",
      latitude: form.latitude || null,
      longitude: form.longitude || null,
      placeId: form.placeId || "",
      addressType: form.addressType || "",
      otherAddressType: form.addressType === "Other" ? form.addressTypeOther || "" : "",
    },
    contactInformation: {
      primaryEmail: form.email || "",
      primaryPhone: preferredPhone || form.mobilePhone || form.officePhone || form.homePhone || "",
      secondaryPhone: [form.mobilePhone, form.officePhone, form.homePhone].find((phone) => phone && phone !== preferredPhone) || "",
      mobilePhone: form.mobilePhone || "",
      officePhone: form.officePhone || "",
      homePhone: form.homePhone || "",
      preferredPhone,
      email: form.email || "",
      phones: {
        mobile: form.mobilePhone || "",
        office: form.officePhone || "",
        home: form.homePhone || "",
        preferred: preferredPhone,
      },
    },
    residentContactInformation: {
      primaryEmail: form.email || "",
      primaryPhone: preferredPhone || form.mobilePhone || form.officePhone || form.homePhone || "",
      secondaryPhone: [form.mobilePhone, form.officePhone, form.homePhone].find((phone) => phone && phone !== preferredPhone) || "",
      mobilePhone: form.mobilePhone || "",
      officePhone: form.officePhone || "",
      homePhone: form.homePhone || "",
      preferredPhone,
      email: form.email || "",
      phones: {
        mobile: form.mobilePhone || "",
        office: form.officePhone || "",
        home: form.homePhone || "",
        preferred: preferredPhone,
      },
    },
    locationInformation: {
      address: form.address || "",
      addressLine1: form.address || "",
      addressLine2: form.addressLine2 || "",
      city: form.city || "",
      county: form.county || "",
      state: form.state || "",
      country: form.country || "",
      zipCode: form.zip || "",
      latitude: form.latitude || null,
      longitude: form.longitude || null,
      placeId: form.placeId || "",
      addressType: form.addressType || "",
      otherAddressType: form.addressType === "Other" ? form.addressTypeOther || "" : "",
    },
    personalInformation: {
      firstName: form.firstName || "",
      middleName: form.middleName || "",
      lastName: form.lastName || "",
      preferredName: form.preferredName || "",
      birthDate: form.dateOfBirth || null,
      sex: form.sex || "",
      socialSecurityNumber: form.socialSecurityNumber || "",
      primaryLanguage: form.primaryLanguage || "",
      heightFeet: form.heightFeet === "" || form.heightFeet == null ? null : Number(form.heightFeet),
      heightInches: form.heightInches === "" || form.heightInches == null ? null : Number(form.heightInches),
      weightLbs: form.weightLbs === "" || form.weightLbs == null ? null : Number(form.weightLbs),
      race: form.race || "",
      raceOther: form.race === "Other" ? form.raceOther || "" : "",
      maritalStatus: form.maritalStatus || "",
      placeOfBirth: form.placeOfBirth || "",
      religion: form.religion || "",
    },
    healthAndCoverage: {
      codeStatus: form.codeStatus || "",
      advancedDirectives: yesNoToBoolean(form.advancedDirectives),
      medicareNumber: form.medicareNumber || "",
      medicaidNumber: form.medicaidNumber || "",
      supplementaryInsurances: form.supplementaryInsurances || "",
    },
    backgroundNotes: form.backgroundNotes || "",
    notes: form.backgroundNotes || "",
  };
};

const residentBioAssessmentPayloadKeys = [
  "bioData",
  "residentBioData",
  "bio",
  "collateralContacts",
  "colleteralContacts",
  "contacts",
  "sourceForm",
  "firstName",
  "middleName",
  "lastName",
  "preferredName",
  "dateOfBirth",
  "birthDate",
  "dob",
  "socialSecurityNumber",
  "sex",
  "gender",
  "primaryLanguage",
  "language",
  "preferredLanguage",
  "codeStatus",
  "advancedDirectives",
  "medicareNumber",
  "medicaidNumber",
  "supplementaryInsurances",
  "otherInsurances",
  "email",
  "mobilePhone",
  "officePhone",
  "homePhone",
  "preferredPhone",
  "phones",
  "contactInformation",
  "residentContactInformation",
  "addressSearch",
  "addressText",
  "address",
  "addressLine1",
  "addressLine2",
  "city",
  "county",
  "state",
  "country",
  "zip",
  "zipCode",
  "latitude",
  "longitude",
  "placeId",
  "addressType",
  "addressTypeOther",
  "otherAddressType",
  "locationInformation",
  "personalInformation",
  "healthAndCoverage",
  "backgroundNotes",
  "notes",
];

const assessmentOnlyInitialPayload = (payload = {}) => {
  const nextPayload = { ...(payload && typeof payload === "object" && !Array.isArray(payload) ? payload : {}) };
  residentBioAssessmentPayloadKeys.forEach((key) => {
    delete nextPayload[key];
  });
  return nextPayload;
};

const stateAssessmentResidentBioRequest = ({
  assessment = {},
  bioPayload = {},
  collateralContacts = [],
  patientType = "NEW",
  preview = null,
  residentId = "",
}) => {
  const root = preview?.previewPayload || preview?.payload || preview || {};
  const previewResident = previewResidentFrom(preview);
  const preferredPhone =
    bioPayload.phones?.preferred ||
    bioPayload.mobilePhone ||
    bioPayload.officePhone ||
    bioPayload.homePhone ||
    "";
  const address = bioPayload.address || {};

  return {
    resident: {
      workspaceId: assessment.workspaceId || root.workspaceId || preview?.workspaceId || previewResident.workspaceId || null,
      organizationId: assessment.organizationId || root.organizationId || preview?.organizationId || previewResident.organizationId || null,
      businessEntityId: assessment.businessEntityId || root.businessEntityId || previewResident.businessEntityId || null,
      businessLocationId: assessment.businessLocationId || root.businessLocationId || previewResident.businessLocationId || null,
      process: {
        patientType: patientType === "RESIDENT" ? "RESIDENT" : "NEW",
        startDate:
          assessment.processStartDate ||
          assessment.startDate ||
          root.processStartDate ||
          root.startDate ||
          new Date().toISOString().slice(0, 10),
        endDate: assessment.processEndDate || assessment.endDate || root.processEndDate || root.endDate || null,
        status: previewResident.status || assessment.residentStatus || "PROSPECTIVE",
        existingResidentId: residentId || previewResident.id || null,
      },
      personalInformation: {
        firstName: bioPayload.firstName || "",
        middleName: bioPayload.middleName || "",
        lastName: bioPayload.lastName || "",
        preferredName: bioPayload.preferredName || "",
        birthDate: bioPayload.dateOfBirth || null,
        sex: stateAssessmentApiSexValue(bioPayload.sex),
        socialSecurityNumber: bioPayload.socialSecurityNumber || "",
        primaryLanguage: bioPayload.primaryLanguage || "",
        heightFeet: bioPayload.heightFeet,
        heightInches: bioPayload.heightInches,
        weightLbs: bioPayload.weightLbs,
        race: bioPayload.race || "",
        placeOfBirth: bioPayload.placeOfBirth || "",
        religion: bioPayload.religion || "",
      },
      healthAndCoverage: {
        codeStatus: bioPayload.codeStatus || "",
        advancedDirectives: yesNoToBoolean(bioPayload.advancedDirectives),
        medicareNumber: bioPayload.medicareNumber || "",
        medicaidNumber: bioPayload.medicaidNumber || "",
        supplementaryInsurances: bioPayload.supplementaryInsurances === "Yes" ? ["Other supplementary insurance reported in assessment"] : [],
      },
      backgroundNotes: bioPayload.backgroundNotes || "",
      notes: bioPayload.backgroundNotes || "",
      onboardingStepStatus: root.onboardingStepStatus || preview?.onboardingStepStatus || null,
      contactInformation: bioPayload.contactInformation || null,
      residentContactInformation: bioPayload.residentContactInformation || bioPayload.contactInformation || null,
      address: {
        address: address.address || bioPayload.addressText || "",
        addressLine2: address.addressLine2 || "",
        city: address.city || "",
        county: address.county || "",
        state: address.state || "",
        country: address.country || "",
        zipCode: address.zipCode || bioPayload.zipCode || "",
        latitude: address.latitude || null,
        longitude: address.longitude || null,
        placeId: address.placeId || "",
        addressType: address.addressType || "",
        otherAddressType: address.otherAddressType || "",
      },
      admission: {
        admissionDate: bioPayload.moveInDate || assessment.admissionDate || root.admissionDate || null,
        dischargeDate: assessment.dischargeDate || root.dischargeDate || null,
        conditionDiagnosisAndCauseForAdmission: assessment.admissionDescription || "",
      },
      contacts: collateralContacts,
      collateralContacts,
      colleteralContacts: collateralContacts,
      careHistory: root.careHistory || null,
      initialCareSnapshot: root.initialCareSnapshot || null,
      documents: root.documents || null,
      sourceForm: {
        ...root.sourceForm,
        ...bioPayload.sourceForm,
        contactInformation: bioPayload.contactInformation,
        residentContactInformation: bioPayload.residentContactInformation,
        contacts: collateralContacts,
        collateralContacts,
        colleteralContacts: collateralContacts,
        preferredPhone,
      },
    },
  };
};

const preliminaryFormToHtml = (form = emptyPreliminaryPlanForm) => `
  <h3>Preliminary Service Plan Summary</h3>
  <p><strong>Generated from captured state assessment, Meet and Greet, onboarding, provider review, and daily note data.</strong></p>
  ${preliminaryPlanFieldSections.map((section) => `
    <h4>${section.title}</h4>
    ${section.fields.map((field) => `
      <p><strong>${preliminaryFieldLabel(field)}:</strong> ${stripHtmlText(form[preliminaryFieldKey(field)]) || "Not captured yet."}</p>
    `).join("")}
  `).join("")}
`;

const parseAssessmentDocuments = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parseAssessmentDocuments(parsed);
    } catch {
      return value.split(",").map((name) => ({ name: name.trim() })).filter((item) => item.name);
    }
  }
  if (typeof value === "object") {
    return parseAssessmentDocuments(value.uploadedDocuments || value.documents || value.pages || value.items);
  }
  return [];
};

const uploadedDocumentsFromRecord = (record = {}) =>
  parseAssessmentDocuments(
    record.uploadedDocuments ||
    record.payload ||
    record.documents ||
    record.initialStateAssessment ||
    record.stateAssessment,
  );

const parseAssessmentPayloadObject = (value) => {
  if (!value) return {};
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return typeof value === "object" && !Array.isArray(value) ? value : {};
};

const probationRecordPayload = (record = {}) => {
  const payload = parseAssessmentPayloadObject(record?.payload || record);
  return {
    ...record,
    ...payload,
    dailyNotes: Array.isArray(payload.dailyNotes) ? payload.dailyNotes : Array.isArray(record?.dailyNotes) ? record.dailyNotes : [],
    assignedStaff: Array.isArray(payload.assignedStaff) ? payload.assignedStaff : Array.isArray(record?.assignedStaff) ? record.assignedStaff : [],
  };
};

const normalizePersonList = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) => {
      if (typeof item === "string") {
        const [name = "", ...relationshipParts] = item.split(" - ");
        const relationship = relationshipParts.join(" - ").trim();
        return {
          name: name.trim(),
          relationship: meetAndGreetRelationshipOptions.includes(relationship) ? relationship : relationship ? "Other" : "",
          relationshipOther: meetAndGreetRelationshipOptions.includes(relationship) ? "" : relationship,
        };
      }
      const relationship = item.relationship || item.role || item.contactType || item.type || "";
      return {
        name: item.name || item.fullName || [item.firstName, item.lastName].filter(Boolean).join(" "),
        relationship: meetAndGreetRelationshipOptions.includes(relationship) ? relationship : relationship ? "Other" : "",
        relationshipOther: item.relationshipOther || item.otherRelationship || (meetAndGreetRelationshipOptions.includes(relationship) ? "" : relationship),
      };
    })
    .filter((item) => item.name || item.relationship);

const meetAndGreetPersonPayload = (items = []) =>
  items
    .filter((item) => item.name || item.relationship || item.relationshipOther)
    .map((item) => ({
      ...item,
      relationship: item.relationship === "Other" ? item.relationshipOther || "Other" : item.relationship,
    }));

const meetAndGreetPersonValue = (person = {}, index = 0, prefix = "person") =>
  person.sourceId || person.id || `${prefix}-${index}-${person.name || ""}-${person.relationship || person.relationshipOther || ""}`;

const selectedMeetAndGreetPersonValue = (person = {}, options = []) => {
  const sourceValue = person.sourceId || person.id || "";
  if (sourceValue && options.some((option) => option.value === sourceValue)) return sourceValue;
  const name = String(person.name || "").trim().toLowerCase();
  const relationship = String(person.relationship === "Other" ? person.relationshipOther : person.relationship || "").trim().toLowerCase();
  return options.find((option) =>
    option.name.toLowerCase() === name &&
    String(option.relationship || "").trim().toLowerCase() === relationship
  )?.value || "";
};

const meetAndGreetOptionsWithCurrent = (options = [], person = {}, index = 0, prefix = "person", fallbackRelationship = "") => {
  if (selectedMeetAndGreetPersonValue(person, options) || !person.name) return options;
  const relationship = person.relationship === "Other" ? person.relationshipOther || fallbackRelationship : person.relationship || fallbackRelationship;
  return [
    {
      value: meetAndGreetPersonValue(person, index, prefix),
      name: person.name,
      relationship,
      label: relationship ? `${person.name} - ${relationship}` : person.name,
    },
    ...options,
  ];
};

const normalizeQuestionResponses = (questions = []) => {
  if (typeof questions === "string") {
    try {
      return normalizeQuestionResponses(JSON.parse(questions));
    } catch {
      return {};
    }
  }
  if (questions && typeof questions === "object" && !Array.isArray(questions)) {
    return questions;
  }
  return (Array.isArray(questions) ? questions : []).reduce((acc, item) => {
    if (Array.isArray(item)) {
      acc[item[0]] = item[1] || "";
    } else if (item?.question) {
      acc[item.question] = item.response || item.answer || "";
    }
    return acc;
  }, {});
};

const parseJsonArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const stringifyJsonValue = (value, fallback = "[]") => {
  try {
    return JSON.stringify(value ?? JSON.parse(fallback));
  } catch {
    return fallback;
  }
};

const residentAssessmentMeetAndGreetRequest = (assessment = {}, payload = {}) => ({
  workspaceId: assessment.workspaceId || null,
  type: assessment.type,
  status: assessment.status || "DRAFT",
  assessmentDate: assessment.assessmentDate || null,
  assessorId: assessment.assessorId || null,
  assessorName: assessment.assessorName || "",
  processStartDate: assessment.processStartDate || null,
  processEndDate: assessment.processEndDate || null,
  patientType: assessment.patientType || assessment.type || "",
  socialWorkerFirstName: assessment.socialWorkerFirstName || "",
  socialWorkerLastName: assessment.socialWorkerLastName || "",
  socialWorkerEmail: assessment.socialWorkerEmail || "",
  socialWorkerPhone: assessment.socialWorkerPhone || "",
  socialWorkerAddress: assessment.socialWorkerAddress || "",
  nextOfKinFirstName: assessment.nextOfKinFirstName || "",
  nextOfKinLastName: assessment.nextOfKinLastName || "",
  nextOfKinEmail: assessment.nextOfKinEmail || "",
  nextOfKinPhone: assessment.nextOfKinPhone || "",
  nextOfKinRelationship: assessment.nextOfKinRelationship || "",
  nextOfKinAddress: assessment.nextOfKinAddress || "",
  meetingDate: normalizeDateInputValue(payload.meetingDate) || null,
  meetingLocation: payload.location || "",
  facilitator: payload.facilitator || "",
  summary: assessment.summary || "",
  clientHistory: assessment.clientHistory || "",
  diagnosisHealthProfile: assessment.diagnosisHealthProfile || "",
  careNeeded: assessment.careNeeded || "",
  medicationBehaviors: assessment.medicationBehaviors || "",
  meetAndGreetNotes: payload.notes || "",
  questionsAndResponses: stringifyJsonValue(payload.questions),
  careCircle: stringifyJsonValue(payload.careCircle),
  careProviderStaff: stringifyJsonValue(payload.careProviderStaff),
  signedDocuments: assessment.signedDocuments || "[]",
  uploadedDocuments: assessment.uploadedDocuments || "[]",
  personalCareNeeds: assessment.personalCareNeeds || "",
  mobilityNeeds: assessment.mobilityNeeds || "",
  cognitiveNeeds: assessment.cognitiveNeeds || "",
  behavioralNeeds: assessment.behavioralNeeds || "",
  medicationNeeds: assessment.medicationNeeds || "",
  dietaryNeeds: assessment.dietaryNeeds || "",
  riskFactors: assessment.riskFactors || "",
  preferences: assessment.preferences || "",
});

const assessmentProcessUpdateRequest = (assessment = {}, overrides = {}, patientType = "NEW") => ({
  workspaceId: assessment.workspaceId || null,
  stageReferences: assessment.stageReferences || null,
  name: assessment.name || assessment.title || "",
  currentScreen: assessment.currentScreen || "",
  type: workflowTypeFor(assessment, patientType),
  stage: assessment.stage || assessment.workflowStage || assessment.currentStage || "PROVIDER_REVIEW",
  status: workflowStatusFor(assessment.status),
  workflowStatus: workflowStatusFor(assessment.workflowStatus || assessment.status),
  stateAssessmentId: assessment.stateAssessmentId || assessment.stageReferences?.initialStateAssessmentId || null,
  meetAndGreetAssessmentId: assessment.meetAndGreetAssessmentId || assessment.stageReferences?.meetAndGreetAssessmentId || null,
  clientOnboardingAssessmentId: assessment.clientOnboardingAssessmentId || assessment.stageReferences?.clientOnboardingAssessmentId || null,
  residentProbationAssessmentId: assessment.residentProbationAssessmentId || assessment.stageReferences?.residentProbationAssessmentId || null,
  preliminaryCarePlanId: assessment.preliminaryCarePlanId || assessment.stageReferences?.preliminaryPlanId || null,
  negotiatedCarePlanId: assessment.negotiatedCarePlanId || assessment.stageReferences?.negotiatedCarePlanId || null,
  finalAssessmentId: assessment.finalAssessmentId || assessment.stageReferences?.finalStateAssessmentId || null,
  annualReviewId: assessment.annualReviewId || assessment.stageReferences?.annualReviewId || null,
  processStartDate: assessment.processStartDate || null,
  processEndDate: assessment.processEndDate || null,
  attachedRecords: assessment.attachedRecords || "",
  documentIds: assessment.documentIds || "",
  dailyNoteIds: assessment.dailyNoteIds || "",
  admissionDate: assessment.admissionDate || null,
  probationStartDate: assessment.probationStartDate || null,
  probationEndDate: assessment.probationEndDate || null,
  providerDecision: assessment.providerDecision || "",
  providerDecisionDate: assessment.providerDecisionDate || null,
  socialWorkerName: assessment.socialWorkerName || "",
  socialWorkerContact: assessment.socialWorkerContact || "",
  nextOfKinName: assessment.nextOfKinName || "",
  nextOfKinRelationship: assessment.nextOfKinRelationship || "",
  nextOfKinContact: assessment.nextOfKinContact || "",
  paymentNegotiationNotes: assessment.paymentNegotiationNotes || "",
  stateConfirmedAt: assessment.stateConfirmedAt || null,
  stateConfirmationDocumentId: assessment.stateConfirmationDocumentId || null,
  notes: assessment.notes || "",
  ...overrides,
});

const assessmentFileName = (file = {}, fallback = "Assessment page") =>
  file.name || file.fileName || file.originalFileName || file.caption || file.title || file.file?.name || fallback;
const assessmentSourceName = (file = {}, fallback = "Assessment source") =>
  file.originalFileName || file.sourceFileName || file.fileName || file.name || fallback;
const assessmentPageNumber = (file = {}, fallbackIndex = 0) => Number(file.pageNumber || file.page || fallbackIndex + 1);
const assessmentPageTitle = (file = {}, fallbackIndex = 0) => file.pageTitle || file.label || `Page ${assessmentPageNumber(file, fallbackIndex)}`;
const assessmentStorageId = (file = {}) => file.fileId || file.storageFileId || file.storageId || "";

const normalizeAssessmentDocumentForPayload = (document = {}, index = 0) => {
  if (typeof document === "string") {
    const name = document.trim();
    return {
      name,
      fileName: name,
      originalFileName: name,
      pageNumber: index + 1,
      pageTitle: `Page ${index + 1}`,
    };
  }
  const pageNumber = assessmentPageNumber(document, index);
  const storageId = assessmentStorageId(document);
  const persistedUrl = assessmentDocumentPayloadUrl(document);
  return {
    fileId: storageId || document.fileId || null,
    storageFileId: storageId || document.storageFileId || null,
    storageId: storageId || document.storageId || null,
    pageNumber,
    pageTitle: assessmentPageTitle(document, index),
    name: assessmentFileName(document, `Assessment page ${pageNumber}`),
    fileName: document.fileName || document.name || document.originalFileName || `Assessment page ${pageNumber}`,
    originalFileName: document.originalFileName || document.sourceFileName || document.fileName || document.name || "",
    caption: document.caption || document.description || assessmentPageTitle(document, index),
    description: document.description || document.caption || "",
    contentType: document.contentType || document.type || document.mimeType || "",
    type: document.type || document.contentType || document.mimeType || "",
    size: document.size || document.fileSize || 0,
    isPhoto: Boolean(document.isPhoto || document.isImage),
    isImage: Boolean(document.isPhoto || document.isImage),
    ...(persistedUrl ? { signedUrl: persistedUrl, previewUrl: persistedUrl, downloadUrl: persistedUrl } : {}),
    confirmed: Boolean(document.confirmed),
    storageStatus: document.storageStatus || (storageId ? "UPLOADED_NOT_CONFIRMED" : "PENDING_UPLOAD"),
    uploaded: Boolean(document.uploaded || storageId),
    uploadedAt: document.uploadedAt || document.addedAt || null,
  };
};
const assessmentDocumentsForPayload = (documents = []) =>
  documents
    .map(normalizeAssessmentDocumentForPayload)
    .filter((document) => assessmentStorageId(document) || assessmentDocumentPayloadUrl(document));

const normalizeUploadedAssessmentPageForState = (document = {}, index = 0) => {
  const normalized = normalizeAssessmentDocumentForPayload(document, index);
  const storageId = assessmentStorageId(normalized);
  const name = assessmentFileName(normalized, `Assessment page ${index + 1}`);
  return {
    ...normalized,
    id: `uploaded-${storageId || normalized.id || normalized.fileName || normalized.name || index}`,
    file: undefined,
    fileId: storageId || normalized.fileId || null,
    storageFileId: storageId || normalized.storageFileId || null,
    name,
    uploaded: true,
    persisted: true,
    needsUpload: false,
    description: normalized.description || normalized.caption || normalized.pageTitle || `Assessment page ${index + 1}`,
    previewUrl: assessmentFileUrl(normalized),
    size: normalized.size || normalized.fileSize || 0,
  };
};

const uploadedAssessmentPagesForState = (documents = []) =>
  documents
    .map(normalizeUploadedAssessmentPageForState)
    .sort((a, b) => Number(b.pageNumber || 0) - Number(a.pageNumber || 0));

const assessmentDocumentStateKey = (documents = []) =>
  JSON.stringify(documents.map((document, index) => ({
    id: assessmentStorageId(document) || document.id || document.fileName || document.name || index,
    pageNumber: assessmentPageNumber(document, index),
    name: assessmentFileName(document, ""),
    description: document.description || document.caption || "",
    url: assessmentFileUrl(document),
  })));

const stateAssessmentNotes = (assessment = {}, pageCount = 0) => {
  const summaryText = typeof assessment.summary === "string" ? assessment.summary.trim() : "";
  const parsedStateAssessment = parseAssessmentPayloadObject(assessment.initialStateAssessment || assessment.stateAssessment);
  const stateAssessment = Object.keys(parsedStateAssessment).length
    ? parsedStateAssessment
    : assessment.initialStateAssessment || assessment.stateAssessment || {};
  const status = stateAssessment.status || assessment.status;
  const count = stateAssessment.pageCount || uploadedDocumentsFromRecord(stateAssessment).length || uploadedDocumentsFromRecord(assessment).length || pageCount;
  if (status || count) {
    return [
      status ? `Status: ${status}` : "",
      count ? `Pages: ${count}` : "",
    ].filter(Boolean).join(" - ");
  }
  if (summaryText && !/^[{[]/.test(summaryText)) return summaryText;
  return "Upload each state assessment page or packet section, then save the assessment record.";
};

const cleanAssessmentUrl = (value) => {
  if (!value) return "";
  const text = String(value).trim();
  if (text.startsWith("{") && text.endsWith("}")) {
    try {
      const parsed = JSON.parse(text);
      return cleanAssessmentUrl(parsed.signedUrl || parsed.previewUrl || parsed.downloadUrl || parsed.url || parsed.data || "");
    } catch {
      return text;
    }
  }
  if ((text.startsWith("\"") && text.endsWith("\"")) || (text.startsWith("'") && text.endsWith("'"))) {
    return text.slice(1, -1).trim();
  }
  return text;
};
const isStorageUrlResolverEndpoint = (value) => {
  const url = cleanAssessmentUrl(value);
  if (!url) return false;
  try {
    const baseUrl = typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const parsed = new URL(url, baseUrl);
    return /^\/storage-service\/api\/v1\/files\/photo\/signed-urls?$/.test(parsed.pathname);
  } catch {
    return /\/storage-service\/api\/v1\/files\/photo\/signed-urls?(?:\?|$)/.test(url);
  }
};
const persistedAssessmentUrl = (value) => {
  const url = cleanAssessmentUrl(value);
  return url && !url.startsWith("blob:") && !isStorageUrlResolverEndpoint(url) ? url : "";
};
const hasLocalAssessmentFile = (file = {}) =>
  typeof File !== "undefined" && file.file instanceof File;
const usableAssessmentUrl = (file = {}, value) => {
  const url = cleanAssessmentUrl(value);
  return url &&
    !isStorageUrlResolverEndpoint(url) &&
    (!url.startsWith("blob:") || hasLocalAssessmentFile(file))
    ? url
    : "";
};
const assessmentFileUrl = (file = {}) =>
  usableAssessmentUrl(file, file.previewUrl) ||
  usableAssessmentUrl(file, file.thumbnailUrl) ||
  usableAssessmentUrl(file, file.signedUrl) ||
  usableAssessmentUrl(file, file.signedURL) ||
  usableAssessmentUrl(file, file.downloadUrl) ||
  usableAssessmentUrl(file, file.downloadURL) ||
  usableAssessmentUrl(file, file.url) ||
  usableAssessmentUrl(file, file.fileUrl) ||
  usableAssessmentUrl(file, file.file?.previewUrl) ||
  usableAssessmentUrl(file, file.file?.signedUrl);
const assessmentDocumentPayloadUrl = (file = {}) =>
  persistedAssessmentUrl(file.signedUrl || file.signedURL) ||
  persistedAssessmentUrl(file.downloadUrl || file.downloadURL) ||
  persistedAssessmentUrl(file.url || file.fileUrl) ||
  persistedAssessmentUrl(file.previewUrl || file.thumbnailUrl);

const isImageAssessmentFile = (file = {}) => {
  const type = String(file.type || file.contentType || file.mimeType || file.file?.type || file.file?.contentType || "").toLowerCase();
  const name = assessmentFileName(file, "").toLowerCase();
  const url = assessmentFileUrl(file).toLowerCase();
  return Boolean(file.isPhoto || file.isImage) || type.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)(\?|$)/.test(name) || /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)(\?|$)/.test(url);
};
const canRenderAssessmentImage = (file = {}) => {
  const type = String(file.type || file.contentType || file.mimeType || file.file?.type || file.file?.contentType || "").toLowerCase();
  const name = assessmentFileName(file, "").toLowerCase();
  const url = assessmentFileUrl(file).toLowerCase();
  if (type && !["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp", "image/bmp", "image/svg+xml"].includes(type)) {
    return false;
  }
  return type.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/.test(name) || /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/.test(url);
};
const isPdfAssessmentFile = (file = {}) => {
  const type = String(file.type || file.contentType || file.mimeType || file.file?.type || "").toLowerCase();
  const name = assessmentFileName(file, "").toLowerCase();
  const url = assessmentFileUrl(file).toLowerCase();
  return type.includes("pdf") || name.endsWith(".pdf") || /\.pdf(\?|$)/.test(url);
};

const formatAssessmentAddress = (value) => {
  if (!value) return "Not set";
  if (typeof value === "string") return value;
  return [
    value.address || value.street || value.line1 || value.addressLine1,
    value.addressLine2 || value.line2,
    value.city,
    value.county,
    value.state,
    value.country,
    value.zipCode || value.zip,
  ].filter(Boolean).join(", ") || "Not set";
};

const extractResidentContacts = (preview) => {
  const root = preview?.previewPayload || preview?.payload || preview || {};
  const stages =
    root.onboardingStages ||
    root.previewPayload?.onboardingStages ||
    root.payload?.onboardingStages ||
    {};
  const contactsStage = stages.contacts || root.contacts || preview?.contacts || {};
  const candidates = [
    contactsStage.collateralContacts,
    contactsStage.colleteralContacts,
    contactsStage.contacts,
    contactsStage.payload?.collateralContacts,
    contactsStage.payload?.colleteralContacts,
    contactsStage.payload?.contacts,
    contactsStage.payload?.resident?.collateralContacts,
    contactsStage.payload?.resident?.colleteralContacts,
    contactsStage.payload?.resident?.contacts,
    contactsStage.payload,
    contactsStage,
    stages.bio?.collateralContacts,
    stages.bio?.colleteralContacts,
    stages.bio?.contacts,
    stages.bio?.resident?.collateralContacts,
    stages.bio?.resident?.colleteralContacts,
    stages.bio?.sourceForm?.contacts,
    stages.bio?.sourceForm?.collateralContacts,
    stages.bio?.sourceForm?.colleteralContacts,
    stages.bio?.resident?.contacts,
    stages.resident?.collateralContacts,
    stages.resident?.colleteralContacts,
    stages.resident?.contacts,
    root.resident?.collateralContacts,
    root.resident?.colleteralContacts,
    root.resident?.contacts,
    preview?.resident?.collateralContacts,
    preview?.resident?.colleteralContacts,
    preview?.resident?.contacts,
    preview?.resident?.contactInformation?.contacts,
    root.contactInformation?.contacts,
    preview?.contactInformation?.contacts,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length) return candidate;
    if (Array.isArray(candidate?.contacts) && candidate.contacts.length) return candidate.contacts;
  }
  return [];
};

const onboardingStagesFromPreview = (preview) => {
  const root = preview?.previewPayload || preview?.payload || preview || {};
  return root.onboardingStages ||
    root.previewPayload?.onboardingStages ||
    root.payload?.onboardingStages ||
    {};
};

const previewResidentFrom = (preview, stages = onboardingStagesFromPreview(preview)) => {
  const root = preview?.previewPayload || preview?.payload || preview || {};
  return {
    ...(root.resident || {}),
    ...(preview?.resident || {}),
    ...(stages.resident || {}),
  };
};

const hasObjectValues = (value) =>
  Boolean(value && typeof value === "object" && !Array.isArray(value) && Object.values(value).some((item) => item !== null && item !== undefined && item !== ""));

const firstFilled = (...values) =>
  values.find((value) => value !== null && value !== undefined && value !== "") || "";

const mergeFilledObjects = (...objects) =>
  objects.reduce((merged, object) => {
    if (!object || typeof object !== "object" || Array.isArray(object)) return merged;
    Object.entries(object).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        merged[key] = value;
      }
    });
    return merged;
  }, {});

const preferredPhoneFieldForStateAssessment = (preferredValue, phoneValues = {}) => {
  const normalized = String(preferredValue || "").trim();
  if (!normalized) return "";
  if (stateAssessmentPreferredPhoneOptions.some(([field]) => field === normalized)) return normalized;
  return stateAssessmentPreferredPhoneOptions.find(([field]) => String(phoneValues[field] || "").trim() === normalized)?.[0] || "";
};

const contactFullName = (contact = {}) =>
  contact.name ||
  contact.fullName ||
  [contact.firstName, contact.middleName, contact.lastName].filter(Boolean).join(" ") ||
  "Not set";

const contactPhone = (contact = {}) => {
  const phones = contact.phones || {};
  const preferred = phones.preferred;
  if (preferred && phones[preferred]) return phones[preferred];
  return (
    contact.phone ||
    contact.primaryPhone ||
    contact.mobilePhone ||
    phones.mobile ||
    phones.office ||
    phones.home ||
    "Not set"
  );
};

const contactEmail = (contact = {}) => contact.email || contact.primaryEmail || "Not set";
const contactRelationship = (contact = {}) => contact.relationship || contact.contactType || contact.type || contact.role || "";
const contactNotes = (contact = {}) => contact.notes || contact.description || "";
const isSocialWorkerContact = (contact = {}) => {
  const relationship = normalizeStageKey(contactRelationship(contact));
  return relationship.includes("SOCIAL_WORKER") || relationship.includes("CASE_MANAGER") || relationship.includes("CASE_MANAGEMENT");
};

const socialWorkerFromAssessment = (assessment = {}) => {
  const safeAssessment = assessment || {};
  const socialWorker = safeAssessment.socialWorker || {};
  const socialWorkerName = processSocialWorkerName(safeAssessment);
  return {
    name: socialWorkerName,
    firstName: safeAssessment.socialWorkerFirstName || socialWorker.firstName || socialWorkerName.split(" ")[0],
    lastName: safeAssessment.socialWorkerLastName || socialWorker.lastName || socialWorkerName.split(" ").slice(1).join(" "),
    phone: safeAssessment.socialWorkerPhone || socialWorker.phone || socialWorker.mobilePhone || "Not set",
    email: safeAssessment.socialWorkerEmail || socialWorker.email || "Not set",
    agency: safeAssessment.socialWorkerAgency || socialWorker.agency || "Not set",
    address: formatAssessmentAddress(safeAssessment.socialWorkerAddress || safeAssessment.socialWorkerAddressDetails || socialWorker.address || socialWorker.addressDetails),
    relationship: "Social Worker",
    notes: "",
    source: "Assessment record",
  };
};

const socialWorkerDetailsFor = (assessment = {}, contacts = []) => {
  const contact = (Array.isArray(contacts) ? contacts : []).find(isSocialWorkerContact);
  if (!contact) return socialWorkerFromAssessment(assessment);
  const name = contactFullName(contact);
  const [firstName = "", ...lastNameParts] = name === "Not set" ? [] : name.split(" ");
  return {
    name,
    firstName: contact.firstName || firstName || "Not set",
    lastName: contact.lastName || lastNameParts.join(" ") || "Not set",
    phone: contactPhone(contact),
    email: contactEmail(contact),
    agency: contact.agency || contact.organization || contact.company || "Not set",
    address: formatAssessmentAddress(contact.address || contact.addressDetails || contact.location),
    relationship: contactRelationship(contact) || "Social Worker",
    notes: contactNotes(contact),
    source: "Resident contacts",
  };
};

const assessmentThumbnail = (file = {}) => {
  if (canRenderAssessmentImage(file) && assessmentFileUrl(file)) {
    return (
      <img
        alt=""
        src={assessmentFileUrl(file)}
        onError={(event) => {
          event.currentTarget.hidden = true;
          event.currentTarget.parentElement?.classList.add("assessment-attachment-thumb-failed");
        }}
      />
    );
  }
  const icon = isImageAssessmentFile(file) ? "ti-photo" : isPdfAssessmentFile(file) ? "ti-file-type-pdf" : "ti-file-description";
  return <i className={`ti ${icon}`} />;
};

const renumberReadyPages = (pages = []) =>
  pages.map((page, index) => {
    const pageNumber = pages.length - index;
    return {
      ...page,
      pageNumber,
      pageTitle: `Page ${pageNumber}`,
      description: page.description && /^Assessment page \d+$/i.test(page.description) ? `Assessment page ${pageNumber}` : page.description,
    };
  });

const SortableStateAssessmentPageRow = ({
  index,
  onDescriptionChange,
  onPreview,
  onRemove,
  page,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: page.id });
  const style = {
    opacity: isDragging ? 0.65 : 1,
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const isUploaded = Boolean(page.uploaded || page.persisted || assessmentStorageId(page));
  const fileSize = Number(page.size || page.fileSize || page.file?.size || 0);

  return (
    <div className="assessment-uploaded-doc-row state-assessment-page-row" ref={setNodeRef} style={style}>
      <button className="state-assessment-drag-handle" type="button" {...attributes} {...listeners} aria-label={`Reorder ${assessmentPageTitle(page, index)}`}>
        <i className="ti ti-grip-vertical" />
      </button>
      <button className={`assessment-attachment-thumb ${isImageAssessmentFile(page) ? "photo" : "document"}`} type="button" onClick={() => onPreview(page)}>
        {assessmentThumbnail(page)}
      </button>
      <strong>
        {assessmentPageTitle(page, index)}
        <small>
          {isUploaded ? "Uploaded page" : "Ready to upload"} - source: {assessmentSourceName(page)}
          {page.convertedFromHeic ? ` - converted to ${assessmentFileName(page)}` : ""}
          {page.conversionWarning ? " - HEIC preview unavailable" : ""}
          {fileSize ? ` - ${(fileSize / 1024).toFixed(1)} KB` : ""}
        </small>
      </strong>
      <input
        aria-label={`Description for ${assessmentPageTitle(page, index)}`}
        value={page.description || ""}
        onChange={(event) => onDescriptionChange(page.id, event.target.value)}
      />
      <span className="assessment-document-actions">
        <button type="button" onClick={() => onPreview(page)}><i className="ti ti-eye" /></button>
        <button type="button" onClick={() => onRemove(page.id)}><i className="ti ti-trash" /></button>
      </span>
    </div>
  );
};

export default function AssessmentProcessDetailPage() {
  const { assessmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { uploadFile, uploading: storageUploading } = useFileUpload();
  const { user, careRoles = [] } = useAuth();
  const { selectedWorkspace } = useWorkspace();
  const navigationAssessment = location.state?.assessment || null;
  const navigationResourceType = location.state?.assessmentResourceType || "";
  const isCurrentResidentAssessmentRoute = location.pathname.endsWith("/current");
  const [assessment, setAssessment] = useState(() => navigationAssessment);
  const [loading, setLoading] = useState(() => !navigationAssessment);
  const [error, setError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");
  const [assessmentResourceType, setAssessmentResourceType] = useState(() => navigationResourceType);
  const addDailyNoteRequestHandledRef = useRef(false);
  const editDailyNoteRequestHandledRef = useRef("");
  const [uploadingStateAssessmentPages, setUploadingStateAssessmentPages] = useState(false);
  const [heicProcessing, setHeicProcessing] = useState(false);
  const [extractedDocument, setExtractedDocument] = useState(null);
  const [extractingAssessmentData, setExtractingAssessmentData] = useState(false);
  const [extractionError, setExtractionError] = useState("");
  const [extractedDocumentDrawerOpen, setExtractedDocumentDrawerOpen] = useState(false);
  const [selectedStageKey, setSelectedStageKey] = useState("");
  const [initialAssessmentRecord, setInitialAssessmentRecord] = useState(undefined);
  const initialAssessmentProcessId = assessment ? processId(assessment) || assessmentId : "";
  const assessmentResidentId = assessment ? processResidentId(assessment) : "";
  const needsAppConfig = Boolean(
    selectedStageKey && (
      isStateAssessmentStage({ stageKeys: [selectedStageKey] }) ||
      isFinalAssessmentStage({ stageKeys: [selectedStageKey] }) ||
      selectedStageKey === "MEET_AND_GREET"
    ),
  );
  const needsWorkspaceMembers = selectedStageKey === "MEET_AND_GREET";
  const { appConfig, loading: appConfigLoading } = useAppConfig({ enabled: needsAppConfig });
  const { users: workspaceMemberUsers, loading: workspaceMembersLoading } = useWorkspaceMembers({
    enabled: needsWorkspaceMembers,
  });
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey ||
    appConfig?.googleMapsApiMatrixKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    "";
  const [stateAssessmentPages, setStateAssessmentPages] = useState([]);
  const loadedAssessmentDocumentKeyRef = useRef("");
  const [residentContacts, setResidentContacts] = useState([]);
  const [residentOnboardingPreview, setResidentOnboardingPreview] = useState(null);
  const [residentOnboardingPreviewLoaded, setResidentOnboardingPreviewLoaded] = useState(false);
  const [, setResidentContactsError] = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const [previewFileIndex, setPreviewFileIndex] = useState(-1);
  const [previewRotation, setPreviewRotation] = useState(0);
  const [stateAssessmentBioForm, setStateAssessmentBioForm] = useState(emptyStateAssessmentBioForm);
  const [stateAssessmentContacts, setStateAssessmentContacts] = useState([]);
  const [stateAssessmentShowSsn, setStateAssessmentShowSsn] = useState(false);
  const [stateAssessmentBioSaving, setStateAssessmentBioSaving] = useState(false);
  const [stateAssessmentBioError, setStateAssessmentBioError] = useState("");
  const [stateAssessmentBioSuccess, setStateAssessmentBioSuccess] = useState("");
  const loadedStateAssessmentBioKeyRef = useRef("");
  const appliedExtractionPrefillKeyRef = useRef("");
  const [meetAndGreetForm, setMeetAndGreetForm] = useState(() => ({
    ...emptyMeetAndGreetForm,
    meetingDate: dateInputValue(new Date()),
  }));
  const [meetAndGreetCareCircle, setMeetAndGreetCareCircle] = useState([]);
  const [meetAndGreetStaff, setMeetAndGreetStaff] = useState([]);
  const [meetAndGreetResponses, setMeetAndGreetResponses] = useState({});
  const [meetAndGreetCustomQuestion, setMeetAndGreetCustomQuestion] = useState("");
  const [meetAndGreetCustomQuestions, setMeetAndGreetCustomQuestions] = useState([]);
  const [meetAndGreetSaving, setMeetAndGreetSaving] = useState(false);
  const [meetAndGreetError, setMeetAndGreetError] = useState("");
  const [meetAndGreetSuccess, setMeetAndGreetSuccess] = useState("");
  const [providerReviewSaving, setProviderReviewSaving] = useState("");
  const [providerReviewError, setProviderReviewError] = useState("");
  const [providerReviewSuccess, setProviderReviewSuccess] = useState("");
  const [preliminaryPlanForm, setPreliminaryPlanForm] = useState(emptyPreliminaryPlanForm);
  const [preliminaryPlanDescription, setPreliminaryPlanDescription] = useState(generatedPreliminaryPlanHtml);
  const [preliminaryGenerationLocked, setPreliminaryGenerationLocked] = useState(false);
  const [preliminaryPlanHistory, setPreliminaryPlanHistory] = useState([]);
  const [preliminaryAttachedFiles, setPreliminaryAttachedFiles] = useState([]);
  const [preliminaryPreviewOpen, setPreliminaryPreviewOpen] = useState(false);
  const [preliminarySaving, setPreliminarySaving] = useState(false);
  const [preliminaryError, setPreliminaryError] = useState("");
  const [preliminarySuccess, setPreliminarySuccess] = useState("");
  const [probationRecord, setProbationRecord] = useState(null);
  const [probationError, setProbationError] = useState("");
  const [probationNoteRange, setProbationNoteRange] = useState({
    start: monthStartInputValue(new Date()),
    end: monthEndInputValue(new Date()),
  });
  const [weeklyReportRange, setWeeklyReportRange] = useState({
    start: dateInputValue(shiftMinutes(new Date(), -6 * 24 * 60)),
    end: dateInputValue(new Date()),
  });
  const [probationNotesView, setProbationNotesView] = useState("LIST");
  const [probationCalendarMonth, setProbationCalendarMonth] = useState(monthInputValue(new Date()));
  const [calendarDayNotes, setCalendarDayNotes] = useState(null);
  const [visibleProbationNoteCount, setVisibleProbationNoteCount] = useState(10);
  const [weeklyDailyNoteReport, setWeeklyDailyNoteReport] = useState(null);
  const [weeklyDailyNoteReportLoading, setWeeklyDailyNoteReportLoading] = useState(false);
  const [weeklyDailyNoteReportError, setWeeklyDailyNoteReportError] = useState("");
  const [weeklyDailyNoteReportEditing, setWeeklyDailyNoteReportEditing] = useState(false);
  const [weeklyDailyNoteReportEditValues, setWeeklyDailyNoteReportEditValues] = useState(null);
  const [weeklyDailyNoteReportSaving, setWeeklyDailyNoteReportSaving] = useState(false);
  const [savedWeeklyDailyNoteReports, setSavedWeeklyDailyNoteReports] = useState([]);
  const [behaviorAnalysisDrawerOpen, setBehaviorAnalysisDrawerOpen] = useState(false);
  const [behaviorAnalysisDetailsOpen, setBehaviorAnalysisDetailsOpen] = useState(false);
  const [dailyNoteDrawerOpen, setDailyNoteDrawerOpen] = useState(false);
  const [dailyNoteEditingNote, setDailyNoteEditingNote] = useState(null);
  const [dailyNoteDate, setDailyNoteDate] = useState(dateInputValue(new Date()));
  const [dailyNoteApproximateTime, setDailyNoteApproximateTime] = useState(timeInputValue(new Date()));
  const [dailyNoteEndTime, setDailyNoteEndTime] = useState(timeInputValue(shiftMinutes(new Date(), 15)));
  const [dailyNoteCategory, setDailyNoteCategory] = useState("Behavior");
  const [dailyNoteOtherCategory, setDailyNoteOtherCategory] = useState("");
  const [dailyNoteSelectionMode, setDailyNoteSelectionMode] = useState("SELECT");
  const [dailyNoteSelectionQuery, setDailyNoteSelectionQuery] = useState("");
  const [showAllDailyNoteSelections, setShowAllDailyNoteSelections] = useState(false);
  const [dailyNoteQuickSelections, setDailyNoteQuickSelections] = useState([]);
  const [dailyNoteRichText, setDailyNoteRichText] = useState("");
  const [dailyNoteInterventionAction, setDailyNoteInterventionAction] = useState("");
  const [dailyNotePhotos, setDailyNotePhotos] = useState([]);
  const [dailyNotePhotoPreview, setDailyNotePhotoPreview] = useState(null);
  const [dailyNotePhotoProcessing, setDailyNotePhotoProcessing] = useState(false);
  const [dailyNoteSaving, setDailyNoteSaving] = useState(false);
  const [showDailyNoteResidentDob, setShowDailyNoteResidentDob] = useState(false);
  const [selectedDailyNote, setSelectedDailyNote] = useState(null);
  const [probationStaffDrawer, setProbationStaffDrawer] = useState(null);
  const [probationStaffSaving, setProbationStaffSaving] = useState(false);
  const [finalAssessmentApproval, setFinalAssessmentApproval] = useState({
    approvalStatus: "Draft",
    approvedBy: "",
    approvedAt: "",
    stateConfirmationReference: "",
    approvalNotes: "",
  });
  const [finalAssessmentCompleting, setFinalAssessmentCompleting] = useState(false);
  const [finalAssessmentCompleteError, setFinalAssessmentCompleteError] = useState("");
  const [finalAssessmentSaving, setFinalAssessmentSaving] = useState(false);
  const [finalAssessmentSaveSuccess, setFinalAssessmentSaveSuccess] = useState("");
  const [stageCompletingKey, setStageCompletingKey] = useState("");
  const [assessmentStatusChanging, setAssessmentStatusChanging] = useState("");
  const [stageCompletionMessage, setStageCompletionMessage] = useState("");
  const stateAssessmentPageUrlsRef = useRef([]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const hydratedPatientType = isCurrentResidentAssessmentRoute ? "RESIDENT" : assessmentPatientType(assessment || {});
  const canCompleteAssessmentStages = careRoles.includes("care_owner") || careRoles.includes("care_admin");
  const canManageDailyNotes =
    careRoles.includes("care_giver") ||
    careRoles.includes("caregiver") ||
    careRoles.includes("care_owner") ||
    careRoles.includes("care_admin");
  const dailyNoteSearchActive = dailyNoteSelectionMode === "SEARCH";
  const visibleDailyNoteSelectionGroups = useMemo(
    () => {
      if (dailyNoteSearchActive && !dailyNoteSelectionQuery.trim()) return [];
      return dailyNoteSelectionGroups(
        dailyNoteQuickSelectionsByCategory,
        dailyNoteCategory,
        dailyNoteSearchActive ? dailyNoteSelectionQuery : "",
        showAllDailyNoteSelections,
      );
    },
    [dailyNoteCategory, dailyNoteSearchActive, dailyNoteSelectionQuery, showAllDailyNoteSelections],
  );
  const dailyNoteSelectionTotal = useMemo(
    () => dailyNoteSearchActive && !dailyNoteSelectionQuery.trim()
      ? 0
      : dailyNoteSelectionCount(
          dailyNoteQuickSelectionsByCategory,
          dailyNoteCategory,
          dailyNoteSearchActive ? dailyNoteSelectionQuery : "",
        ),
    [dailyNoteCategory, dailyNoteSearchActive, dailyNoteSelectionQuery],
  );

  useEffect(() => {
    let isMounted = true;
    const justCreatedAssessment = Boolean(location.state?.justCreatedAssessment);
    const navigationAssessmentForId = navigationAssessment && String(processId(navigationAssessment) || navigationAssessment.id || "") === String(assessmentId)
      ? navigationAssessment
      : null;
    if (navigationAssessmentForId) {
      setAssessment(navigationAssessmentForId);
      setAssessmentResourceType(navigationResourceType || "residentAssessment");
      setError("");
      setLoading(false);
    } else {
      setLoading(true);
    }

    const loadAssessment = async () => {
      const maxAttempts = justCreatedAssessment ? 5 : 1;
      let lastError = null;
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        try {
          if (isCurrentResidentAssessmentRoute) {
            const [resident, assessmentProcesses] = await Promise.all([
              careService.residents.get(assessmentId),
              careService.assessmentProcesses.list({ residentId: assessmentId }).catch(() => []),
            ]);
            const activeProcess = activeAssessmentProcessForResident(assessmentProcesses);
            const activeProcessId = processId(activeProcess);
            if (activeProcessId) {
              navigate(`/assessments/${activeProcessId}/new`, { replace: true });
              return;
            }

            const payload = {
              assessmentType: "ANNUAL_ASSESSMENT",
              patientType: "RESIDENT",
              residentId: assessmentId,
              residentName: residentNameForCurrentAssessment(resident),
              startDate: new Date().toISOString().slice(0, 10),
              status: "OPEN",
              stage: "YEARLY_STATE_ASSESSMENT",
            };
            const created = await careService.assessmentProcesses.create(payload);
            const createdId = processId(created) || created?.id || created?.assessmentProcessId;
            if (!createdId) throw new Error("Assessment process was created without an id.");
            navigate(`/assessments/${createdId}/new`, {
              replace: true,
              state: { assessment: created || { ...payload, id: createdId }, assessmentResourceType: "assessmentProcess", justCreatedAssessment: true },
            });
            return;
          }

          const data = await careService.assessmentProcesses.get(assessmentId);
          if (!isMounted) return;
          setAssessment({
            ...(navigationAssessmentForId || {}),
            ...(data || {}),
            assessmentExtraction: data?.assessmentExtraction || navigationAssessmentForId?.assessmentExtraction || null,
            initialStateAssessment: data?.initialStateAssessment || navigationAssessmentForId?.initialStateAssessment,
            stateAssessment: data?.stateAssessment || navigationAssessmentForId?.stateAssessment,
          });
          setAssessmentResourceType("assessmentProcess");
          setError("");
          setLoading(false);
          return;
        } catch (assessmentProcessError) {
          try {
            const data = await careService.residentAssessments.get(assessmentId);
            if (!isMounted) return;
            setAssessment(data || null);
            setAssessmentResourceType("residentAssessment");
            setError("");
            setLoading(false);
            return;
          } catch (residentAssessmentError) {
            lastError = residentAssessmentError || assessmentProcessError;
            if (attempt < maxAttempts - 1) await wait(500);
          }
        }
      }
      if (!isMounted) return;
      if (!navigationAssessmentForId) {
        setAssessment(null);
        setAssessmentResourceType("");
        setError(lastError?.response?.data?.message || lastError?.message || "Unable to load the resident care process.");
      }
      setLoading(false);
    };

    loadAssessment();
    return () => {
      isMounted = false;
    };
  }, [assessmentId, isCurrentResidentAssessmentRoute, location.state, navigate, navigationAssessment, navigationResourceType]);

  useEffect(() => {
    if (!assessmentResidentId) {
      setResidentContacts([]);
      setResidentOnboardingPreview(null);
      setResidentOnboardingPreviewLoaded(true);
      setResidentContactsError("");
      return undefined;
    }
    let isMounted = true;
    setResidentOnboardingPreviewLoaded(false);
    careService.residents
      .getOnboardingPreview(assessmentResidentId)
      .then((preview) => {
        if (!isMounted) return;
        const previewContacts = extractResidentContacts(preview);
        setResidentOnboardingPreview(preview || null);
        setResidentContacts((current) => (previewContacts.length ? previewContacts : current));
        setResidentContactsError("");
      })
      .catch((err) => {
        if (!isMounted) return;
        setResidentOnboardingPreview(null);
        setResidentContacts([]);
        setResidentContactsError(err?.response?.data?.message || err?.message || "Unable to load resident contacts.");
      })
      .finally(() => {
        if (isMounted) setResidentOnboardingPreviewLoaded(true);
      });
    return () => {
      isMounted = false;
    };
  }, [assessmentResidentId]);

  useEffect(() => {
    if (!assessment || assessmentResourceType === "assessmentProcess") return undefined;
    const processAssessmentId = linkedAssessmentProcessIdFrom(
      assessment,
      assessmentId,
      assessmentResourceType,
      residentOnboardingPreview,
    );
    if (!processAssessmentId || processAssessmentId === assessmentId) return undefined;
    let isMounted = true;
    careService.assessmentProcesses
      .get(processAssessmentId)
      .then((workflow) => {
        if (!isMounted || !workflow) return;
        setAssessment(workflow);
        setAssessmentResourceType("assessmentProcess");
        const workflowStageKey = normalizeStageKey(workflow.stage || workflow.workflowStage || workflow.currentStage);
        const workflowStages = assessmentFlowStages.filter((stage) => stage.appliesTo.includes(assessmentPatientType(workflow)));
        const hashStageKey = assessmentStageKeyFromHash(window.location.hash, workflowStages, assessmentPatientType(workflow));
        if (workflowStageKey && !selectedStageKey && !hashStageKey) {
          setSelectedStageKey(workflowStageKey);
          const workflowStage = workflowStages.find((stage) =>
            stage.stageKeys.some((key) => normalizeStageKey(key) === workflowStageKey),
          );
          if (workflowStage) {
            setAssessmentStageHash(workflowStage, assessmentPatientType(workflow));
          }
        }
      })
      .catch(() => undefined);
    return () => {
      isMounted = false;
    };
  }, [assessment, assessmentId, assessmentResourceType, residentOnboardingPreview, selectedStageKey]);

  useEffect(() => {
    if (assessmentResourceType !== "assessmentProcess" || !initialAssessmentProcessId) return undefined;
    let isMounted = true;
    setInitialAssessmentRecord(undefined);
    careService.assessmentRecords
      .byProcessAndType(initialAssessmentProcessId, "initial-assessment")
      .then((record) => {
        if (isMounted) setInitialAssessmentRecord(record || {});
      })
      .catch((err) => {
        if (!isMounted) return;
        setInitialAssessmentRecord({});
        setUploadError(err?.response?.status === 404 ? "" : (err?.response?.data?.message || err?.message || "Unable to load assessment details."));
      });
    return () => {
      isMounted = false;
    };
  }, [assessmentResourceType, initialAssessmentProcessId]);

  useEffect(() => {
    if (!assessment || assessmentResourceType === "assessmentProcess") return;
    setInitialAssessmentRecord(assessment.initialStateAssessment || assessment.stateAssessment || assessment);
  }, [assessment, assessmentResourceType]);

  useEffect(() => {
    if (!assessment) return undefined;
    const needsInitialAssessmentData =
      isStateAssessmentStage({ stageKeys: [selectedStageKey] }) ||
      isOnboardingStage({ stageKeys: [selectedStageKey] });
    if (!needsInitialAssessmentData) return undefined;
    const localPayload = parseAssessmentPayloadObject(assessment.initialStateAssessment || assessment.stateAssessment);
    const hydrateInitialAssessment = (record = {}) => {
      const payload = {
        ...localPayload,
        ...parseAssessmentPayloadObject(record?.payload || record),
      };
      const recordDocuments = uploadedDocumentsFromRecord(payload.uploadedDocuments ? payload : record);
      const documents = recordDocuments.length
        ? recordDocuments
        : uploadedDocumentsFromRecord(assessment.initialStateAssessment || assessment.stateAssessment || assessment);
      const documentKey = assessmentDocumentStateKey(documents);
      const initialDocumentKey = `initial:${documentKey}`;
      if (initialDocumentKey === loadedAssessmentDocumentKeyRef.current) return;
      setStateAssessmentPages(renumberReadyPages(uploadedAssessmentPagesForState(documents)));
      loadedAssessmentDocumentKeyRef.current = initialDocumentKey;
    };
    if (initialAssessmentRecord === undefined) return undefined;
    hydrateInitialAssessment(initialAssessmentRecord);
    return undefined;
  }, [assessment, initialAssessmentRecord, selectedStageKey]);

  useEffect(() => {
    const needsInitialBioData =
      isStateAssessmentStage({ stageKeys: [selectedStageKey] }) ||
      isOnboardingStage({ stageKeys: [selectedStageKey] });
    if (!assessment || !needsInitialBioData) return undefined;
    let isMounted = true;
    const hydrateBio = (record = {}) => {
      if (!isMounted) return;
      const recordPayload = parseAssessmentPayloadObject(record?.payload || record);
      const currentPayload = assessmentOnlyInitialPayload(parseAssessmentPayloadObject(assessment.initialStateAssessment || assessment.stateAssessment));
      const initialPayload = assessmentOnlyInitialPayload({
        ...currentPayload,
        ...recordPayload,
      });
      const bioAssessment = Object.keys(initialPayload).length
        ? { ...assessment, initialStateAssessment: initialPayload }
        : assessment;
      const previewStages = onboardingStagesFromPreview(residentOnboardingPreview);
      const previewBio = previewStages.bio || {};
      const previewResident = previewResidentFrom(residentOnboardingPreview, previewStages);
      const previewPersonal = previewBio.personalInformation || previewResident.personalInformation || {};
      const savedBio = initialPayload.bioData || initialPayload.residentBioData || initialPayload.bio || {};
      const savedContacts =
        Array.isArray(savedBio.collateralContacts) ? savedBio.collateralContacts :
        Array.isArray(savedBio.colleteralContacts) ? savedBio.colleteralContacts :
        Array.isArray(savedBio.contacts) ? savedBio.contacts : [];
      const previewContacts = extractResidentContacts(residentOnboardingPreview);
      const savedExtraction = recordPayload.assessmentExtraction ||
        initialPayload.assessmentExtraction ||
        assessment?.assessmentExtraction ||
        null;
      const extractedBioPatch = savedExtraction ? stateAssessmentBioPatchFromExtraction(savedExtraction) : {};
      const extractedContacts = savedExtraction ? extractionContactsForStateAssessment(savedExtraction) : [];
      const bioKey = JSON.stringify({
        id: assessment.id || assessmentId,
        residentId: processResidentId(assessment),
        bio: savedBio || null,
        previewBio,
        contacts: savedContacts.length ? savedContacts : previewContacts.length ? previewContacts : residentContacts,
        residentName: processResidentName(assessment),
        residentDateOfBirth: previewPersonal.birthDate || previewPersonal.dateOfBirth || previewPersonal.dob || previewResident.birthDate || previewResident.dateOfBirth || previewResident.dob || assessment.resident?.birthDate || assessment.resident?.dateOfBirth || assessment.resident?.dob || assessment.birthDate || assessment.dateOfBirth || assessment.dob || assessment.residentBirthDate || assessment.residentDateOfBirth || "",
        residentSex: previewPersonal.sex || previewPersonal.gender || previewResident.sex || previewResident.gender || assessment.resident?.sex || assessment.resident?.gender || assessment.sex || assessment.gender || assessment.residentSex || assessment.residentGender || "",
        residentPrimaryLanguage: previewPersonal.primaryLanguage || previewPersonal.language || previewPersonal.preferredLanguage || previewResident.primaryLanguage || previewResident.language || previewResident.preferredLanguage || assessment.resident?.primaryLanguage || assessment.resident?.language || assessment.resident?.preferredLanguage || assessment.primaryLanguage || assessment.language || assessment.preferredLanguage || assessment.residentPrimaryLanguage || assessment.residentLanguage || "",
      });
      if (bioKey === loadedStateAssessmentBioKeyRef.current) return;
      const hydratedBioForm = mergeExtractionBioPrefill(
        stateAssessmentBioFrom(bioAssessment, residentOnboardingPreview),
        extractedBioPatch,
      );
      setStateAssessmentBioForm((current) => mergeFilledObjects(hydratedBioForm, current));
      setStateAssessmentContacts((current) => mergeStateAssessmentContacts([
        ...(savedContacts.length ? savedContacts : previewContacts.length ? previewContacts : residentContacts),
        ...extractedContacts,
        ...current,
      ]));
      setStateAssessmentBioError("");
      setStateAssessmentBioSuccess("");
      loadedStateAssessmentBioKeyRef.current = bioKey;
    };
    if (initialAssessmentRecord === undefined) return undefined;
    hydrateBio(initialAssessmentRecord);
    return () => {
      isMounted = false;
    };
  }, [assessment, assessmentId, initialAssessmentRecord, residentContacts, residentOnboardingPreview, selectedStageKey]);

  useEffect(() => {
    const needsMeetAndGreetData = ["MEET_AND_GREET", "PROVIDER_REVIEW"].includes(selectedStageKey);
    if (!assessment || !needsMeetAndGreetData) return undefined;
    let isMounted = true;
    const processAssessmentId = linkedAssessmentProcessIdFrom(assessment, assessmentId, assessmentResourceType, residentOnboardingPreview);
    if (!processAssessmentId) {
      setMeetAndGreetForm({
        meetingDate: normalizeDateInputValue(assessment.meetingDate) || dateInputValue(new Date()),
        location: assessment.meetingLocation || "",
        facilitator: assessment.facilitator || "",
        facilitatorUserId: assessment.facilitatorUserId || "",
        notes: assessment.meetAndGreetNotes || "",
      });
      setMeetAndGreetCareCircle(
        normalizePersonList(parseJsonArray(assessment.careCircle)).length
          ? normalizePersonList(parseJsonArray(assessment.careCircle))
          : [],
      );
      setMeetAndGreetStaff(normalizePersonList(parseJsonArray(assessment.careProviderStaff)));
      setMeetAndGreetResponses(normalizeQuestionResponses(parseJsonArray(assessment.questionsAndResponses)));
      setMeetAndGreetCustomQuestions([]);
      setMeetAndGreetError("");
      return undefined;
    }
    careService.meetAndGreet
      .getForProcess(processAssessmentId)
      .then((record) => {
        if (!isMounted) return;
        const payload = parseAssessmentPayloadObject(record?.payload || record);
        setMeetAndGreetForm({
          meetingDate: normalizeDateInputValue(payload.meetingDate || payload.meetingDay || payload.date) || dateInputValue(new Date()),
          location: payload.location || payload.meetingLocation || "",
          facilitator: payload.facilitator || "",
          facilitatorUserId: payload.facilitatorUserId || payload.facilitatorWorkspaceMemberId || "",
          notes: payload.notes || "",
        });
        setMeetAndGreetCareCircle(
          normalizePersonList(payload.careCircle).length
            ? normalizePersonList(payload.careCircle)
            : [],
        );
        setMeetAndGreetStaff(normalizePersonList(payload.careProviderStaff || payload.staffPresent));
        setMeetAndGreetResponses(normalizeQuestionResponses(payload.questions || payload.questionsAndResponses || payload.responses));
        setMeetAndGreetCustomQuestions(
          (payload.customQuestions || [])
          .map((item) => (typeof item === "string" ? item : item?.question))
          .filter(Boolean),
        );
        setMeetAndGreetError("");
      })
      .catch((err) => {
        if (!isMounted) return;
        setMeetAndGreetForm((current) => current);
        setMeetAndGreetCareCircle((current) => current);
        setMeetAndGreetError(err?.response?.status === 404 ? "" : (err?.response?.data?.message || err?.message || "Unable to load Meet and Greet details."));
      });
    return () => {
      isMounted = false;
    };
  }, [assessment, assessmentId, assessmentResourceType, residentContacts, residentOnboardingPreview, selectedStageKey]);

  useEffect(() => {
    if (!assessment || selectedStageKey !== "PRELIMINARY_CARE_PLAN") return undefined;
    const localPayload = parseAssessmentPayloadObject(assessment.preliminaryPlan);
    const hydratePreliminary = (payload = {}) => {
      const summaryForm = {
        ...emptyPreliminaryPlanForm,
        ...preliminaryPlanValueFromExtraction(extractedDocument),
        ...(payload.summaryForm || payload.preliminaryPlanForm || {}),
      };
      setPreliminaryPlanForm(summaryForm);
      setPreliminaryPlanDescription(payload.description || payload.preliminaryPlanDescription || preliminaryFormToHtml(summaryForm));
      setPreliminaryGenerationLocked(Boolean(payload.generationLocked || payload.preventFurtherGeneration));
      setPreliminaryPlanHistory(Array.isArray(payload.history || payload.planHistory) ? (payload.history || payload.planHistory) : []);
      setPreliminaryAttachedFiles(Array.isArray(payload.attachedFiles) ? payload.attachedFiles : []);
      setPreliminaryError("");
    };
    if (assessmentResourceType !== "assessmentProcess") {
      hydratePreliminary(localPayload);
      return undefined;
    }
    let isMounted = true;
    const processAssessmentId = processId(assessment) || assessmentId;
    careService.assessmentRecords
      .byProcessAndType(processAssessmentId, "preliminary-plan")
      .then((record) => {
        if (!isMounted) return;
        hydratePreliminary({
          ...localPayload,
          ...parseAssessmentPayloadObject(record?.payload || record),
        });
      })
      .catch((err) => {
        if (!isMounted) return;
        hydratePreliminary(localPayload);
        setPreliminaryError(err?.response?.status === 404 ? "" : (err?.response?.data?.message || err?.message || "Unable to load preliminary plan."));
      });
    return () => {
      isMounted = false;
    };
  }, [assessment, assessmentId, assessmentResourceType, extractedDocument, selectedStageKey]);

  useEffect(() => {
    if (!assessment || !selectedStageKey || (
      !isProbationNotesStage({ stageKeys: [selectedStageKey] })
      && !isNegotiatedCarePlanStage({ stageKeys: [selectedStageKey] })
    )) return undefined;
    const recordType = hydratedPatientType === "RESIDENT" ? "annual-residency-progress" : "resident-30-day-probation";
    const hydrateProbation = (record = {}, databaseNotes = []) => {
      const payload = probationRecordPayload(record);
      const dailyNotes = mergeDailyNotes(payload.dailyNotes || [], databaseNotes);
      const startDate = payload.startDate || assessment.probationStartDate || assessment.processStartDate || "";
      const estimatedEndDate = oneYearEstimatedEndDate(startDate);
      setProbationRecord({
        startDate,
        endDate: payload.endDate || assessment.probationEndDate || assessment.processEndDate || estimatedEndDate,
        status: payload.status || assessment.status || "Active observation",
        description: payload.description || (hydratedPatientType === "RESIDENT"
          ? "Annual residency progress period for ongoing resident care patterns, status changes, care coordination, daily notes, family updates, and items that may affect the negotiated care plan."
          : "Thirty-day probation observation period for behavior, health, welfare, medication routines, care needs, and discrepancies between state assessment, Meet and Greet, and actual home routines."),
        evaluationNotes: payload.evaluationNotes || "",
        dailyNotes,
        assignedStaff: payload.assignedStaff || [],
      });
      setProbationError("");
      setVisibleProbationNoteCount(10);
    };

    if (assessmentResourceType !== "assessmentProcess") {
      hydrateProbation({
        startDate: assessment.probationStartDate || assessment.processStartDate,
        endDate: assessment.probationEndDate || assessment.processEndDate,
        status: assessment.status,
        description: assessment.notes,
        dailyNotes: parseJsonArray(assessment.dailyNotes),
        assignedStaff: parseJsonArray(assessment.assignedStaff || assessment.careProviderStaff),
      });
      return undefined;
    }

    let isMounted = true;
    const processAssessmentId = processId(assessment) || assessmentId;
    Promise.allSettled([
      careService.assessmentRecords.byProcessAndType(processAssessmentId, recordType),
      careService.dailyNotes.list(compactObject({
        residentId: processResidentId(assessment),
        assessmentProcessId: processAssessmentId,
        recordType,
        assessmentRecordType: recordType,
      })),
    ])
      .then(([recordResult, dailyNotesResult]) => {
        if (!isMounted) return;
        const record = recordResult.status === "fulfilled" ? recordResult.value : {};
        const databaseNotes = dailyNotesResult.status === "fulfilled" ? asArray(dailyNotesResult.value) : [];
        hydrateProbation(record, databaseNotes);
        const errors = [recordResult, dailyNotesResult].filter((result) => result.status === "rejected").map((result) => result.reason);
        const visibleError = errors.find((err) => err?.response?.status !== 404);
        if (visibleError) {
          setProbationError(visibleError?.response?.data?.message || visibleError?.message || "Unable to load daily notes.");
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        hydrateProbation({});
        setProbationError(err?.response?.status === 404 ? "" : (err?.response?.data?.message || err?.message || "Unable to load daily notes."));
      });
    return () => {
      isMounted = false;
    };
  }, [assessment, assessmentId, assessmentResourceType, hydratedPatientType, selectedStageKey]);

  useEffect(() => {
    if (!assessment || !isFinalAssessmentStage({ stageKeys: [selectedStageKey] })) return undefined;
    const localPayload = parseAssessmentPayloadObject(assessment.finalStateAssessment);
    const hydrateFinalAssessment = (payload = {}) => {
      const documents = uploadedDocumentsFromRecord(payload);
      setStateAssessmentPages(renumberReadyPages(uploadedAssessmentPagesForState(documents)));
      loadedAssessmentDocumentKeyRef.current = `final:${assessmentDocumentStateKey(documents)}`;
      setFinalAssessmentApproval({
        approvalStatus: payload.approvalStatus || payload.status || "Draft",
        approvedBy: payload.approvedBy || "",
        approvedAt: payload.approvedAt || "",
        stateConfirmationReference: payload.stateConfirmationReference || payload.confirmationReference || "",
        approvalNotes: payload.approvalNotes || payload.notes || "",
      });
      setUploadError("");
    };
    if (assessmentResourceType !== "assessmentProcess") {
      hydrateFinalAssessment(localPayload);
      return undefined;
    }
    let isMounted = true;
    const processAssessmentId = processId(assessment) || assessmentId;
    careService.assessmentRecords
      .byProcessAndType(processAssessmentId, "final-assessment")
      .then((record) => {
        if (!isMounted) return;
        hydrateFinalAssessment({
          ...localPayload,
          ...parseAssessmentPayloadObject(record?.payload || record),
        });
      })
      .catch((err) => {
        if (!isMounted) return;
        hydrateFinalAssessment(localPayload);
        setUploadError(err?.response?.status === 404 ? "" : (err?.response?.data?.message || err?.message || "Unable to load final assessment details."));
      });
    return () => {
      isMounted = false;
    };
  }, [assessment, assessmentId, assessmentResourceType, selectedStageKey]);

  useEffect(() => () => {
    stateAssessmentPageUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  useEffect(() => {
    if (!uploadSuccess) return undefined;
    const timer = setTimeout(() => setUploadSuccess(""), 3500);
    return () => clearTimeout(timer);
  }, [uploadSuccess]);

  useEffect(() => {
    if (!stateAssessmentBioSuccess) return undefined;
    const timer = setTimeout(() => setStateAssessmentBioSuccess(""), 3500);
    return () => clearTimeout(timer);
  }, [stateAssessmentBioSuccess]);

  useEffect(() => {
    if (!finalAssessmentSaveSuccess) return undefined;
    const timer = setTimeout(() => setFinalAssessmentSaveSuccess(""), 3500);
    return () => clearTimeout(timer);
  }, [finalAssessmentSaveSuccess]);

  useEffect(() => {
    if (!meetAndGreetSuccess) return undefined;
    const timer = setTimeout(() => setMeetAndGreetSuccess(""), 3500);
    return () => clearTimeout(timer);
  }, [meetAndGreetSuccess]);

  useEffect(() => {
    if (!providerReviewSuccess) return undefined;
    const timer = setTimeout(() => setProviderReviewSuccess(""), 3500);
    return () => clearTimeout(timer);
  }, [providerReviewSuccess]);

  useEffect(() => {
    if (!preliminarySuccess) return undefined;
    const timer = setTimeout(() => setPreliminarySuccess(""), 3500);
    return () => clearTimeout(timer);
  }, [preliminarySuccess]);

  useEffect(() => {
    if (!assessment) return undefined;
    const stages = assessmentFlowStages.filter((stage) => stage.appliesTo.includes(hydratedPatientType));
    const fallbackStageKey =
      assessment?.workflowStage ||
      assessment?.currentStage ||
      assessment?.stage ||
      (normalizeStageKey(assessment?.type).includes("NEW_RESIDENT_ASSESSMENT") ? "STATE_INITIAL_ASSESSMENT" : "") ||
      (normalizeStageKey(assessment?.type).includes("ANNUAL_ASSESSMENT") ? "YEARLY_STATE_ASSESSMENT" : "");
    const fallbackStage = stages.find((stage) => stage.stageKeys.some((key) => normalizeStageKey(key) === normalizeStageKey(fallbackStageKey)));
    const fallbackStageKeyNormalized = normalizeStageKey(fallbackStage?.stageKeys?.[0]);
    const fallbackStageIndex = fallbackStage ? stages.indexOf(fallbackStage) : -1;
    const workflowIsCompleted = workflowStatusValueFor(assessment) === "COMPLETED";
    const stageIsReached = (stage) => {
      const stageIndex = stages.indexOf(stage);
      if (workflowIsCompleted) return true;
      if (fallbackStageIndex < 0) return false;
      return stageIndex <= fallbackStageIndex;
    };
    const reachedStages = stages.filter(stageIsReached);
    const stageKeyIsReached = (stageKey = "") =>
      reachedStages.some((stage) => stage.stageKeys.some((key) => normalizeStageKey(key) === normalizeStageKey(stageKey)));
    const selectedStageStillValid = Boolean(
      selectedStageKey &&
      stageKeyIsReached(selectedStageKey),
    );
    const resolveStageKey = () =>
      (stageKeyIsReached(assessmentStageKeyFromHash(window.location.hash, stages, hydratedPatientType))
        ? assessmentStageKeyFromHash(window.location.hash, stages, hydratedPatientType)
        : "") ||
      (selectedStageStillValid ? selectedStageKey : "") ||
      (stageKeyIsReached(fallbackStageKeyNormalized) ? fallbackStageKeyNormalized : "");
    const nextStageKey = resolveStageKey();
    if (nextStageKey !== selectedStageKey) {
      setSelectedStageKey(nextStageKey);
    }
    const onHashChange = () => {
      const hashStageKey = assessmentStageKeyFromHash(window.location.hash, stages, hydratedPatientType);
      if (hashStageKey && stageKeyIsReached(hashStageKey)) {
        setSelectedStageKey(hashStageKey);
      }
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [assessment, hydratedPatientType, selectedStageKey]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (
      params.get("addDailyNote") !== "true" ||
      addDailyNoteRequestHandledRef.current ||
      !assessment ||
      !isProbationNotesStage({ stageKeys: [selectedStageKey] })
    ) return;

    addDailyNoteRequestHandledRef.current = true;
    if (!canManageDailyNotes) {
      setProbationError("Only caregivers, care owners, and admins can add daily notes.");
    } else {
      const now = new Date();
      setDailyNoteEditingNote(null);
      setDailyNoteDate(dateInputValue(now));
      setDailyNoteApproximateTime(timeInputValue(now));
      setDailyNoteEndTime(timeInputValue(shiftMinutes(now, 15)));
      setDailyNoteCategory("Behavior");
      setDailyNoteOtherCategory("");
      setDailyNoteSelectionMode("SELECT");
      setDailyNoteSelectionQuery("");
      setShowAllDailyNoteSelections(false);
      setDailyNoteQuickSelections([]);
      setDailyNoteRichText("");
      setDailyNoteInterventionAction("");
      setDailyNotePhotos([]);
      setShowDailyNoteResidentDob(false);
      setDailyNoteDrawerOpen(true);
    }
    params.delete("addDailyNote");
    const query = params.toString();
    window.history.replaceState(null, "", `${location.pathname}${query ? `?${query}` : ""}${location.hash}`);
  }, [assessment, canManageDailyNotes, location.hash, location.pathname, location.search, selectedStageKey]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editNoteId = params.get("editDailyNote") || "";
    if (
      !editNoteId ||
      editDailyNoteRequestHandledRef.current === editNoteId ||
      !assessment ||
      !isProbationNotesStage({ stageKeys: [selectedStageKey] }) ||
      !Array.isArray(probationRecord?.dailyNotes)
    ) return;

    editDailyNoteRequestHandledRef.current = editNoteId;
    const note = probationRecord.dailyNotes.find((item) => String(item?.id) === editNoteId);
    if (!canManageDailyNotes) {
      setProbationError("Only caregivers, care owners, and admins can edit daily notes.");
    } else if (!note) {
      setProbationError("The selected daily note could not be found.");
    } else {
      const knownCategory = dailyNoteCategories.includes(note.category);
      const rangeStart = note.startTimestamp || note.rangeStartTimestamp || note.startedAt || note.happenedAt || note.occurredAt || note.date || new Date();
      const rangeEnd = note.endTimestamp || note.rangeEndTimestamp || note.endedAt || shiftMinutes(new Date(rangeStart), 15);
      setDailyNoteEditingNote(note);
      setDailyNoteDate(dateInputValue(note.noteDate || rangeStart));
      setDailyNoteApproximateTime(note.startTime || timeInputValue(rangeStart));
      setDailyNoteEndTime(note.endTime || timeInputValue(rangeEnd));
      setDailyNoteCategory(knownCategory ? note.category : "Other");
      setDailyNoteOtherCategory(knownCategory ? "" : note.category || "");
      setDailyNoteSelectionMode("SELECT");
      setDailyNoteSelectionQuery("");
      setShowAllDailyNoteSelections(false);
      setDailyNoteQuickSelections(normalizeDailyNoteQuickSelections(note.quickSelections).slice(0, 1));
      setDailyNoteRichText(note.notes || `<p>${note.summary || ""}</p>`);
      setDailyNoteInterventionAction(note.interventionAction || note.intervention || note.actionTaken || "");
      setDailyNotePhotos(note.photos || []);
      setShowDailyNoteResidentDob(false);
      setSelectedDailyNote(null);
      setDailyNoteDrawerOpen(true);
    }

    params.delete("editDailyNote");
    const query = params.toString();
    window.history.replaceState(null, "", `${location.pathname}${query ? `?${query}` : ""}${location.hash}`);
  }, [assessment, canManageDailyNotes, location.hash, location.pathname, location.search, probationRecord, selectedStageKey]);

  const extractedAssessmentId = assessmentResourceType === "residentAssessment"
    ? assessment?.id || assessmentId
    : assessment?.stateAssessmentId || assessment?.stageReferences?.initialStateAssessmentId || "";

  const loadSavedExtractedDocument = useCallback(async () => {
    try {
      let savedDocument = null;
      if (assessmentResourceType === "assessmentProcess") {
        const initialPayload = parseAssessmentPayloadObject(assessment?.initialStateAssessment || assessment?.stateAssessment);
        const initialExtraction = assessment?.assessmentExtraction || initialPayload.assessmentExtraction || null;
        const recordPayload = parseAssessmentPayloadObject(initialAssessmentRecord?.payload);
        savedDocument = recordPayload.assessmentExtraction || initialExtraction;
      } else if (extractedAssessmentId) {
        savedDocument = await careService.assessmentExtractions.getExtractedDocument(extractedAssessmentId);
      }
      setExtractedDocument(savedDocument || null);
      const needsInitialBioData =
        isStateAssessmentStage({ stageKeys: [selectedStageKey] }) ||
        isOnboardingStage({ stageKeys: [selectedStageKey] });
      if (savedDocument && needsInitialBioData) {
        const fields = extractionFieldsFromDocument(savedDocument);
        const extractionKey = JSON.stringify({
          id: assessment?.id || assessmentId,
          stage: selectedStageKey,
          fields: fields.map((field) => [field.sectionKey, field.label, field.value]).slice(0, 120),
        });
        if (appliedExtractionPrefillKeyRef.current !== extractionKey) {
          appliedExtractionPrefillKeyRef.current = extractionKey;
          const bioPatch = stateAssessmentBioPatchFromExtraction(savedDocument);
          const extractedContacts = extractionContactsForStateAssessment(savedDocument);
          setStateAssessmentBioForm((current) => mergeExtractionBioPrefill(current, bioPatch));
          if (extractedContacts.length) {
            setStateAssessmentContacts((current) => {
              return mergeStateAssessmentContacts([...current, ...extractedContacts]);
            });
          }
        }
      }
    } catch {
      setExtractedDocument(null);
    }
  }, [assessment, assessmentId, assessmentResourceType, extractedAssessmentId, initialAssessmentRecord, selectedStageKey]);

  useEffect(() => {
    const needsExtractedDocument = Boolean(
      selectedStageKey && (
        isStateAssessmentStage({ stageKeys: [selectedStageKey] }) ||
        isOnboardingStage({ stageKeys: [selectedStageKey] }) ||
        selectedStageKey === "PRELIMINARY_CARE_PLAN"
      ),
    );
    const recordReady = assessmentResourceType !== "assessmentProcess" || initialAssessmentRecord !== undefined;
    if (needsExtractedDocument && recordReady) loadSavedExtractedDocument();
  }, [assessmentResourceType, initialAssessmentRecord, loadSavedExtractedDocument, selectedStageKey]);

  const facilitatorOptions = useMemo(() => {
    const seen = new Set();
    const options = workspaceMemberUsers
      .map((member) => {
        const name = getWorkspaceMemberDisplayName(member);
        const email = getWorkspaceMemberEmail(member);
        return {
          value: name,
          userId: member.id || "",
          label: email ? `${name} (${email})` : name,
        };
      })
      .filter((option) => {
        const key = option.value.trim().toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    const currentFacilitator = String(meetAndGreetForm.facilitator || "").trim();
    if (currentFacilitator && !seen.has(currentFacilitator.toLowerCase())) {
      options.unshift({ value: currentFacilitator, userId: meetAndGreetForm.facilitatorUserId || "", label: currentFacilitator });
    }
    return options;
  }, [meetAndGreetForm.facilitator, meetAndGreetForm.facilitatorUserId, workspaceMemberUsers]);
  const workspaceStaffOptions = useMemo(() => {
    const seen = new Set();
    return workspaceMemberUsers
      .map((member, index) => {
        const name = getWorkspaceMemberDisplayName(member);
        const email = getWorkspaceMemberEmail(member);
        const value = member.id || email || meetAndGreetPersonValue({ name }, index, "workspace-member");
        return {
          value,
          name,
          relationship: "Facility Staff",
          label: email ? `${name} (${email})` : name,
        };
      })
      .filter((option) => {
        const key = option.value.trim().toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [workspaceMemberUsers]);
  const collateralContactOptions = useMemo(() => {
    const seen = new Set();
    const contacts = [
      ...stateAssessmentContacts,
      ...residentContacts.map(normalizeStateAssessmentContact),
    ];
    return contacts
      .map((contact, index) => {
        const name = contactFullName(contact);
        const relationship = contact.relationship === "Other"
          ? contact.relationshipOther || "Other"
          : contactRelationship(contact) || contact.relationship || "";
        const value = contact.id || meetAndGreetPersonValue({ name, relationship }, index, "collateral-contact");
        return {
          value,
          name,
          relationship,
          label: relationship ? `${name} - ${relationship}` : name,
        };
      })
      .filter((option) => {
        const key = `${option.name}|${option.relationship}`.trim().toLowerCase();
        if (!option.name || option.name === "Not set" || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [residentContacts, stateAssessmentContacts]);

  const probationRecordTypeValue = hydratedPatientType === "RESIDENT" ? "annual-residency-progress" : "resident-30-day-probation";
  const weeklyReportsProcessId = assessment
    ? (assessmentResourceType === "assessmentProcess" ? processId(assessment) || assessmentId : "") ||
      assessment?.assessmentProcessId ||
      assessment?.processId ||
      residentOnboardingPreview?.assessmentProcessId ||
      residentOnboardingPreview?.previewPayload?.assessmentProcessId ||
      residentOnboardingPreview?.previewPayload?.processId ||
      ""
    : "";
  const weeklyReportsForNcp = useMemo(() => {
    const reports = [
      ...(weeklyDailyNoteReport ? [weeklyDailyNoteReport] : []),
      ...savedWeeklyDailyNoteReports,
    ];
    const seen = new Set();
    return reports.filter((report, index) => {
      const key = report?.id || report?.reportId || `${report?.weekStart || ""}-${report?.weekEnd || ""}-${index}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [savedWeeklyDailyNoteReports, weeklyDailyNoteReport]);
  const behaviorTierAnalysis = useMemo(
    () => behaviorTierAnalysisForNotes(probationRecord?.dailyNotes || []),
    [probationRecord?.dailyNotes],
  );

  useEffect(() => {
    const needsWeeklyReports = Boolean(
      selectedStageKey && (
        isProbationNotesStage({ stageKeys: [selectedStageKey] }) ||
        isNegotiatedCarePlanStage({ stageKeys: [selectedStageKey] })
      ),
    );
    if (!weeklyReportsProcessId || !needsWeeklyReports) {
      setSavedWeeklyDailyNoteReports([]);
      return undefined;
    }
    let isMounted = true;
    careService.dailyNotes
      .listWeeklyReports({ assessmentProcessId: weeklyReportsProcessId, recordType: probationRecordTypeValue })
      .then((reports) => {
        if (isMounted) setSavedWeeklyDailyNoteReports(Array.isArray(reports) ? reports : []);
      })
      .catch(() => {
        if (isMounted) setSavedWeeklyDailyNoteReports([]);
      });
    return () => {
      isMounted = false;
    };
  }, [probationRecordTypeValue, selectedStageKey, weeklyReportsProcessId]);

  if (loading) {
    return (
      <CareShell className="dashboard-content residents-page assessments-page">
        <Panel title="Resident Care Process">Loading resident care process...</Panel>
      </CareShell>
    );
  }

  if (error) {
    return (
      <CareShell className="dashboard-content residents-page assessments-page">
        <div className="residents-actions">
          <Link className="mini-btn" to="/assessments"><i className="ti ti-arrow-left" /> Care Processes</Link>
        </div>
        <p className="form-error">{error}</p>
      </CareShell>
    );
  }

  if (!assessment && !error) return <Navigate to="/assessments" replace />;
  const assessmentProcessId = processId(assessment) || assessmentId;
  const linkedAssessmentProcessId =
    (assessmentResourceType === "assessmentProcess" ? assessmentProcessId : "") ||
    assessment?.assessmentProcessId ||
    assessment?.processId ||
    residentOnboardingPreview?.assessmentProcessId ||
    residentOnboardingPreview?.previewPayload?.assessmentProcessId ||
    residentOnboardingPreview?.previewPayload?.processId ||
    "";
  const residentId = processResidentId(assessment);
  const providerDecisionKey = normalizeStageKey(assessment?.providerDecision);
  const residentLink = residentId ? `/residents/${residentId}#assessment` : "/residents";
  const patientType = hydratedPatientType;
  const flowStages = assessmentFlowStages.filter((stage) => stage.appliesTo.includes(patientType));
  const currentStageKey =
    assessment?.workflowStage ||
    assessment?.currentStage ||
    assessment?.stage ||
    (normalizeStageKey(assessment?.type).includes("NEW_RESIDENT_ASSESSMENT") ? "STATE_INITIAL_ASSESSMENT" : "") ||
    (normalizeStageKey(assessment?.type).includes("ANNUAL_ASSESSMENT") ? "YEARLY_STATE_ASSESSMENT" : "");
  const currentStageIndex = stageIndexFor(flowStages, currentStageKey);
  const currentStage = currentStageIndex > -1 ? flowStages[currentStageIndex] : null;
  const assessmentWorkflowStatus = workflowStatusValueFor(assessment);
  const assessmentIsCompleted = assessmentWorkflowStatus === "COMPLETED";
  const blockCompletedAssessmentInteraction = (event) => {
    if (!assessmentIsCompleted || event.target.closest("[data-readonly-allowed='true']")) return;
    const interactiveTarget = event.target.closest("button, input, select, textarea, label, [contenteditable='true']");
    if (!interactiveTarget && event.type !== "submit" && event.type !== "beforeinput") return;
    event.preventDefault();
    event.stopPropagation();
  };
  const displayStages = flowStages.map((stage, index) => {
    const status = assessmentIsCompleted && currentStageIndex > -1 && index <= currentStageIndex
      ? "Complete"
      : currentStageIndex < 0 ? "Pending" : index < currentStageIndex ? "Complete" : index === currentStageIndex ? "Current" : "Pending";
    const isReached = status !== "Pending";
    return {
      ...stage,
      status,
      isReached,
      displayLabel: patientType === "RESIDENT" && stage.existingLabel ? stage.existingLabel : stage.label,
      displayDescription: patientType === "RESIDENT" && stage.existingDescription ? stage.existingDescription : stage.description,
    };
  });
  const selectedStage = displayStages.find((stage) => stage.isReached && stage.stageKeys.some((key) => normalizeStageKey(key) === selectedStageKey));
  const showStateAssessmentUpload = selectedStage ? isStateAssessmentStage(selectedStage) : false;
  const showMeetAndGreetForm = selectedStage ? selectedStage.stageKeys.some((key) => normalizeStageKey(key) === "MEET_AND_GREET") : false;
  const showProviderReview = selectedStage ? isProviderReviewStage(selectedStage) : false;
  const showOnboardingStage = selectedStage ? isOnboardingStage(selectedStage) : false;
  const showPreliminaryPlan = selectedStage ? isPreliminaryPlanStage(selectedStage) : false;
  const showProbationNotes = selectedStage ? isProbationNotesStage(selectedStage) : false;
  const showNegotiatedCarePlan = selectedStage ? isNegotiatedCarePlanStage(selectedStage) : false;
  const showFinalAssessmentUpload = selectedStage ? isFinalAssessmentStage(selectedStage) : false;
  const socialWorkerDetails = socialWorkerDetailsFor(assessment, residentContacts);
  const reviewAssessmentPages = [...stateAssessmentPages].sort((a, b) => Number(a.pageNumber || 0) - Number(b.pageNumber || 0));
  const reviewCareCircle = meetAndGreetPersonPayload(meetAndGreetCareCircle);
  const reviewProviderStaff = meetAndGreetPersonPayload(meetAndGreetStaff);
  const reviewQuestions = [...meetAndGreetQuestions, ...meetAndGreetCustomQuestions]
    .map((question) => ({ question, response: meetAndGreetResponses[question] || "" }))
    .filter((item) => item.response || meetAndGreetQuestions.includes(item.question));
  const residentDisplayName = [
    stateAssessmentBioForm.firstName,
    stateAssessmentBioForm.middleName,
    stateAssessmentBioForm.lastName,
  ].filter(Boolean).join(" ") ||
    processResidentName(assessment);
  const residentDateOfBirth = normalizeDateInputValue(
    stateAssessmentBioForm.dateOfBirth ||
    assessment?.residentDateOfBirth ||
    assessment?.residentBirthDate ||
    assessment?.resident?.dateOfBirth ||
    assessment?.resident?.birthDate ||
    assessment?.resident?.dob ||
    assessment?.dateOfBirth ||
    assessment?.birthDate ||
    assessment?.dob,
  );
  const probationNotes = (probationRecord?.dailyNotes || [])
    .filter((note) => isWithinDateRange(note.noteDate || note.date, probationNoteRange.start, probationNoteRange.end))
    .sort((a, b) => new Date(b.date || b.noteDate).getTime() - new Date(a.date || a.noteDate).getTime());
  const weeklyReportRangeNotes = (probationRecord?.dailyNotes || [])
    .filter((note) => isWithinDateRange(note.noteDate || note.date, weeklyReportRange.start, weeklyReportRange.end));
  const visibleProbationNotes = probationNotes.slice(0, visibleProbationNoteCount);
  const weeklyReportTitle = weeklyDailyNoteReport ? weeklyReportTitleFor(weeklyDailyNoteReport) : "";
  const weeklyReportRangeLabel = weeklyDailyNoteReport
    ? `${formatDate(weeklyDailyNoteReport.weekStart)} - ${formatDate(weeklyDailyNoteReport.weekEnd)}`
    : "";
  const weeklyReportParagraph = weeklyDailyNoteReport ? weeklyReportParagraphFor(weeklyDailyNoteReport) : "";
  const weeklyReportLists = weeklyDailyNoteReport ? weeklyReportSectionsFor(weeklyDailyNoteReport) : [];
  const probationCalendarDays = calendarDaysForMonth(
    probationCalendarMonth,
    [...(probationRecord?.dailyNotes || [])].sort((a, b) => new Date(a.date || a.noteDate).getTime() - new Date(b.date || b.noteDate).getTime()),
  );
  const assignedProbationStaff = (probationRecord?.assignedStaff || [])
    .map((staff, originalIndex) => ({ ...staff, originalIndex }))
    .sort((a, b) => new Date(b.to || 0).getTime() - new Date(a.to || 0).getTime());
  const probationStartDateDisplay = probationRecord?.startDate || assessment.probationStartDate || assessment.processStartDate;
  const probationEndDateDisplay =
    probationRecord?.endDate ||
    assessment.probationEndDate ||
    assessment.processEndDate ||
    oneYearEstimatedEndDate(probationStartDateDisplay);
  const probationCreatedAtDisplay =
    probationRecord?.createdAt ||
    probationRecord?.dateCreated ||
    assessment.createdAt ||
    assessment.processCreatedAt ||
    assessment.startDate ||
    assessment.processStartDate;
  const workspaceOrganizationName = firstText(
    selectedWorkspace?.organization?.name,
    selectedWorkspace?.organizationName,
    selectedWorkspace?.businessName,
    assessment?.organization?.name,
    assessment?.organizationName,
    assessment?.workspaceOrganizationName,
    selectedWorkspace?.name,
    "Oliveeb Care",
  );
  const printDailyNotesReport = () => {
    if (typeof window === "undefined") return;
    const printedAt = new Date().toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
    const reportRowsHtml = probationNotes.length
      ? probationNotes.map((note) => {
        const selections = normalizeDailyNoteQuickSelections(note.quickSelections);
        const interventionHtml = note.interventionAction || note.intervention || note.actionTaken || "";
        const attachmentCount = Array.isArray(note.photos) ? note.photos.length : 0;
        const occurrenceText = selections.length ? selections.map(dailyNoteQuickSelectionText).join("; ") : "N/A";
        return `
          <tr>
            <td>
              <strong>${escapeHtml(formatDate(note.noteDate || dateInputValue(note.date)))}</strong>
              <small>${escapeHtml(dailyNoteTimeRangeLabel(note))}</small>
            </td>
            <td>${escapeHtml(note.category || "Not set")}</td>
            <td>${escapeHtml(occurrenceText)}</td>
            <td>
              <strong>${escapeHtml(note.title || "Daily note")}</strong>
              <p>${escapeHtml(stripHtmlText(note.notes || note.summary || "") || "No note summary added.")}</p>
              ${attachmentCount ? `<small>${attachmentCount} photo${attachmentCount === 1 ? "" : "s"}</small>` : ""}
            </td>
            <td>${escapeHtml(stripHtmlText(interventionHtml) || "N/A")}</td>
            <td>${escapeHtml(dailyNoteReporterName(note))}</td>
          </tr>
        `;
      }).join("")
      : `<tr><td colspan="6">No daily notes found for the selected report range.</td></tr>`;
    const printWindow = window.open("", "_blank", "width=1100,height=760");
    if (!printWindow) {
      setProbationError("Allow pop-ups to print the daily notes report.");
      return;
    }
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Daily Notes Report - ${escapeHtml(residentDisplayName)}</title>
          <style>
            @page { size: landscape; margin: 0.35in; }
            * { box-sizing: border-box; }
            body { color: #111827; font-family: Arial, sans-serif; font-size: 9pt; line-height: 1.32; margin: 0; }
            header { align-items: flex-start; border-bottom: 3px solid #123b16; display: flex; gap: 16px; justify-content: space-between; margin-bottom: 12px; padding-bottom: 10px; }
            header span { color: #1baa39; display: block; font-size: 9pt; font-weight: 800; margin-bottom: 2px; text-transform: uppercase; }
            h1 { color: #123b16; font-size: 22pt; margin: 0 0 4px; }
            header p { color: #374151; margin: 0; }
            header aside { display: grid; gap: 2px; text-align: right; }
            .meta { border: 1px solid #cfd8d0; display: grid; grid-template-columns: 1.2fr 0.9fr 1fr 0.55fr; margin-bottom: 12px; }
            .meta span { border-right: 1px solid #cfd8d0; color: #374151; padding: 6px 8px; }
            .meta span:last-child { border-right: 0; }
            table { border-collapse: collapse; table-layout: fixed; width: 100%; }
            th, td { border: 1px solid #cfd8d0; padding: 6px 7px; text-align: left; vertical-align: top; }
            th { background: #edf7ef; color: #123b16; font-size: 8pt; font-weight: 900; text-transform: uppercase; }
            td strong, td small { display: block; }
            td strong { color: #111827; font-size: 9pt; }
            td small { color: #4b5563; font-size: 7.8pt; margin-top: 2px; }
            td p { margin: 3px 0 0; white-space: pre-wrap; }
            footer { display: grid; gap: 18px; grid-template-columns: 1.2fr 1.2fr 0.7fr; margin-top: 26px; }
            footer span { border-top: 1px solid #111827; color: #374151; font-size: 8.5pt; padding-top: 5px; }
          </style>
        </head>
        <body>
          <header>
            <div>
              <span>${escapeHtml(workspaceOrganizationName)}</span>
              <h1>Daily Notes Report</h1>
              <p>Assessment behavioral notes, observations, interventions, and supporting photo counts.</p>
            </div>
            <aside>
              <span>Printed</span>
              <strong>${escapeHtml(printedAt)}</strong>
            </aside>
          </header>
          <section class="meta">
            <span>Resident: <strong>${escapeHtml(residentDisplayName)}</strong></span>
            <span>Date of birth: <strong>${escapeHtml(residentDateOfBirth ? formatDate(residentDateOfBirth) : "Not added")}</strong></span>
            <span>Range: <strong>${escapeHtml(probationNoteRange.start || "Any")} - ${escapeHtml(probationNoteRange.end || "Any")}</strong></span>
            <span>Total notes: <strong>${probationNotes.length}</strong></span>
          </section>
          <table>
            <colgroup>
              <col style="width: 14%;" />
              <col style="width: 14%;" />
              <col style="width: 18%;" />
              <col style="width: 27%;" />
              <col style="width: 17%;" />
              <col style="width: 10%;" />
            </colgroup>
            <thead>
              <tr>
                <th>Date Time</th>
                <th>Observation Category</th>
                <th>Occurance</th>
                <th>Observation Description</th>
                <th>Action Taken</th>
                <th>Reported By</th>
              </tr>
            </thead>
            <tbody>${reportRowsHtml}</tbody>
          </table>
          <footer>
            <span>Resident / Representative Signature</span>
            <span>Provider Signature</span>
            <span>Date</span>
          </footer>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.setTimeout(() => printWindow.print(), 100);
  };
  const printBehaviorAnalysisReport = () => {
    if (typeof window === "undefined") return;
    const printedAt = new Date().toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
    const tierScoreRowsHtml = behaviorTierAnalysis.tierScores.map((tierScore) => {
      const standard = cbhsTierStandards.find((item) => item.tier === tierScore.tier);
      const indicatorRows = tierScore.indicators.map((indicator) => `
        <li class="${indicator.matched ? "matched" : ""}">
          <span>${indicator.matched ? "Met" : "Not met"}</span>
          ${escapeHtml(indicator.label)}
        </li>
      `).join("");
      return `
        <article class="${behaviorTierAnalysis.tier === tierScore.tier ? "selected" : ""}">
          <header>
            <div>
              <strong>Tier ${tierScore.tier}</strong>
              <small>${escapeHtml(standard?.hours || "")}</small>
            </div>
            <b>${tierScore.score}%</b>
          </header>
          <ul>${indicatorRows}</ul>
        </article>
      `;
    }).join("");
    const categorySummaryHtml = behaviorTierAnalysis.topCategories.length
      ? behaviorTierAnalysis.topCategories.map((item) => `<span>${escapeHtml(item.label)} <strong>${item.count}</strong></span>`).join("")
      : "<span>No category summary available</span>";
    const behaviorSummaryHtml = behaviorTierAnalysis.topBehaviorSelections.length
      ? behaviorTierAnalysis.topBehaviorSelections.map((item) => `<span>${escapeHtml(item.label)} <strong>${item.count}</strong></span>`).join("")
      : "<span>No qualifying behavior selections found</span>";
    const allBehaviorSummaryHtml = behaviorTierAnalysis.topAllBehaviorSelections.length
      ? behaviorTierAnalysis.topAllBehaviorSelections.map((item) => `<span>${escapeHtml(item.label)} <strong>${item.count}</strong></span>`).join("")
      : "<span>No behavior selections found</span>";
    const printWindow = window.open("", "_blank", "width=1100,height=780");
    if (!printWindow) {
      setProbationError("Allow pop-ups to print the behavior analysis score report.");
      return;
    }
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Behavior Analysis Score - ${escapeHtml(residentDisplayName)}</title>
          <style>
            @page { size: landscape; margin: 0.35in; }
            * { box-sizing: border-box; }
            body { color: #111827; font-family: Arial, sans-serif; font-size: 9pt; line-height: 1.35; margin: 0; }
            header.report-head { align-items: flex-start; border-bottom: 3px solid #123b16; display: flex; gap: 16px; justify-content: space-between; margin-bottom: 12px; padding-bottom: 10px; }
            header.report-head span { color: #1baa39; display: block; font-size: 9pt; font-weight: 800; margin-bottom: 2px; text-transform: uppercase; }
            h1 { color: #123b16; font-size: 22pt; margin: 0 0 4px; }
            h2 { color: #123b16; font-size: 12pt; margin: 16px 0 8px; }
            p { margin: 0; }
            header.report-head p { color: #374151; }
            header.report-head aside { display: grid; gap: 2px; text-align: right; }
            .meta, .summary { border: 1px solid #cfd8d0; display: grid; margin-bottom: 12px; }
            .meta { grid-template-columns: 1.25fr 0.8fr 0.85fr 0.55fr; }
            .summary { grid-template-columns: repeat(4, 1fr); }
            .meta span, .summary span { border-right: 1px solid #cfd8d0; color: #374151; padding: 7px 8px; }
            .meta span:last-child, .summary span:last-child { border-right: 0; }
            .meta strong, .summary strong { color: #123b16; display: block; font-size: 11pt; margin-top: 2px; }
            .pill-summary { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
            .pill-summary span { background: #eef7ef; border: 1px solid #cfd8d0; border-radius: 999px; color: #123b16; font-weight: 800; padding: 4px 8px; }
            .pill-summary strong { color: #2385db; margin-left: 4px; }
            .tier-grid { display: grid; gap: 8px; grid-template-columns: repeat(4, 1fr); }
            article { border: 1px solid #cfd8d0; border-radius: 6px; padding: 8px; break-inside: avoid; }
            article.selected { background: #eef7ef; border-color: #1baa39; }
            article header { align-items: flex-start; display: flex; justify-content: space-between; margin-bottom: 6px; }
            article strong { color: #123b16; display: block; font-size: 12pt; }
            article small { color: #4b5563; font-weight: 700; }
            article b { color: #2385db; font-size: 13pt; }
            ul { display: grid; gap: 4px; list-style: none; margin: 0; padding: 0; }
            li { color: #4b5563; font-size: 8.4pt; }
            li span { border: 1px solid #cfd8d0; border-radius: 999px; color: #6b7280; display: inline-block; font-size: 7pt; font-weight: 800; margin-right: 4px; min-width: 44px; padding: 1px 4px; text-align: center; text-transform: uppercase; }
            li.matched { color: #123b16; font-weight: 800; }
            li.matched span { background: #e8f7ed; border-color: #1baa39; color: #0d7b28; }
            footer { color: #4b5563; font-size: 8pt; margin-top: 18px; }
          </style>
        </head>
        <body>
          <header class="report-head">
            <div>
              <span>${escapeHtml(workspaceOrganizationName)}</span>
              <h1>Behavior Analysis Score</h1>
              <p>CBHS tier evidence based on daily notes and tier-specific documentation indicators.</p>
            </div>
            <aside>
              <span>Printed</span>
              <strong>${escapeHtml(printedAt)}</strong>
            </aside>
          </header>
          <section class="meta">
            <span>Resident: <strong>${escapeHtml(residentDisplayName)}</strong></span>
            <span>Date of birth: <strong>${escapeHtml(residentDateOfBirth ? formatDate(residentDateOfBirth) : "Not added")}</strong></span>
            <span>Suggested tier: <strong>${escapeHtml(behaviorTierAnalysis.tier ? `Tier ${behaviorTierAnalysis.tier}` : "Not enough evidence")}</strong></span>
            <span>Score: <strong>${behaviorTierAnalysis.score}/100</strong></span>
          </section>
          <section class="summary">
            <span>Confidence <strong>${escapeHtml(behaviorTierAnalysis.confidence)}</strong></span>
            <span>Behavior notes <strong>${behaviorTierAnalysis.noteCount}</strong></span>
            <span>Days documented <strong>${behaviorTierAnalysis.dayCount}</strong></span>
            <span>Support hours/day <strong>${behaviorTierAnalysis.documentedSupportHours.toFixed(1)}</strong></span>
          </section>
          <section class="summary">
            <span>Qualifying behavior types <strong>${behaviorTierAnalysis.qualifyingBehaviorCount}</strong></span>
            <span>One-to-one support matches <strong>${behaviorTierAnalysis.oneToOneHits}</strong></span>
            <span>Multiple-staff matches <strong>${behaviorTierAnalysis.multipleStaffHits}</strong></span>
            <span>Imminent-risk matches <strong>${behaviorTierAnalysis.imminentRiskHits}</strong></span>
          </section>
          <h2>Evidence Summary</h2>
          <p class="pill-summary">${categorySummaryHtml}</p>
          <h2>Behavior Selections Captured</h2>
          <p class="pill-summary">${allBehaviorSummaryHtml}</p>
          <h2>Qualifying CBHS Selections</h2>
          <p class="pill-summary">${behaviorSummaryHtml}</p>
          ${behaviorTierAnalysis.tierStandard ? `
            <h2>Suggested Tier Standard</h2>
            <article class="selected">
              <strong>${escapeHtml(behaviorTierAnalysis.tierStandard.label)}</strong>
              <p>${escapeHtml(behaviorTierAnalysis.tierStandard.language)}</p>
            </article>
          ` : ""}
          <h2>Tier-Specific Documentation Indicator Scores</h2>
          <section class="tier-grid">${tierScoreRowsHtml}</section>
          <footer>This report is decision support only. Detailed source observations are available in the interactive behavior analysis audit view.</footer>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.setTimeout(() => printWindow.print(), 100);
  };
  const resolveLinkedAssessmentProcessId = async () => {
    if (assessmentResourceType === "assessmentProcess" && assessmentProcessId) return assessmentProcessId;
    if (linkedAssessmentProcessId) return linkedAssessmentProcessId;
    const residentIdForPreview = processResidentId(assessment);
    if (!residentIdForPreview) return "";
    const preview = await careService.residents.getOnboardingPreview(residentIdForPreview);
    setResidentOnboardingPreview(preview || null);
    return (
      preview?.assessmentProcessId ||
      preview?.previewPayload?.assessmentProcessId ||
      ""
    );
  };
  const markStageCompleted = (stage = selectedStage) => {
    if (!canCompleteAssessmentStages) {
      setStageCompletionMessage("Only care owners and admins can mark assessment stages as completed.");
      return;
    }
    if (!stage) return;
    if (isFinalAssessmentStage(stage)) {
      markFinalAssessmentCompleted();
      return;
    }
    const stageKey = normalizeStageKey(stage.stageKeys?.[0]);
    const stageLabel = stage.displayLabel || stage.label || "stage";
    const stageIndex = displayStageIndexFor(displayStages, stage);
    const defaultNextStage = displayStages[stageIndex + 1];
    const nextStage = defaultNextStage;
    Modal.confirm({
      title: `Mark ${stageLabel} as completed?`,
      content: nextStage
        ? `This will move ${residentDisplayName} to ${nextStage.displayLabel}.`
        : `This will mark ${stageLabel} as completed for ${residentDisplayName}.`,
      okText: "Mark as Completed",
      cancelText: "Cancel",
      onOk: async () => {
        setStageCompletingKey(stageKey);
        setStageCompletionMessage("");
        try {
          const processRecordId = await resolveLinkedAssessmentProcessId();
          if (!processRecordId) {
            throw new Error("Stage completion is available after opening the linked resident care process.");
          }
          const targetStage = normalizeStageKey(nextStage?.stageKeys?.[0] || stage.stageKeys?.[0]);
          const targetScreen = stageBookmarkSlug(nextStage?.displayLabel || nextStage?.label || stageLabel);
          const saved = await careService.assessmentProcesses.update(
            processRecordId,
            assessmentProcessUpdateRequest(assessment, {
              stage: targetStage,
              currentStage: targetStage,
              workflowStage: targetStage,
              currentScreen: targetScreen,
              status: "OPEN",
              workflowStatus: "OPEN",
            }, patientType),
          );
          setAssessment({
            ...assessment,
            ...saved,
            stage: targetStage,
            currentStage: targetStage,
            workflowStage: targetStage,
            currentScreen: targetScreen,
            status: workflowStatusFor(saved?.status || "OPEN"),
          });
          setAssessmentResourceType("assessmentProcess");
          setSelectedStageKey(targetStage);
          if (nextStage) {
            setAssessmentStageHash(nextStage, patientType);
          }
          setStageCompletionMessage(`${stageLabel} marked as completed.`);
        } catch (err) {
          setStageCompletionMessage(err?.response?.data?.message || err?.message || "Unable to mark stage as completed.");
          throw err;
        } finally {
          setStageCompletingKey("");
        }
      },
    });
  };
  const changeAssessmentStatus = (nextStatus, label) => {
    if (!canCompleteAssessmentStages) {
      setStageCompletionMessage("Only care owners and admins can change assessment status.");
      return;
    }
    const normalizedStatus = workflowStatusFor(nextStatus);
    Modal.confirm({
      title: `${label} assessment?`,
      content: `${label} will change the assessment status to ${statusLabelFor(normalizedStatus)} for ${residentDisplayName}.`,
      okText: label,
      cancelText: "Keep Editing",
      centered: true,
      okButtonProps: { danger: ["CANCELED", "ARCHIVED", "DELETED"].includes(normalizedStatus) },
      onOk: async () => {
        setAssessmentStatusChanging(normalizedStatus);
        setStageCompletionMessage("");
        try {
          const processRecordId = await resolveLinkedAssessmentProcessId();
          const statusPayload = {
            status: normalizedStatus,
            workflowStatus: normalizedStatus,
          };
          let saved = null;
          if (processRecordId) {
            try {
              saved = await careService.assessmentProcesses.updateStatus(processRecordId, statusPayload);
            } catch (statusErr) {
              if (![404, 405].includes(statusErr?.response?.status)) throw statusErr;
              saved = await careService.assessmentProcesses.update(
                processRecordId,
                assessmentProcessUpdateRequest(assessment, statusPayload, patientType),
              );
            }
            setAssessmentResourceType("assessmentProcess");
          } else {
            throw new Error("Care process status can be changed after opening the linked resident care process.");
          }
          setAssessment({
            ...assessment,
            ...saved,
            status: workflowStatusFor(saved?.status || normalizedStatus),
            workflowStatus: workflowStatusFor(saved?.workflowStatus || saved?.status || normalizedStatus),
          });
          setStageCompletionMessage(`Assessment status changed to ${statusLabelFor(normalizedStatus)}.`);
        } catch (err) {
          setStageCompletionMessage(err?.response?.data?.message || err?.message || `Unable to ${label.toLowerCase()} assessment.`);
          throw err;
        } finally {
          setAssessmentStatusChanging("");
        }
      },
    });
  };
  const stageCompleteAction = (stage = selectedStage) => {
    if (!canCompleteAssessmentStages) return null;
    const stageKey = normalizeStageKey(stage?.stageKeys?.[0]);
    const assessmentClosed = closedWorkflowStatuses.includes(assessmentWorkflowStatus);
    const disabled = !stage || assessmentClosed || Boolean(stageCompletingKey) || Boolean(assessmentStatusChanging) || finalAssessmentCompleting;
    return (
      <div className="assessment-status-action-group">
        <button
          className="assessment-stage-complete-link success"
          disabled={disabled}
          onClick={() => markStageCompleted(stage)}
          type="button"
        >
          <i className={`ti ${stageCompletingKey === stageKey || (isFinalAssessmentStage(stage || {}) && finalAssessmentCompleting) ? "ti-loader-2" : "ti-circle-check"}`} />
          {stageCompletingKey === stageKey || (isFinalAssessmentStage(stage || {}) && finalAssessmentCompleting) ? "Completing..." : "Mark as Completed"}
        </button>
        <button
          className="assessment-stage-complete-link warning"
          disabled={disabled}
          onClick={() => changeAssessmentStatus("CANCELED", "Cancel")}
          type="button"
        >
          <i className={`ti ${assessmentStatusChanging === "CANCELED" ? "ti-loader-2" : "ti-ban"}`} />
          {assessmentStatusChanging === "CANCELED" ? "Canceling..." : "Cancel"}
        </button>
        <button
          className="assessment-stage-complete-link danger"
          disabled={disabled}
          onClick={() => changeAssessmentStatus("DELETED", "Delete")}
          type="button"
        >
          <i className={`ti ${assessmentStatusChanging === "DELETED" ? "ti-loader-2" : "ti-trash"}`} />
          {assessmentStatusChanging === "DELETED" ? "Deleting..." : "Delete"}
        </button>
      </div>
    );
  };
  const providerDecisionAction = () => {
    if (providerDecisionKey === "ACCEPTED") return null;
    const disabled = assessmentIsCompleted || Boolean(providerReviewSaving) || Boolean(stageCompletingKey) || finalAssessmentCompleting;
    return (
      <div className="assessment-drawer-action-links assessment-page-action-links provider-review-decision-actions">
        <button
          className="decline"
          type="button"
          disabled={disabled}
          onClick={() => confirmProviderDecision("DECLINED")}
        >
          <i className={`ti ${providerReviewSaving === "DECLINED" ? "ti-loader-2" : "ti-circle-x"}`} />
          {providerReviewSaving === "DECLINED" ? "Saving..." : "Decline Resident"}
        </button>
        <button
          className="success"
          type="button"
          disabled={disabled}
          onClick={() => confirmProviderDecision("ACCEPTED")}
        >
          <i className={`ti ${providerReviewSaving === "ACCEPTED" ? "ti-loader-2" : "ti-circle-check"}`} />
          {providerReviewSaving === "ACCEPTED" ? "Saving..." : "Accept Resident"}
        </button>
      </div>
    );
  };
  const updateMeetAndGreetForm = (field, value) => {
    setMeetAndGreetForm((current) => ({ ...current, [field]: value }));
  };
  const selectMeetAndGreetPerson = (setter, indexToUpdate, option, fallbackRelationship = "") => {
    setter((current) => current.map((item, index) => (
      index === indexToUpdate
        ? {
          ...item,
          sourceId: option?.value || "",
          name: option?.name || "",
          relationship: option?.relationship || fallbackRelationship,
          relationshipOther: "",
        }
        : item
    )));
  };
  const addMeetAndGreetPerson = (setter, defaults = {}) => {
    setter((current) => [...current, { name: "", relationship: "", ...defaults }]);
  };
  const removeMeetAndGreetPerson = (setter, indexToRemove) => {
    setter((current) => current.filter((_, index) => index !== indexToRemove));
  };

  const updateFinalAssessmentApproval = (field, value) => {
    setFinalAssessmentApproval((current) => ({ ...current, [field]: value }));
  };

  const updateStateAssessmentBioField = (field, value) => {
    setStateAssessmentBioForm((current) => ({ ...current, [field]: value }));
  };

  const applyAssessmentExtractionPrefill = (document) => {
    if (!document) return;
    const bioPatch = stateAssessmentBioPatchFromExtraction(document);
    const extractedContacts = extractionContactsForStateAssessment(document);
    setStateAssessmentBioForm((current) => mergeExtractionBioPrefill(current, bioPatch));
    if (extractedContacts.length) {
      setStateAssessmentContacts((current) => {
        return mergeStateAssessmentContacts([...current, ...extractedContacts]);
      });
    }
  };

  const addStateAssessmentContact = () => {
    setStateAssessmentContacts((current) => [emptyStateAssessmentContact(), ...current]);
  };

  const updateStateAssessmentContact = (id, field, value) => {
    setStateAssessmentContacts((current) =>
      current.map((contact) => (contact.id === id ? { ...contact, [field]: value } : contact)),
    );
  };

  const removeStateAssessmentContact = (id) => {
    setStateAssessmentContacts((current) => current.filter((contact) => contact.id !== id));
  };

  const saveStateAssessmentBioDetails = async () => {
    setStateAssessmentBioSaving(true);
    setStateAssessmentBioError("");
    setStateAssessmentBioSuccess("");
    try {
      const currentPayload = assessmentOnlyInitialPayload(parseAssessmentPayloadObject(assessment?.initialStateAssessment || assessment?.stateAssessment));
      const bioPayload = stateAssessmentBioPayload(stateAssessmentBioForm);
      const collateralContacts = stateAssessmentContactPayload(stateAssessmentContacts);
      const payload = {
        ...currentPayload,
        bioData: bioPayload,
        residentBioData: bioPayload,
        collateralContacts,
        contacts: collateralContacts,
        assessmentExtraction: extractedDocument || currentPayload.assessmentExtraction || null,
        uploadedDocuments: assessmentDocumentsForPayload(currentPayload.uploadedDocuments || stateAssessmentPages),
        updatedAt: new Date().toISOString(),
      };
      let savedRecord = null;
      const residentIdForContacts = processResidentId(assessment) || residentOnboardingPreview?.residentId || residentOnboardingPreview?.resident?.id || "";
      if (residentIdForContacts) {
        const residentBioRequest = stateAssessmentResidentBioRequest({
          assessment,
          bioPayload,
          collateralContacts,
          patientType,
          preview: residentOnboardingPreview,
          residentId: residentIdForContacts,
        });
        const bioResponse = await careService.residents.updateOnboardingBio(residentIdForContacts, residentBioRequest);
        if (bioResponse?.payload || bioResponse?.previewPayload) {
          setResidentOnboardingPreview((current) => ({
            ...(current || {}),
            previewPayload: bioResponse.payload || bioResponse.previewPayload || current?.previewPayload || null,
            onboardingStepStatus: bioResponse.onboardingStepStatus || current?.onboardingStepStatus || null,
          }));
        }
      }
      const processRecordId = linkedAssessmentProcessId || (assessmentResourceType === "assessmentProcess" ? assessmentProcessId : "");
      if (!processRecordId) {
        throw new Error("Unable to determine the resident care process for this resident bio data.");
      }
      savedRecord = await careService.assessmentRecords.saveForProcess(
        processRecordId,
        "initial-assessment",
        { payload },
      );
      if (residentIdForContacts) {
        await careService.residentContacts.replaceForResident(residentIdForContacts, collateralContacts);
      }
      const savedPayload = {
        ...payload,
        ...assessmentOnlyInitialPayload(parseAssessmentPayloadObject(savedRecord?.payload)),
        bioData: bioPayload,
        residentBioData: bioPayload,
        collateralContacts,
        contacts: collateralContacts,
      };
      setAssessment((current) => ({
        ...current,
        initialStateAssessment: savedPayload,
      }));
      setInitialAssessmentRecord({ ...(savedRecord || {}), payload: savedPayload });
      loadedStateAssessmentBioKeyRef.current = "";
      setStateAssessmentBioSuccess("Resident bio data saved for the state initial assessment.");
    } catch (err) {
      setStateAssessmentBioError(err?.response?.data?.message || err?.message || "Unable to save resident bio data.");
    } finally {
      setStateAssessmentBioSaving(false);
    }
  };

  const addDailyNotePhotos = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selectedFiles.length) return;
    const hasHeicFile = selectedFiles.some((file) => /\.(heic|heif)$/i.test(file.name) || String(file.type || "").toLowerCase().includes("hei"));
    if (hasHeicFile) {
      setDailyNotePhotoProcessing(true);
    }
    try {
      const files = await Promise.all(selectedFiles.map(async (selectedFile) => {
        const conversionResult = await convertHeicToJpegResult(selectedFile);
        const file = conversionResult.file;
        return {
          fileName: file.name,
          originalFileName: selectedFile.name,
          description: file.name,
          type: file.type,
          contentType: file.type,
          convertedFromHeic: conversionResult.converted,
          conversionWarning: conversionWarningFor(conversionResult),
          previewUrl: URL.createObjectURL(file),
        };
      }));
      setDailyNotePhotos((current) => [...current, ...files]);
    } finally {
      if (hasHeicFile) {
        setDailyNotePhotoProcessing(false);
      }
    }
  };

  const updateDailyNotePhotoDescription = (indexToUpdate, description) => {
    setDailyNotePhotos((current) =>
      current.map((photo, index) => (index === indexToUpdate ? { ...photo, description } : photo)),
    );
  };

  const dailyNotePhotoUrl = (photo = {}) =>
    photo.previewUrl || photo.signedUrl || photo.downloadUrl || photo.url || photo.fileUrl || "";

  const openDailyNotePhotoPreview = (photo = {}) => {
    const url = dailyNotePhotoUrl(photo);
    if (!url) return;
    setDailyNotePhotoPreview({
      ...photo,
      url,
      title: photo.description || photo.fileName || photo.originalFileName || "Daily note photo",
    });
  };

  const removeDailyNotePhoto = (indexToRemove) => {
    setDailyNotePhotos((current) => current.filter((_, index) => index !== indexToRemove));
  };

  const openAddDailyNoteDrawer = (noteDate = "") => {
    if (!canManageDailyNotes) {
      setProbationError("Only caregivers, care owners, and admins can add daily notes.");
      return;
    }
    const now = new Date();
    setDailyNoteEditingNote(null);
    setDailyNoteDate(normalizeDateInputValue(noteDate) || dateInputValue(now));
    setDailyNoteApproximateTime(timeInputValue(now));
    setDailyNoteEndTime(timeInputValue(shiftMinutes(now, 15)));
    setDailyNoteCategory("Behavior");
    setDailyNoteOtherCategory("");
    setDailyNoteSelectionMode("SELECT");
    setDailyNoteSelectionQuery("");
    setShowAllDailyNoteSelections(false);
    setDailyNoteQuickSelections([]);
    setDailyNoteRichText("");
    setDailyNoteInterventionAction("");
    setDailyNotePhotos([]);
    setShowDailyNoteResidentDob(false);
    setDailyNoteDrawerOpen(true);
  };

  const openEditDailyNoteDrawer = (note) => {
    if (!canManageDailyNotes) {
      setProbationError("Only caregivers, care owners, and admins can edit daily notes.");
      return;
    }
    const knownCategory = dailyNoteCategories.includes(note.category);
    const rangeStart = note.startTimestamp || note.rangeStartTimestamp || note.startedAt || note.happenedAt || note.occurredAt || note.date || new Date();
    const rangeEnd = note.endTimestamp || note.rangeEndTimestamp || note.endedAt || shiftMinutes(new Date(rangeStart), 15);
    const happenedAt = rangeStart;
    setDailyNoteEditingNote(note);
    setDailyNoteDate(dateInputValue(note.noteDate || happenedAt));
    setDailyNoteApproximateTime(note.startTime || timeInputValue(happenedAt));
    setDailyNoteEndTime(note.endTime || timeInputValue(rangeEnd));
    setDailyNoteCategory(knownCategory ? note.category : "Other");
    setDailyNoteOtherCategory(knownCategory ? "" : note.category || "");
    setDailyNoteSelectionMode("SELECT");
    setDailyNoteSelectionQuery("");
    setShowAllDailyNoteSelections(false);
    setDailyNoteQuickSelections(normalizeDailyNoteQuickSelections(note.quickSelections).slice(0, 1));
    setDailyNoteRichText(note.notes || `<p>${note.summary || ""}</p>`);
    setDailyNoteInterventionAction(note.interventionAction || note.intervention || note.actionTaken || "");
    setDailyNotePhotos(note.photos || []);
    setShowDailyNoteResidentDob(false);
    setSelectedDailyNote(null);
    setDailyNoteDrawerOpen(true);
  };

  const toggleDailyNoteQuickSelection = (selection) => {
    const selectionLabel = typeof selection === "string" ? selection : selection?.label;
    if (!selectionLabel) return;
    if (typeof selection === "object" && selection?.category) {
      setDailyNoteCategory(selection.category);
      setDailyNoteOtherCategory("");
    }
    setDailyNoteQuickSelections((current) =>
      current.some((item) => item.label === selectionLabel)
        ? []
        : [{ label: selectionLabel, category: selection?.category || dailyNoteCategory, frequency: "", magnitude: "", trend: "" }],
    );
  };

  const updateDailyNoteQuickSelectionDetail = (selection, field, value) => {
    setDailyNoteQuickSelections((current) =>
      current.map((item) => (item.label === selection ? { ...item, [field]: value } : item)),
    );
  };

  const probationRecordType = () => probationRecordTypeValue;

  const recordFromDailyNoteResponse = (response, nextRecord, fallbackNote) => {
    const payload = response?.payload && typeof response.payload === "object" ? response.payload : response;
    if (payload?.dailyNotes || response?.dailyNotes) {
      return probationRecordPayload(response?.payload ? response : { payload });
    }
    const savedNote = payload?.dailyNote || payload?.note || payload;
    const shouldMergeNote = savedNote && typeof savedNote === "object" && (
      savedNote.id ||
      savedNote.noteDate ||
      savedNote.date ||
      savedNote.summary ||
      savedNote.notes
    );
    if (!shouldMergeNote) {
      return nextRecord;
    }
    const fallbackKey = dailyNoteKey(fallbackNote);
    return {
      ...nextRecord,
      dailyNotes: (nextRecord.dailyNotes || []).map((note) =>
        dailyNoteKey(note) === fallbackKey ? { ...fallbackNote, ...savedNote } : note,
      ),
    };
  };

  const dailyNoteDatabasePayload = (notePayload, processRecordId, recordType) =>
    compactObject({
      ...notePayload,
      id: isLocalDailyNoteId(notePayload?.id) ? undefined : notePayload?.id,
      residentId,
      assessmentProcessId: processRecordId,
      processId: processRecordId,
      assessmentId: assessment?.id || assessmentId,
      assessmentRecordType: recordType,
      recordType,
      source: "ASSESSMENT_PROCESS",
      workspaceId: assessment?.workspaceId,
      organizationId: assessment?.organizationId,
    });

  const submitDailyNote = async (notePayload, nextRecord) => {
    const processRecordId = await resolveLinkedAssessmentProcessId();
    if (!processRecordId) {
      throw new Error("Daily notes can be saved after opening the linked resident care process.");
    }
    const recordType = probationRecordType();
    const databasePayload = dailyNoteDatabasePayload(notePayload, processRecordId, recordType);
    const noteId = notePayload?.id;
    const savedNote = noteId && !isLocalDailyNoteId(noteId)
      ? await careService.dailyNotes.update(noteId, databasePayload)
      : await careService.dailyNotes.create(databasePayload);
    return recordFromDailyNoteResponse(savedNote, nextRecord, notePayload);
  };

  const removeDailyNote = async (notePayload, nextRecord) => {
    const processRecordId = await resolveLinkedAssessmentProcessId();
    if (!processRecordId) {
      throw new Error("Daily notes can be deleted after opening the linked resident care process.");
    }
    const noteId = notePayload?.id || dailyNoteKey(notePayload);
    if (!noteId || isLocalDailyNoteId(noteId)) {
      throw new Error("This daily note does not have a database id yet.");
    }
    await careService.dailyNotes.delete(noteId);
    return nextRecord;
  };

  const downloadWeeklyDailyNoteReport = (report = weeklyDailyNoteReport) => {
    if (!report) return;
    downloadWeeklyReportPdf({ residentName: residentDisplayName, ...report });
  };

  const startWeeklyDailyNoteReportEdit = () => {
    if (!weeklyDailyNoteReport) return;
    setWeeklyDailyNoteReportEditing(true);
    setWeeklyDailyNoteReportEditValues(weeklyReportEditorValuesFor(weeklyDailyNoteReport));
    setWeeklyDailyNoteReportError("");
  };

  const cancelWeeklyDailyNoteReportEdit = () => {
    setWeeklyDailyNoteReportEditing(false);
    setWeeklyDailyNoteReportEditValues(null);
    setWeeklyDailyNoteReportError("");
  };

  const updateWeeklyDailyNoteReportSectionEdit = (sectionKey, value) => {
    setWeeklyDailyNoteReportEditValues((current) => ({
      ...(current || {}),
      sections: {
        ...(current?.sections || {}),
        [sectionKey]: value,
      },
    }));
  };

  const saveWeeklyDailyNoteReportEdit = async () => {
    if (!weeklyDailyNoteReport || !weeklyDailyNoteReportEditValues || weeklyDailyNoteReportSaving) return;
    if (!weeklyDailyNoteReport.id) {
      setWeeklyDailyNoteReportError("This report cannot be edited because it does not have a saved report id.");
      return;
    }
    setWeeklyDailyNoteReportSaving(true);
    setWeeklyDailyNoteReportError("");
    try {
      const processRecordId = await resolveLinkedAssessmentProcessId();
      if (!processRecordId) {
        throw new Error("Open the linked resident care process before saving weekly report edits.");
      }
      const editedReport = weeklyReportWithEdits(
        { residentName: residentDisplayName, ...weeklyDailyNoteReport },
        weeklyDailyNoteReportEditValues,
      );
      const savedReport = await careService.dailyNotes.updateWeeklyReport(
        weeklyDailyNoteReport.id,
        { assessmentProcessId: processRecordId, recordType: probationRecordType() },
        editedReport,
      );
      setWeeklyDailyNoteReport(savedReport || editedReport);
      setSavedWeeklyDailyNoteReports((current) => {
        const nextReport = savedReport || editedReport;
        const nextId = nextReport.id || nextReport.reportId;
        return [
          nextReport,
          ...current.filter((report) => {
            const reportId = report?.id || report?.reportId;
            return nextId ? reportId !== nextId : report !== weeklyDailyNoteReport;
          }),
        ];
      });
      setWeeklyDailyNoteReportEditing(false);
      setWeeklyDailyNoteReportEditValues(null);
    } catch (err) {
      setWeeklyDailyNoteReportError(err?.response?.data?.message || err?.message || "Unable to save weekly report edits.");
    } finally {
      setWeeklyDailyNoteReportSaving(false);
    }
  };

  const openWeeklyDailyNoteReportsPage = async () => {
    const processRecordId = await resolveLinkedAssessmentProcessId();
    if (!processRecordId) {
      setWeeklyDailyNoteReportError("Open the linked resident care process before viewing generated reports.");
      return;
    }
    navigate(`/assessments/${encodeURIComponent(processRecordId)}/weekly-reports?recordType=${encodeURIComponent(probationRecordType())}`);
  };

  const generateWeeklyDailyNoteReport = async () => {
    if (weeklyDailyNoteReportLoading) return;
    setWeeklyDailyNoteReportLoading(true);
    setWeeklyDailyNoteReportError("");
    setWeeklyDailyNoteReportEditing(false);
    setWeeklyDailyNoteReportEditValues(null);
    try {
      if (!weeklyReportRange.start || !weeklyReportRange.end) {
        throw new Error("Select the report start date and end date before generating.");
      }
      if (weeklyReportRange.end < weeklyReportRange.start) {
        throw new Error("The report end date must be on or after the start date.");
      }
      if (!weeklyReportRangeNotes.length) {
        throw new Error("No daily notes were found in the selected report date range.");
      }
      const processRecordId = await resolveLinkedAssessmentProcessId();
      if (!processRecordId) {
        throw new Error("Open the linked resident care process before generating a weekly report.");
      }
      const report = await careService.dailyNotes.generateWeeklyReport({
        assessmentProcessId: processRecordId,
        residentId: processResidentId(assessment),
        recordType: probationRecordType(),
        weekStart: weeklyReportRange.start,
        weekEnd: weeklyReportRange.end,
      });
      setWeeklyDailyNoteReport(report);
      setSavedWeeklyDailyNoteReports((current) => [report, ...current]);
      setWeeklyDailyNoteReportEditing(false);
      setWeeklyDailyNoteReportEditValues(null);
    } catch (err) {
      setWeeklyDailyNoteReportError(err?.response?.data?.message || err?.message || "Unable to generate weekly report.");
    } finally {
      setWeeklyDailyNoteReportLoading(false);
    }
  };

  const saveDailyNote = async () => {
    if (dailyNoteSaving || dailyNotePhotoProcessing) return;
    if (!canManageDailyNotes) {
      setProbationError("Only caregivers, care owners, and admins can save daily notes.");
      return;
    }
    setDailyNoteSaving(true);
    setProbationError("");
    const now = new Date().toISOString();
    const category = dailyNoteCategory === "Other" ? dailyNoteOtherCategory || "Other" : dailyNoteCategory;
    const typedSummary = dailyNoteRichText.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    const quickSummaryParts = [
      dailyNoteQuickSelections.length
        ? dailyNoteQuickSelections
          .map((item) => [
            item.category ? `${item.category} > ${item.label}` : item.label,
            item.frequency ? `frequency: ${item.frequency}` : "",
            item.magnitude ? `magnitude: ${item.magnitude}` : "",
            item.trend ? `trend: ${item.trend}` : "",
          ].filter(Boolean).join(" - "))
          .join("; ")
        : "",
    ].filter(Boolean);
    const startTime = dailyNoteApproximateTime || timeInputValue(new Date());
    const endTime = dailyNoteEndTime || startTime;
    const rangeStartTimestamp = `${dailyNoteDate}T${startTime}:00`;
    const rangeEndTimestamp = `${dailyNoteDate}T${endTime}:00`;
    const currentUserId = personId(user);
    const currentUserName = personName(user) || "Not recorded";
    const existingReporterId = firstText(
      dailyNoteEditingNote?.addedById,
      dailyNoteEditingNote?.authorId,
      dailyNoteEditingNote?.capturedByUserId,
      dailyNoteEditingNote?.createdByUserId,
    );
    const existingReporterName = dailyNoteEditingNote ? dailyNoteReporterName(dailyNoteEditingNote) : "";
    const addedById = dailyNoteEditingNote ? firstText(existingReporterId, currentUserId) : currentUserId;
    const addedByName = dailyNoteEditingNote ? firstText(existingReporterName, currentUserName) : currentUserName;
    const notePayload = {
      id: dailyNoteEditingNote?.id || `daily-note-${Date.now()}`,
      noteDate: dailyNoteDate,
      date: rangeStartTimestamp,
      rangeStartTimestamp,
      rangeEndTimestamp,
      startTimestamp: rangeStartTimestamp,
      endTimestamp: rangeEndTimestamp,
      startedAt: rangeStartTimestamp,
      endedAt: rangeEndTimestamp,
      happenedAt: rangeStartTimestamp,
      occurredAt: rangeStartTimestamp,
      approximateTime: startTime,
      startTime,
      endTime,
      timeIsApproximate: true,
      title: dailyNoteEditingNote?.title || `${category} note`,
      category,
      quickSelections: dailyNoteQuickSelections.slice(0, 1),
      summary: typedSummary || quickSummaryParts.join(" | ") || "Daily note captured.",
      notes: dailyNoteRichText,
      interventionAction: dailyNoteInterventionAction,
      intervention: dailyNoteInterventionAction,
      actionTaken: dailyNoteInterventionAction,
      photos: dailyNotePhotos,
      addedById,
      authorId: addedById,
      capturedByUserId: addedById,
      createdByUserId: addedById,
      addedBy: addedByName,
      addedByName,
      authorName: addedByName,
      capturedBy: addedByName,
      capturedByName: addedByName,
      createdBy: addedByName,
      createdByName: addedByName,
      updatedById: currentUserId,
      updatedByUserId: currentUserId,
      updatedBy: currentUserName,
      updatedByName: currentUserName,
      updatedAt: now,
    };
    try {
      const base = probationRecord || { dailyNotes: [], assignedStaff: [] };
      const editingKey = dailyNoteEditingNote ? dailyNoteKey(dailyNoteEditingNote) : "";
      const existingIndex = editingKey
        ? (base.dailyNotes || []).findIndex((note) => dailyNoteKey(note) === editingKey)
        : -1;
      const dailyNotes = [...(base.dailyNotes || [])];
      if (existingIndex > -1) {
        dailyNotes[existingIndex] = notePayload;
      } else {
        dailyNotes.unshift(notePayload);
      }
      const nextRecord = { ...base, dailyNotes };
      setProbationRecord(nextRecord);
      const persistedRecord = await submitDailyNote(notePayload, nextRecord);
      setProbationRecord({
        ...nextRecord,
        ...persistedRecord,
        dailyNotes: persistedRecord.dailyNotes?.length ? persistedRecord.dailyNotes : nextRecord.dailyNotes,
        assignedStaff: persistedRecord.assignedStaff?.length ? persistedRecord.assignedStaff : nextRecord.assignedStaff,
      });
      setDailyNoteDrawerOpen(false);
      setDailyNoteEditingNote(null);
    } catch (err) {
      setProbationError(err?.response?.data?.message || err?.message || "Unable to save daily note.");
    } finally {
      setDailyNoteSaving(false);
    }
  };

  const deleteDailyNote = async (noteToDelete) => {
    const deleteKey = dailyNoteKey(noteToDelete);
    const base = probationRecord || { dailyNotes: [], assignedStaff: [] };
    const nextRecord = {
      ...base,
      dailyNotes: (base.dailyNotes || []).filter((note) => dailyNoteKey(note) !== deleteKey),
    };
    setProbationRecord(nextRecord);
    setSelectedDailyNote(null);
    setProbationError("");
    try {
      const persistedRecord = await removeDailyNote(noteToDelete, nextRecord);
      setProbationRecord({
        ...nextRecord,
        ...persistedRecord,
        dailyNotes: persistedRecord.dailyNotes || nextRecord.dailyNotes,
        assignedStaff: persistedRecord.assignedStaff || nextRecord.assignedStaff,
      });
    } catch (err) {
      setProbationRecord(base);
      setProbationError(err?.response?.data?.message || err?.message || "Unable to delete daily note.");
    }
  };

  const addProbationStaff = () => {
    setProbationStaffDrawer({
      index: null,
      values: { name: "", role: "", from: `${dateInputValue(new Date())}T07:00`, to: `${dateInputValue(new Date())}T15:00` },
    });
  };

  const editProbationStaff = (staff) => {
    const { originalIndex, ...values } = staff;
    setProbationStaffDrawer({ index: originalIndex, values });
  };

  const removeProbationStaff = (indexToRemove) => {
    setProbationRecord((current) => ({
      ...(current || {}),
      assignedStaff: (current?.assignedStaff || []).filter((_, index) => index !== indexToRemove),
    }));
  };

  const updateProbationStaffDrawerField = (field, value) => {
    setProbationStaffDrawer((current) => current ? { ...current, values: { ...current.values, [field]: value } } : current);
  };

  const saveProbationStaffDrawer = () => {
    if (!probationStaffDrawer || probationStaffSaving) return;
    setProbationStaffSaving(true);
    setProbationRecord((current) => {
      const base = current || { dailyNotes: [], assignedStaff: [] };
      const nextStaff = [...(base.assignedStaff || [])];
      if (probationStaffDrawer.index === null) {
        nextStaff.push(probationStaffDrawer.values);
      } else {
        nextStaff[probationStaffDrawer.index] = probationStaffDrawer.values;
      }
      return { ...base, assignedStaff: nextStaff };
    });
    window.setTimeout(() => {
      setProbationStaffDrawer(null);
      setProbationStaffSaving(false);
    }, 150);
  };
  const addMeetAndGreetCustomQuestion = () => {
    const question = meetAndGreetCustomQuestion.trim();
    if (!question) return;
    setMeetAndGreetCustomQuestions((current) => [...current, question]);
    setMeetAndGreetResponses((current) => ({ ...current, [question]: "" }));
    setMeetAndGreetCustomQuestion("");
  };
  const saveMeetAndGreet = async () => {
    setMeetAndGreetSaving(true);
    setMeetAndGreetError("");
    setMeetAndGreetSuccess("");
    try {
      const allQuestions = [...meetAndGreetQuestions, ...meetAndGreetCustomQuestions];
      const questionsAndResponses = allQuestions.map((question) => ({
        question,
        response: meetAndGreetResponses[question] || "",
      }));
      const payload = {
        meetingDate: normalizeDateInputValue(meetAndGreetForm.meetingDate),
        location: meetAndGreetForm.location,
        facilitator: meetAndGreetForm.facilitator,
        facilitatorUserId: meetAndGreetForm.facilitatorUserId,
        careCircle: meetAndGreetPersonPayload(meetAndGreetCareCircle),
        careProviderStaff: meetAndGreetPersonPayload(meetAndGreetStaff),
        questions: questionsAndResponses,
        questionsAndResponses,
        responses: questionsAndResponses,
        customQuestions: meetAndGreetCustomQuestions,
        notes: meetAndGreetForm.notes,
      };
      const processRecordId = await resolveLinkedAssessmentProcessId();
      const savedAssessment = processRecordId
        ? await careService.meetAndGreet.saveForProcess(processRecordId, payload)
        : await careService.residentAssessments.update(
          assessment?.id || assessmentId,
          residentAssessmentMeetAndGreetRequest(assessment, payload),
        );
      if (processRecordId) {
        setAssessmentResourceType("assessmentProcess");
      } else if (assessmentResourceType === "residentAssessment") {
        setAssessment(savedAssessment);
      }
      setMeetAndGreetSuccess("Meet and Greet details saved.");
    } catch (err) {
      setMeetAndGreetError(err?.response?.data?.message || err?.message || "Unable to save Meet and Greet details.");
    } finally {
      setMeetAndGreetSaving(false);
    }
  };
  const saveProviderDecision = async (decision, notes = "") => {
    setProviderReviewSaving(decision);
    setProviderReviewError("");
    setProviderReviewSuccess("");
    try {
      const processRecordId = await resolveLinkedAssessmentProcessId();
      if (!processRecordId) {
        throw new Error("Provider decision can be saved after opening the linked resident care process.");
      }
      const saved = await careService.providerDecision.saveForProcess(processRecordId, {
        decision,
        notes,
      });
      const nextStage = normalizeStageKey(saved?.stage || saved?.workflowStage || saved?.currentStage || (decision === "ACCEPTED" ? "CLIENT_ONBOARDING" : "PROVIDER_REVIEW"));
      setAssessment({
        ...assessment,
        ...saved,
        stage: nextStage,
        currentStage: nextStage,
        workflowStage: nextStage,
        providerDecision: saved?.providerDecision || decision,
        providerDecisionNotes: saved?.providerDecisionNotes || notes,
      });
      setAssessmentResourceType("assessmentProcess");
      setProviderReviewSuccess(decision === "ACCEPTED" ? "Resident accepted for care." : "Resident declined for care.");
    } catch (err) {
      setProviderReviewError(err?.response?.data?.message || err?.message || "Unable to save provider decision.");
    } finally {
      setProviderReviewSaving("");
    }
  };
  const confirmProviderDecision = (decision) => {
    const accepting = decision === "ACCEPTED";
    let decisionNotes = "";
    Modal.confirm({
      title: accepting ? "Accept resident?" : "Decline resident?",
      content: (
        <div className="provider-decision-confirm">
          <p>
            {accepting
              ? `Accept ${residentDisplayName} and move the resident care process to onboarding?`
              : `Decline ${residentDisplayName} for care? The declined decision will be recorded.`}
          </p>
          <label className="ncp-form-field ncp-form-field-wide">
            <span>Decision notes</span>
            <textarea
              rows={4}
              defaultValue={assessment.providerDecisionNotes || ""}
              placeholder={accepting ? "Add acceptance notes, conditions, or next steps." : "Add decline reason or follow-up notes."}
              onChange={(event) => {
                decisionNotes = event.target.value;
              }}
            />
          </label>
        </div>
      ),
      okText: accepting ? "Accept resident" : "Decline resident",
      cancelText: "Cancel",
      okButtonProps: accepting ? {} : { danger: true },
      centered: true,
      onOk: () => saveProviderDecision(decision, decisionNotes || assessment.providerDecisionNotes || ""),
    });
  };
  const saveFinalAssessmentDetails = async () => {
    const saveStartedAt = Date.now();
    setFinalAssessmentSaving(true);
    setFinalAssessmentCompleteError("");
    setFinalAssessmentSaveSuccess("");
    try {
      const payload = {
        ...parseAssessmentPayloadObject(assessment.finalStateAssessment),
        ...finalAssessmentApproval,
        updatedAt: new Date().toISOString(),
        uploadedDocuments: assessmentDocumentsForPayload(stateAssessmentPages),
      };
      const processRecordId = await resolveLinkedAssessmentProcessId();
      const savedRecord = processRecordId
        ? await careService.assessmentRecords.saveForProcess(
          processRecordId,
          "final-assessment",
          { payload },
        )
        : null;
      const savedPayload = parseAssessmentPayloadObject(savedRecord?.payload || payload);
      setAssessment((current) => ({
        ...current,
        finalStateAssessment: savedPayload,
      }));
      setFinalAssessmentSaveSuccess(processRecordId ? "Final assessment details saved." : "Final assessment details saved on this page.");
    } catch (err) {
      setFinalAssessmentCompleteError(err?.response?.data?.message || err?.message || "Unable to save final assessment details.");
    } finally {
      const elapsed = Date.now() - saveStartedAt;
      if (elapsed < 500) {
        await new Promise((resolve) => setTimeout(resolve, 500 - elapsed));
      }
      setFinalAssessmentSaving(false);
    }
  };
  const markFinalAssessmentCompleted = () => {
    if (!canCompleteAssessmentStages) {
      setFinalAssessmentCompleteError("Only care owners and admins can mark assessment stages as completed.");
      return;
    }
    Modal.confirm({
      title: "Mark final assessment as completed?",
      content: `This will complete the resident care process for ${residentDisplayName}.`,
      okText: "Mark as Completed",
      cancelText: "Cancel",
      onOk: async () => {
        setFinalAssessmentCompleting(true);
        setFinalAssessmentCompleteError("");
        try {
          const processRecordId = await resolveLinkedAssessmentProcessId();
          if (!processRecordId) {
            throw new Error("Final assessment completion is available after opening the linked resident care process.");
          }
          const completionDate = new Date().toISOString().slice(0, 10);
          const saved = await careService.assessmentProcesses.update(
            processRecordId,
            assessmentProcessUpdateRequest(assessment, {
              stage: "FINAL_ASSESSMENT_UPDATE",
              currentStage: "FINAL_ASSESSMENT_UPDATE",
              workflowStage: "FINAL_ASSESSMENT_UPDATE",
              status: "COMPLETED",
              workflowStatus: "COMPLETED",
              currentScreen: "final-assessment",
              processEndDate: assessment.processEndDate || completionDate,
              stateConfirmedAt: assessment.stateConfirmedAt || completionDate,
              finalStateAssessment: stringifyJsonValue({
                ...parseAssessmentPayloadObject(assessment.finalStateAssessment),
                ...finalAssessmentApproval,
                status: "COMPLETED",
                completedAt: new Date().toISOString(),
                completedBy: "Provider",
                uploadedDocuments: assessmentDocumentsForPayload(stateAssessmentPages),
              }, "{}"),
            }, patientType),
          );
          const completed = await careService.assessmentProcesses.updateStatus(processRecordId, {
            status: "COMPLETED",
            workflowStatus: "COMPLETED",
          });
          setAssessment({
            ...assessment,
            ...saved,
            ...completed,
            status: "COMPLETED",
            workflowStatus: "COMPLETED",
          });
          setFinalAssessmentApproval((current) => ({
            ...current,
            approvalStatus: current.approvalStatus === "Draft" ? "Confirmed by state" : current.approvalStatus,
          }));
        } catch (err) {
          setFinalAssessmentCompleteError(err?.response?.data?.message || err?.message || "Unable to mark final assessment as completed.");
          throw err;
        } finally {
          setFinalAssessmentCompleting(false);
        }
      },
    });
  };
  const applyPreliminaryAiAction = (nextDescription) => {
    setPreliminaryPlanHistory((current) => [...current, preliminaryPlanDescription]);
    setPreliminaryPlanDescription(nextDescription);
  };
  const refinePreliminaryPlan = () => {
    applyPreliminaryAiAction(`${preliminaryFormToHtml(preliminaryPlanForm)}<p><strong>AI refinement:</strong> Clarified care priorities, tightened language, and aligned observations with assessment, MaG, onboarding, and daily note data.</p>`);
  };
  const summarizePreliminaryPlan = () => {
    const sourceText = preliminaryPlanFieldSections
      .flatMap((section) => section.fields)
      .map((field) => `${preliminaryFieldLabel(field)}: ${stripHtmlText(preliminaryPlanForm[preliminaryFieldKey(field)])}`)
      .filter((line) => !line.endsWith(": "))
      .join(" ");
    applyPreliminaryAiAction(`<p><strong>Summary:</strong> ${sourceText || "Capture preliminary service plan fields before generating the summary."}</p>`);
  };
  const formatPreliminaryPlan = () => {
    applyPreliminaryAiAction(preliminaryFormToHtml(preliminaryPlanForm));
  };
  const undoPreliminaryPlanAction = () => {
    setPreliminaryPlanHistory((current) => {
      if (!current.length) return current;
      const previous = current[current.length - 1];
      setPreliminaryPlanDescription(previous);
      return current.slice(0, -1);
    });
  };
  const addPreliminaryAttachedFiles = (event) => {
    const files = Array.from(event.target.files || []).map((file) => ({
      name: file.name,
      caption: file.name,
      type: file.type || "File",
      size: file.size,
      addedAt: new Date().toISOString(),
    }));
    event.target.value = "";
    setPreliminaryAttachedFiles((current) => [...files, ...current]);
  };
  const updatePreliminaryAttachedFile = (indexToUpdate, field, value) => {
    setPreliminaryAttachedFiles((current) =>
      current.map((file, index) => (index === indexToUpdate ? { ...file, [field]: value } : file)),
    );
  };
  const removePreliminaryAttachedFile = (indexToRemove) => {
    setPreliminaryAttachedFiles((current) => current.filter((_, index) => index !== indexToRemove));
  };
  const updatePreliminaryPlanField = (field, value) => {
    setPreliminaryPlanForm((current) => ({ ...current, [field]: value }));
  };
  const preliminaryPayload = () => ({
    summaryForm: preliminaryPlanForm,
    preliminaryPlanForm,
    description: preliminaryFormToHtml(preliminaryPlanForm),
    preliminaryPlanDescription: preliminaryFormToHtml(preliminaryPlanForm),
    aiDraftDescription: preliminaryPlanDescription,
    latestGeneratedAt: new Date().toISOString(),
    lastUpdatedAt: new Date().toISOString(),
    generatedFrom: ["State assessment", "Meet and Greet", "Onboarding", "Provider review", "Daily notes"],
    generationLocked: preliminaryGenerationLocked,
    history: preliminaryPlanHistory,
    attachedFiles: preliminaryAttachedFiles,
  });
  const savePreliminaryPlan = async () => {
    setPreliminarySaving(true);
    setPreliminaryError("");
    setPreliminarySuccess("");
    try {
      if (assessmentResourceType !== "assessmentProcess") {
        throw new Error("Preliminary plan can be saved after opening the linked resident care process.");
      }
      const payload = preliminaryPayload();
      await careService.assessmentRecords.saveForProcess(assessmentProcessId, "preliminary-plan", { payload });
      setAssessment((current) => ({ ...current, preliminaryPlan: payload }));
      setPreliminarySuccess("Preliminary plan saved.");
    } catch (err) {
      setPreliminaryError(err?.response?.data?.message || err?.message || "Unable to save preliminary plan.");
    } finally {
      setPreliminarySaving(false);
    }
  };
  const printPreliminaryPlan = () => {
    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
        <head><title>Preliminary Service Plan</title></head>
        <body style="font-family: Arial, sans-serif; color: #102f2b; line-height: 1.5; padding: 32px;">
          <h1 style="color:#004225;">Preliminary Service Plan</h1>
          ${preliminaryFormToHtml(preliminaryPlanForm)}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };
  const addStateAssessmentPages = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selectedFiles.length) return;
    const hasHeicFile = selectedFiles.some((file) => /\.(heic|heif)$/i.test(file.name) || String(file.type || "").toLowerCase().includes("hei"));
    setUploadingStateAssessmentPages(true);
    setHeicProcessing(hasHeicFile);
    setError("");
    setUploadError("");
    setUploadSuccess("");
    try {
      const mappedFiles = [];
      for (const [index, selectedFile] of selectedFiles.entries()) {
        const pageNumber = stateAssessmentPages.length + selectedFiles.length - index;
        const conversionResult = await convertHeicToJpegResult(selectedFile);
        const convertedFromHeic = conversionResult.converted;
        const conversionWarning = conversionWarningFor(conversionResult);
        const file = conversionResult.file;
        const previewUrl = URL.createObjectURL(file);
        const isPhoto = String(file.type || "").toLowerCase().startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)$/i.test(file.name);
        stateAssessmentPageUrlsRef.current.push(previewUrl);
        const upload = await uploadFile(null, file, {
          confirmUpload: true,
          fileType: file.type || "DOCUMENT",
          includeUserId: false,
          preserveOriginal: true,
          purpose: "OTHER",
          skipUserValidation: true,
        });
        const fileId = upload?.fileId || upload?.file?.id || null;
        if (!fileId) {
          throw new Error(`Storage upload did not return a file ID for ${file.name}.`);
        }
        mappedFiles.push({
          id: `${file.name}-${file.lastModified}-${index}`,
          file,
          fileId,
          storageFileId: fileId,
          pageNumber,
          pageTitle: `Page ${pageNumber}`,
          name: file.name,
          originalFileName: selectedFile.name,
          convertedFromHeic,
          conversionWarning,
          description: `Assessment page ${pageNumber}`,
          type: file.type,
          contentType: file.type,
          isPhoto,
          isImage: isPhoto,
          previewUrl,
          size: file.size,
          addedAt: new Date().toISOString(),
          uploaded: true,
          uploadedAt: new Date().toISOString(),
          confirmed: Boolean(upload?.confirmed),
          needsUpload: false,
          storageStatus: upload?.confirmed ? "CONFIRMED" : "UPLOADED_NOT_CONFIRMED",
        });
      }
      if (!mappedFiles.length) return;
      setError(mappedFiles.map((file) => file.conversionWarning).find(Boolean) || "");
      setStateAssessmentPages((current) => renumberReadyPages([...mappedFiles, ...current]));
      setUploadSuccess(`${mappedFiles.length} assessment page${mappedFiles.length === 1 ? "" : "s"} uploaded.`);
    } catch (err) {
      setUploadError(err?.response?.data?.message || err?.message || "Unable to upload assessment pages.");
    } finally {
      setHeicProcessing(false);
      setUploadingStateAssessmentPages(false);
    }
  };
  const removeStateAssessmentPage = (id) => {
    setStateAssessmentPages((current) => {
      const removed = current.find((page) => page.id === id);
      if (removed?.previewUrl) {
        if (removed.previewUrl.startsWith("blob:")) URL.revokeObjectURL(removed.previewUrl);
        stateAssessmentPageUrlsRef.current = stateAssessmentPageUrlsRef.current.filter((url) => url !== removed.previewUrl);
      }
      return renumberReadyPages(current.filter((page) => page.id !== id));
    });
  };
  const updateStateAssessmentPageDescription = (id, description) => {
    setStateAssessmentPages((current) => current.map((item) => item.id === id ? { ...item, description } : item));
  };
  const reorderStateAssessmentPages = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setStateAssessmentPages((current) => {
      const oldIndex = current.findIndex((page) => page.id === active.id);
      const newIndex = current.findIndex((page) => page.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return current;
      return renumberReadyPages(arrayMove(current, oldIndex, newIndex));
    });
  };
  const previewGalleryPages = stateAssessmentPages;
  const previewGalleryTitle = showFinalAssessmentUpload ? "Final Assessment Pages" : "State Assessment Pages";
  const previewGalleryCount = previewGalleryPages.length;
  const findPreviewFileIndex = (file = {}) => {
    const fileId = file.id || "";
    const storageId = assessmentStorageId(file);
    const fileName = assessmentFileName(file, "");
    const fileUrl = assessmentFileUrl(file);
    return previewGalleryPages.findIndex((page) => (
      (fileId && page.id === fileId)
      || (storageId && assessmentStorageId(page) === storageId)
      || (fileUrl && assessmentFileUrl(page) === fileUrl)
      || (fileName && assessmentFileName(page, "") === fileName)
    ));
  };
  const showPreviewAt = (index) => {
    if (!previewGalleryCount) return;
    const nextIndex = (index + previewGalleryCount) % previewGalleryCount;
    setPreviewFile(previewGalleryPages[nextIndex]);
    setPreviewFileIndex(nextIndex);
    setPreviewRotation(0);
  };
  const openFilePreview = (file) => {
    const nextIndex = findPreviewFileIndex(file);
    setPreviewFile(file);
    setPreviewFileIndex(nextIndex);
    setPreviewRotation(0);
  };
  const closeFilePreview = () => {
    setPreviewFile(null);
    setPreviewFileIndex(-1);
    setPreviewRotation(0);
  };
  const rotatePreview = (degrees) => {
    setPreviewRotation((current) => (current + degrees + 360) % 360);
  };

  const extractAssessmentData = async () => {
    if (!stateAssessmentPages.length) {
      setExtractionError("Add assessment pages before extracting data.");
      return;
    }

    const localPages = stateAssessmentPages.filter((page) => page.file).map((page) => page.file);
    if (!localPages.length) {
      setExtractionError("Select the original assessment files before extracting data.");
      return;
    }

    setExtractingAssessmentData(true);
    setExtractionError("");
    try {
      const extractionPages = await Promise.all(localPages.map(async (file) => {
        const isCompressibleImage = String(file.type || "").toLowerCase().startsWith("image/")
          && !/image\/(gif|svg\+xml)/i.test(file.type || "");
        if (!isCompressibleImage) return file;

        const compressed = await compressImageToTarget(file, {
          maxBytes: 1_000_000,
          maxWidth: 2048,
          maxHeight: 2048,
          initialQuality: 0.82,
          minQuality: 0.55,
          forceJpeg: true,
        });
        if (compressed.blob === file) return file;
        const jpegName = file.name.replace(/\.[^.]+$/, "") + ".jpg";
        return new File([compressed.blob], jpegName, {
          lastModified: file.lastModified || Date.now(),
          type: compressed.contentType,
        });
      }));
      const extractionAssessmentProcessId = linkedAssessmentProcessId
        || (assessmentResourceType === "assessmentProcess" ? assessmentProcessId : "");
      if (!extractionAssessmentProcessId) {
        throw new Error("Open the linked resident care process before extracting assessment data.");
      }
      const extraction = await careService.assessmentExtractions.extractForProcess(
        extractionAssessmentProcessId,
        extractionPages,
      );
      if (!extraction?.sections) {
        throw new Error("Gemini did not return an assessment extraction.");
      }
      setExtractedDocument(extraction);
      setAssessment((current) => ({
        ...(current || assessment || {}),
        initialStateAssessment: {
          ...parseAssessmentPayloadObject((current || assessment)?.initialStateAssessment || (current || assessment)?.stateAssessment),
          assessmentExtraction: extraction,
        },
      }));
      applyAssessmentExtractionPrefill(extraction);
      setExtractedDocumentDrawerOpen(true);
    } catch (err) {
      const connectionReset = err?.code === "ERR_NETWORK"
        || /connection reset|network error/i.test(err?.message || "");
      setExtractionError(
        err?.response?.data?.message
        || (connectionReset
          ? "The Gemini extraction connection closed before processing finished. The selected pages are still available; please try extraction again."
          : err?.message)
        || "Unable to extract assessment data.",
      );
    } finally {
      setExtractingAssessmentData(false);
    }
  };

  const openExtractedDocumentDrawer = () => {
    if (!extractedDocument) {
      setExtractionError("No extracted document is available to preview.");
      return;
    }
    setExtractedDocumentDrawerOpen(true);
  };

  const continueExtractionToCarePlan = () => {
    const negotiatedPlanStage = flowStages.find((stage) => isNegotiatedCarePlanStage(stage));
    applyAssessmentExtractionPrefill(extractedDocument);
    setExtractedDocumentDrawerOpen(false);
    setSelectedStageKey("NEGOTIATED_CARE_PLAN");
    if (negotiatedPlanStage) {
      setAssessmentStageHash(negotiatedPlanStage, patientType);
    }
    window.setTimeout(() => {
      document.querySelector(".negotiated-plan-workflow")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const uploadAndSaveStateAssessmentPages = async () => {
    if (!stateAssessmentPages.length) {
      setUploadError("Add at least one assessment page before saving.");
      setUploadSuccess("");
      return;
    }

    setUploadingStateAssessmentPages(true);
    setUploadError("");
    setUploadSuccess("");

    try {
      const uploadedPages = [];
      const pendingUploads = stateAssessmentPages.filter((page) => page.file && !assessmentStorageId(page)).length;
      for (const page of stateAssessmentPages) {
        const existingFileId = assessmentStorageId(page);
        if (existingFileId) {
          uploadedPages.push({
            ...page,
            file: undefined,
            fileId: existingFileId,
            storageFileId: existingFileId,
            confirmed: Boolean(page.confirmed),
            needsUpload: false,
            uploaded: true,
            storageStatus: page.storageStatus || (page.confirmed ? "CONFIRMED" : "UPLOADED_NOT_CONFIRMED"),
          });
          continue;
        }

        if (!page.file) {
          uploadedPages.push(page);
          continue;
        }

        const upload = await uploadFile(null, page.file, {
          confirmUpload: true,
          fileType: page.contentType || page.type || page.file?.type || "DOCUMENT",
          includeUserId: false,
          preserveOriginal: true,
          purpose: "OTHER",
          skipUserValidation: true,
        });
        const fileId = upload?.fileId || upload?.file?.id || null;
        if (!fileId) throw new Error(`Storage upload did not return a file ID for ${assessmentFileName(page)}.`);
        uploadedPages.push({
          ...page,
          file: undefined,
          fileId,
          storageFileId: fileId,
          confirmed: Boolean(upload?.confirmed),
          needsUpload: false,
          uploaded: true,
          uploadedAt: new Date().toISOString(),
          storageStatus: upload?.confirmed ? "CONFIRMED" : "UPLOADED_NOT_CONFIRMED",
        });
      }

      const nextDocuments = uploadedPages
        .map((page, index) => ({
          fileId: assessmentStorageId(page) || null,
          storageFileId: assessmentStorageId(page) || null,
          fileName: assessmentFileName(page, `Assessment page ${index + 1}`),
          originalFileName: page.originalFileName || page.sourceFileName || assessmentSourceName(page, `Assessment page ${index + 1}`),
          pageNumber: assessmentPageNumber(page, index),
          pageTitle: assessmentPageTitle(page, index),
          caption: page.description || assessmentPageTitle(page, index),
          description: page.description || "",
          contentType: page.contentType || page.type || "",
          type: page.type || page.contentType || "",
          size: page.size || page.fileSize || 0,
          isPhoto: Boolean(page.isPhoto || page.isImage),
          isImage: Boolean(page.isPhoto || page.isImage),
          confirmed: Boolean(page.confirmed),
          storageStatus: page.storageStatus || (page.confirmed ? "CONFIRMED" : "UPLOADED_NOT_CONFIRMED"),
          uploaded: true,
          uploadedAt: page.uploadedAt || page.addedAt || new Date().toISOString(),
        }))
        .sort((a, b) => Number(a.pageNumber || 0) - Number(b.pageNumber || 0));

      const isResidentAssessment = assessmentResourceType === "residentAssessment";
      const assessmentRecordType = showFinalAssessmentUpload ? "final-assessment" : "initial-assessment";
      const assessmentRecordPayload = showFinalAssessmentUpload
        ? { uploadedDocuments: nextDocuments, ...finalAssessmentApproval }
        : { uploadedDocuments: nextDocuments };
      const savedAssessment = isResidentAssessment
        ? await careService.residentAssessments.updatePages(
          assessment?.id || assessmentId,
          { uploadedDocuments: nextDocuments },
        )
        : showFinalAssessmentUpload
          ? await careService.assessmentRecords.saveForProcess(
            assessmentProcessId,
            "final-assessment",
            { payload: assessmentRecordPayload },
          )
        : await careService.assessmentRecords.savePagesForProcess(
          assessmentProcessId,
          assessmentRecordType,
          assessmentRecordPayload,
        );

      const savedDocuments = uploadedDocumentsFromRecord(
        savedAssessment,
      );
      const confirmedDocuments = savedDocuments.length ? savedDocuments : nextDocuments;
      const nextDocumentKey = assessmentDocumentStateKey(confirmedDocuments);
      const currentAssessmentPayload = showFinalAssessmentUpload
        ? parseAssessmentPayloadObject(assessment?.finalStateAssessment)
        : assessmentOnlyInitialPayload(parseAssessmentPayloadObject(assessment?.initialStateAssessment || assessment?.stateAssessment));
      const stateAssessmentPayload = {
        ...currentAssessmentPayload,
        ...(showFinalAssessmentUpload
          ? parseAssessmentPayloadObject(savedAssessment?.payload)
          : assessmentOnlyInitialPayload(parseAssessmentPayloadObject(savedAssessment?.payload))),
        pageCount: confirmedDocuments.length,
        uploadedDocuments: confirmedDocuments,
        ...(showFinalAssessmentUpload ? finalAssessmentApproval : {}),
      };

      setAssessment((current) => ({
        ...current,
        ...(isResidentAssessment ? (savedAssessment || {}) : {}),
        documents: confirmedDocuments,
        uploadedDocuments: confirmedDocuments,
        ...(showFinalAssessmentUpload ? {
          finalStateAssessment: stateAssessmentPayload,
        } : {
          initialStateAssessment: isResidentAssessment ? {
          ...assessmentOnlyInitialPayload(parseAssessmentPayloadObject(current?.initialStateAssessment)),
          ...assessmentOnlyInitialPayload(parseAssessmentPayloadObject(savedAssessment?.initialStateAssessment)),
          pageCount: confirmedDocuments.length,
          status: savedAssessment?.status || "UPLOADED",
          uploadedDocuments: confirmedDocuments,
          } : stateAssessmentPayload,
        }),
      }));
      if (!showFinalAssessmentUpload) {
        setInitialAssessmentRecord({ ...(savedAssessment || {}), payload: stateAssessmentPayload });
      }
      stateAssessmentPages.forEach((page) => {
        if (page.previewUrl?.startsWith("blob:")) URL.revokeObjectURL(page.previewUrl);
      });
      stateAssessmentPageUrlsRef.current = [];
      setStateAssessmentPages(renumberReadyPages(uploadedAssessmentPagesForState(confirmedDocuments)));
      loadedAssessmentDocumentKeyRef.current = `${showFinalAssessmentUpload ? "final" : "initial"}:${nextDocumentKey}`;
      setUploadSuccess(
        pendingUploads
          ? `${pendingUploads} new assessment page${pendingUploads === 1 ? "" : "s"} uploaded and ${confirmedDocuments.length} total page${confirmedDocuments.length === 1 ? "" : "s"} saved.`
          : `${confirmedDocuments.length} assessment page${confirmedDocuments.length === 1 ? "" : "s"} reordered and saved.`,
      );
      return true;
    } catch (err) {
      setUploadError(err?.response?.data?.message || err?.message || "Unable to upload and save assessment pages.");
      return false;
    } finally {
      setUploadingStateAssessmentPages(false);
    }
  };
  const previewUrl = assessmentFileUrl(previewFile || {});
  const previewName = assessmentFileName(previewFile || {}, "Assessment page");
  const previewDisplayIndex = previewFileIndex >= 0 ? previewFileIndex : findPreviewFileIndex(previewFile || {});
  const canNavigatePreviewGallery = previewGalleryCount > 1 && previewDisplayIndex >= 0;
  const previewRotatedSideways = Math.abs(previewRotation % 180) === 90;
  const confirmCancelAssessmentProcess = () => {
    Modal.confirm({
      title: "Cancel resident care process editing?",
      content: "Any unsaved changes on this screen will be lost.",
      okText: "Yes, Cancel",
      cancelText: "Keep Editing",
      centered: true,
      okButtonProps: { danger: true },
      onOk: () => navigate(residentLink || "/assessments"),
    });
  };

  return (
    <CareShell className="dashboard-content residents-page assessments-page assessment-process-page">
      <div className="residents-page-head">
        <div className="page-title">
          <h1>Resident Care Process</h1>
          <p>{assessment?.name || assessment?.title || `${processResidentName(assessment)} resident care process workspace.`}</p>
        </div>
        <div className="residents-actions">
          <Link className="mini-btn" to="/assessments"><i className="ti ti-arrow-left" /> Care Processes</Link>
          <Link className="btn primary" to={residentLink}><i className="ti ti-user" /> Resident</Link>
          <button className="btn oliveeb-red-outline" type="button" onClick={confirmCancelAssessmentProcess}><i className="ti ti-x" /> Cancel</button>
        </div>
      </div>
      {error ? <p className="form-error">{error}</p> : null}
      {stageCompletionMessage ? (
        <p className={stageCompletionMessage.toLowerCase().includes("unable") || stageCompletionMessage.toLowerCase().includes("available after") ? "form-error" : "form-success"}>
          {stageCompletionMessage}
        </p>
      ) : null}

      <Panel title="Care Process Flow">
        <div className="assessment-flow-summary">
          <div>
            <span>Current Stage</span>
            <strong>{currentStage ? (patientType === "RESIDENT" && currentStage.existingLabel ? currentStage.existingLabel : currentStage.label) : "Stage not set"}</strong>
            <p>{currentStage ? (patientType === "RESIDENT" && currentStage.existingDescription ? currentStage.existingDescription : currentStage.description) : "This resident care process has not reported a recognized stage yet."}</p>
          </div>
          <em className={`pill ${processStatusClass(assessmentWorkflowStatus)}`}>{assessmentWorkflowStatus || "Open"}</em>
        </div>
        <div className="assessment-flow-stage-list">
          {displayStages.map((stage) => (
            <button
              className={`assessment-flow-stage is-${stage.status.toLowerCase()} ${!stage.isReached ? "is-locked" : ""} ${selectedStage?.displayLabel === stage.displayLabel ? "is-selected" : ""}`}
              type="button"
              disabled={!stage.isReached}
              aria-disabled={!stage.isReached}
              onClick={() => {
                if (!stage.isReached) return;
                setSelectedStageKey(normalizeStageKey(stage.stageKeys[0]));
                setAssessmentStageHash(stage, patientType);
              }}
              key={stage.displayLabel}
            >
              <span><i className={`ti ${stage.icon}`} /></span>
              <div>
                <strong>{stage.displayLabel}</strong>
                <small>{stage.displayDescription}</small>
              </div>
              <em>{stage.status}</em>
            </button>
          ))}
        </div>
      </Panel>

      {assessmentIsCompleted ? (
        <div className="assessment-stage-readonly-notice" role="status">
          <i className="ti ti-lock" aria-hidden="true" />
          <div>
            <strong>Completed assessment</strong>
            <span>Stage records are read-only. You can still select each stage to review its data.</span>
          </div>
        </div>
      ) : null}

      <div
        className={`assessment-stage-data ${assessmentIsCompleted ? "is-read-only" : ""}`}
        onBeforeInputCapture={blockCompletedAssessmentInteraction}
        onChangeCapture={blockCompletedAssessmentInteraction}
        onClickCapture={blockCompletedAssessmentInteraction}
        onSubmitCapture={blockCompletedAssessmentInteraction}
      >
      {showStateAssessmentUpload || showFinalAssessmentUpload ? (
        <Panel
          title={`${selectedStage.displayLabel} Upload Details`}
          actionNode={showFinalAssessmentUpload ? (
            <div className="assessment-drawer-action-links assessment-page-action-links probation-action-buttons">
              <button
                className="btn primary state-assessment-upload-button"
                disabled={finalAssessmentSaving || finalAssessmentCompleting || uploadingStateAssessmentPages || storageUploading || heicProcessing}
                onClick={saveFinalAssessmentDetails}
                type="button"
              >
                {finalAssessmentSaving ? (
                  <><i className="ti ti-loader-2" /> Saving...</>
                ) : (
                  <><i className="ti ti-device-floppy" /> Save</>
                )}
              </button>
              {stageCompleteAction()}
            </div>
          ) : stageCompleteAction()}
        >
          <div className="state-assessment-upload-grid">
            <section className="assessment-drawer-section state-assessment-upload-rail">
              <div className="assessment-section-heading-row assessment-section-heading-row--actions">
                <h3>{showFinalAssessmentUpload ? "Upload Final Assessment Pages" : "Upload Assessment Pages"}</h3>
                <div className="assessment-section-actions" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {showFinalAssessmentUpload ? (
                    <button
                      className="btn primary state-assessment-upload-button"
                      disabled={!stateAssessmentPages.length || uploadingStateAssessmentPages || storageUploading || heicProcessing}
                      onClick={uploadAndSaveStateAssessmentPages}
                      type="button"
                    >
                      {uploadingStateAssessmentPages || storageUploading ? (
                        <><i className="ti ti-loader-2" /> Saving...</>
                      ) : heicProcessing ? (
                        <><i className="ti ti-loader-2" /> Processing...</>
                      ) : (
                        <><i className="ti ti-cloud-upload" /> Save pages</>
                      )}
                    </button>
                  ) : (
                    <>
                      <button
                        className="btn primary state-assessment-upload-button"
                        disabled={
                          extractingAssessmentData
                          || uploadingStateAssessmentPages
                          || storageUploading
                          || heicProcessing
                          || !stateAssessmentPages.length
                        }
                        onClick={extractAssessmentData}
                        type="button"
                      >
                        {extractingAssessmentData ? (
                          <><i className="ti ti-loader-2" /> Extracting...</>
                        ) : heicProcessing ? (
                          <><i className="ti ti-loader-2" /> Processing...</>
                        ) : (
                          <><i className="ti ti-file-search" /> Extract data</>
                        )}
                      </button>
                      <button
                        className="btn secondary state-assessment-upload-button"
                        disabled={!extractedDocument || extractingAssessmentData}
                        onClick={openExtractedDocumentDrawer}
                        type="button"
                      >
                        <i className="ti ti-eye" /> Preview extracted data
                      </button>
                    </>
                  )}
                </div>
              </div>
              {showFinalAssessmentUpload || !stateAssessmentPages.length ? (
                <label className="assessment-upload assessment-upload-compact">
                  <input
                    accept=".pdf,.doc,.docx,image/*"
                    className="assessment-file-input"
                    multiple
                    disabled={uploadingStateAssessmentPages || storageUploading || heicProcessing}
                    onChange={addStateAssessmentPages}
                    type="file"
                  />
                  <i className="ti ti-file-upload" />
                  <strong>{showFinalAssessmentUpload ? "Add final assessment pages" : "Add assessment pages"}</strong>
                  <small>Upload PDF, Word, or image pages from the {showFinalAssessmentUpload ? "final assessment and approval packet" : "state assessment"}.</small>
                </label>
              ) : null}
              {heicProcessing ? (
                <div className="state-assessment-processing" role="status">
                  <i className="ti ti-loader-2" />
                  <span>
                    <strong>Processing HEIC photo</strong>
                    <small>Converting the selected photo to JPEG so it can be previewed and uploaded.</small>
                  </span>
                </div>
              ) : null}
              {uploadError ? <p className="form-error">{uploadError}</p> : null}
              {extractionError ? <p className="form-error">{extractionError}</p> : null}
              {uploadSuccess ? <SavedNotice>{uploadSuccess}</SavedNotice> : null}
              {showFinalAssessmentUpload && finalAssessmentSaveSuccess ? <SavedNotice>{finalAssessmentSaveSuccess}</SavedNotice> : null}
              {showFinalAssessmentUpload && finalAssessmentCompleteError ? <p className="form-error">{finalAssessmentCompleteError}</p> : null}
              <section className="assessment-drawer-section state-assessment-file-section">
                <h3>{showFinalAssessmentUpload ? "Final Assessment Pages" : "Assessment Pages"}</h3>
                <p className="state-assessment-file-help">
                  {showFinalAssessmentUpload
                    ? "Drag pages to set the order. New pages upload immediately and keep their storage file."
                    : stateAssessmentPages.length
                      ? "The uploaded assessment document is shown below."
                      : "Pages upload immediately. Drag pages to set the order, then extract the assessment data."}
                </p>
                <div className="assessment-uploaded-doc-list state-assessment-page-list">
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={reorderStateAssessmentPages}>
                    <SortableContext items={stateAssessmentPages.map((page) => page.id)} strategy={verticalListSortingStrategy}>
                      {stateAssessmentPages.map((page, index) => (
                        <SortableStateAssessmentPageRow
                          index={index}
                          key={page.id}
                          onDescriptionChange={updateStateAssessmentPageDescription}
                          onPreview={openFilePreview}
                          onRemove={removeStateAssessmentPage}
                          page={page}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                  {!stateAssessmentPages.length ? (
                    <div className="assessment-empty-state">No assessment pages added yet.</div>
                  ) : null}
                </div>
              </section>
            </section>
            <div className="state-assessment-main-column">
              <div className="state-assessment-details-grid">
                {!showFinalAssessmentUpload ? (
                  <section className="assessment-drawer-section state-assessment-upload-details">
                    <h3>Process Details</h3>
                    <div className="assessment-detail-grid">
                      <div><span>Resident</span><strong>{residentDisplayName}</strong></div>
                      <div><span>Assessment type</span><strong>{patientType === "NEW" ? "Initial state assessment" : "Yearly state assessment"}</strong></div>
                      <div><span>Assessment date</span><strong>{formatDate(assessment.assessmentDate || assessment.processStartDate)}</strong></div>
                      <div><span>Status / Pages</span><strong>{stateAssessmentNotes(assessment, stateAssessmentPages.length)}</strong></div>
                    </div>
                  </section>
                ) : null}
                {!showFinalAssessmentUpload ? (
                  <section className="assessment-drawer-section state-assessment-bio-form-section">
                    <div className="assessment-section-heading-row assessment-section-heading-row--actions">
                      <h3>Resident Bio Data</h3>
                      <div className="assessment-section-actions">
                        <button
                          className="btn primary state-assessment-upload-button"
                          disabled={stateAssessmentBioSaving}
                          onClick={saveStateAssessmentBioDetails}
                          type="button"
                        >
                          {stateAssessmentBioSaving ? (
                            <><i className="ti ti-loader-2" /> Saving...</>
                          ) : (
                            <><i className="ti ti-device-floppy" /> Save bio data</>
                          )}
                        </button>
                      </div>
                    </div>
                    {stateAssessmentBioError ? <p className="form-error">{stateAssessmentBioError}</p> : null}
                    {stateAssessmentBioSuccess ? <SavedNotice>{stateAssessmentBioSuccess}</SavedNotice> : null}
                    <div className="state-assessment-bio-form-grid">
                      <div className="state-assessment-bio-column state-assessment-bio-data-column">
                        <section className="state-assessment-bio-card tone-green">
                          <h4>Resident Identifiers</h4>
                          <div className="resident-form-grid compact">
                            {stateAssessmentBioFields.map(([name, label, type]) => (
                              <label className="resident-form-field" key={name}>
                                <span>{label}</span>
                                {name === "socialSecurityNumber" ? (
                                  <div className="resident-sensitive-input">
                                    <input
                                      autoComplete="off"
                                      inputMode="numeric"
                                      maxLength={11}
                                      name={name}
                                      type={stateAssessmentShowSsn ? "text" : "password"}
                                      value={stateAssessmentBioForm[name] || ""}
                                      onChange={(event) => updateStateAssessmentBioField(name, formatStateAssessmentSsn(event.target.value))}
                                    />
                                    <button
                                      aria-label={stateAssessmentShowSsn ? "Hide Social Security Number" : "Show Social Security Number"}
                                      onClick={() => setStateAssessmentShowSsn((current) => !current)}
                                      type="button"
                                    >
                                      <i className={`ti ${stateAssessmentShowSsn ? "ti-eye-off" : "ti-eye"}`} />
                                    </button>
                                  </div>
                                ) : (
                                  <input
                                    name={name}
                                    type={type}
                                    value={stateAssessmentBioForm[name] || ""}
                                    onChange={(event) => updateStateAssessmentBioField(name, event.target.value)}
                                  />
                                )}
                              </label>
                            ))}
                            <label className="resident-form-field">
                              <span>Sex</span>
                              <select value={stateAssessmentBioForm.sex || ""} onChange={(event) => updateStateAssessmentBioField("sex", event.target.value)}>
                                <option value="">Select sex</option>
                                {stateAssessmentSexOptions.map((option) => (
                                  <option value={option} key={option}>{option}</option>
                                ))}
                              </select>
                            </label>
                            <label className="resident-form-field">
                              <span>Primary language</span>
                              <input value={stateAssessmentBioForm.primaryLanguage || ""} onChange={(event) => updateStateAssessmentBioField("primaryLanguage", event.target.value)} />
                            </label>
                            <label className="resident-form-field">
                              <span>Race</span>
                              <select value={stateAssessmentBioForm.race || ""} onChange={(event) => updateStateAssessmentBioField("race", event.target.value)}>
                                <option value="">Select race</option>
                                {stateAssessmentRaceOptions.map((option) => <option value={option} key={option}>{option}</option>)}
                              </select>
                            </label>
                            {stateAssessmentBioForm.race === "Other" ? <label className="resident-form-field"><span>Specify race</span><input value={stateAssessmentBioForm.raceOther || ""} onChange={(event) => updateStateAssessmentBioField("raceOther", event.target.value)} /></label> : null}
                            <label className="resident-form-field"><span>Marital status</span><select value={stateAssessmentBioForm.maritalStatus || ""} onChange={(event) => updateStateAssessmentBioField("maritalStatus", event.target.value)}><option value="">Select marital status</option>{preliminaryMaritalStatusOptions.map((option) => <option value={option} key={option}>{option}</option>)}</select></label>
                            <label className="resident-form-field">
                              <span>Height (ft-in)</span>
                              <span className="resident-height-fields">
                                <input aria-label="Height feet" min="1" max="9" placeholder="ft" type="number" value={stateAssessmentBioForm.heightFeet ?? ""} onChange={(event) => updateStateAssessmentBioField("heightFeet", event.target.value)} />
                                <input aria-label="Height inches" min="0" max="11" placeholder="in" type="number" value={stateAssessmentBioForm.heightInches ?? ""} onChange={(event) => updateStateAssessmentBioField("heightInches", event.target.value)} />
                              </span>
                            </label>
                            <label className="resident-form-field">
                              <span>Weight (lbs)</span>
                              <input min="1" step="0.1" type="number" value={stateAssessmentBioForm.weightLbs ?? ""} onChange={(event) => updateStateAssessmentBioField("weightLbs", event.target.value)} />
                            </label>
                            <label className="resident-form-field"><span>Place of Birth</span><input value={stateAssessmentBioForm.placeOfBirth || ""} onChange={(event) => updateStateAssessmentBioField("placeOfBirth", event.target.value)} /></label>
                            <label className="resident-form-field"><span>Religion</span><input value={stateAssessmentBioForm.religion || ""} onChange={(event) => updateStateAssessmentBioField("religion", event.target.value)} /></label>
                            <label className="resident-form-field"><span>Move-in Date</span><input type="date" value={stateAssessmentBioForm.moveInDate || ""} onChange={(event) => updateStateAssessmentBioField("moveInDate", event.target.value)} /></label>
                          </div>
                        </section>

                        <section className="state-assessment-bio-card tone-blue">
                          <h4>Status and Coverage</h4>
                          <div className="resident-form-grid compact">
                            <label className="resident-form-field">
                              <span>Code Status</span>
                              <select value={stateAssessmentBioForm.codeStatus || ""} onChange={(event) => updateStateAssessmentBioField("codeStatus", event.target.value)}>
                                <option value="">Select code status</option>
                                {stateAssessmentCodeStatusOptions.map((option) => (
                                  <option value={option} key={option}>{option}</option>
                                ))}
                              </select>
                            </label>
                            <label className="resident-form-field">
                              <span>Advanced Directives</span>
                              <select value={stateAssessmentBioForm.advancedDirectives || ""} onChange={(event) => updateStateAssessmentBioField("advancedDirectives", event.target.value)}>
                                <option value="">Select option</option>
                                {stateAssessmentYesNoOptions.map((option) => (
                                  <option value={option} key={option}>{option}</option>
                                ))}
                              </select>
                            </label>
                            <label className="resident-form-field">
                              <span>Medicare Number</span>
                              <input value={stateAssessmentBioForm.medicareNumber || ""} onChange={(event) => updateStateAssessmentBioField("medicareNumber", event.target.value)} />
                            </label>
                            <label className="resident-form-field">
                              <span>Medicaid Number</span>
                              <input value={stateAssessmentBioForm.medicaidNumber || ""} onChange={(event) => updateStateAssessmentBioField("medicaidNumber", event.target.value)} />
                            </label>
                            <label className="resident-form-field">
                              <span>Do you have other Supplementary Insurance?</span>
                              <select value={stateAssessmentBioForm.supplementaryInsurances || ""} onChange={(event) => updateStateAssessmentBioField("supplementaryInsurances", event.target.value)}>
                                <option value="">Select option</option>
                                {stateAssessmentYesNoOptions.map((option) => (
                                  <option value={option} key={option}>{option}</option>
                                ))}
                              </select>
                            </label>
                          </div>
                        </section>

                        <section className="state-assessment-bio-card tone-yellow">
                          <h4>Resident Contact Information</h4>
                          <div className="resident-form-grid compact">
                            {stateAssessmentPhoneFields.map(([name, label]) => (
                              <label className="resident-form-field" key={name}>
                                <span>{label}</span>
                                <input type="tel" value={stateAssessmentBioForm[name] || ""} onChange={(event) => updateStateAssessmentBioField(name, event.target.value)} />
                              </label>
                            ))}
                            <label className="resident-form-field">
                              <span>Preferred phone</span>
                              <select value={stateAssessmentBioForm.preferredPhone || ""} onChange={(event) => updateStateAssessmentBioField("preferredPhone", event.target.value)}>
                                <option value="">Select preferred</option>
                                {stateAssessmentPreferredPhoneOptions.map(([value, label]) => (
                                  <option value={value} key={value}>{label}</option>
                                ))}
                              </select>
                            </label>
                            <label className="resident-form-field">
                              <span>Email</span>
                              <input type="email" value={stateAssessmentBioForm.email || ""} onChange={(event) => updateStateAssessmentBioField("email", event.target.value)} />
                            </label>
                            <label className="resident-form-field full-span resident-address-search">
                              <span>Location search</span>
                              {appConfigLoading && !googleMapsApiKey ? (
                                <input type="text" disabled placeholder="Loading map address search..." />
                              ) : (
                                <GooglePlacesAutocompleteInput
                                  googleMapsApiKey={googleMapsApiKey}
                                  placeholder={googleMapsApiKey ? "Search address e.g. 100 Main St, Seattle" : "Enter address"}
                                  types={["address"]}
                                  value={stateAssessmentBioForm.addressSearch || stateAssessmentBioForm.address || ""}
                                  onChange={(value) => {
                                    updateStateAssessmentBioField("addressSearch", value);
                                    if (!googleMapsApiKey) updateStateAssessmentBioField("address", value);
                                  }}
                                  onResolvePlace={async ({ details, prediction }) => {
                                    const resolvedAddress = details?.formatted_address || prediction?.description || stateAssessmentBioForm.addressSearch || "";
                                    updateStateAssessmentBioField("addressSearch", resolvedAddress);
                                    updateStateAssessmentBioField("address", resolvedAddress);
                                    if (details) {
                                      const fields = addressFieldsFromPlaceDetails(details);
                                      updateStateAssessmentBioField("address", fields.addressLine1 || resolvedAddress);
                                      updateStateAssessmentBioField("city", fields.city || "");
                                      updateStateAssessmentBioField("county", fields.county || "");
                                      updateStateAssessmentBioField("state", fields.state || "");
                                      updateStateAssessmentBioField("country", fields.country || "");
                                      updateStateAssessmentBioField("zip", fields.zipCode || "");
                                      updateStateAssessmentBioField("latitude", fields.latitude || "");
                                      updateStateAssessmentBioField("longitude", fields.longitude || "");
                                      updateStateAssessmentBioField("placeId", fields.placeId || "");
                                    }
                                  }}
                                />
                              )}
                            </label>
                            <label className="resident-form-field">
                              <span>Address type</span>
                              <select value={stateAssessmentBioForm.addressType || ""} onChange={(event) => updateStateAssessmentBioField("addressType", event.target.value)}>
                                <option value="">Select address type</option>
                                {addressTypeOptions.map((option) => (
                                  <option value={option} key={option}>{option}</option>
                                ))}
                              </select>
                            </label>
                            {stateAssessmentBioForm.addressType === "Other" ? (
                              <label className="resident-form-field">
                                <span>Specify address type</span>
                                <input value={stateAssessmentBioForm.addressTypeOther || ""} onChange={(event) => updateStateAssessmentBioField("addressTypeOther", event.target.value)} />
                              </label>
                            ) : null}
                            <label className="resident-form-field">
                              <span>Address line 1</span>
                              <input value={stateAssessmentBioForm.address || ""} onChange={(event) => updateStateAssessmentBioField("address", event.target.value)} />
                            </label>
                            <label className="resident-form-field">
                              <span>Address line 2</span>
                              <input value={stateAssessmentBioForm.addressLine2 || ""} onChange={(event) => updateStateAssessmentBioField("addressLine2", event.target.value)} />
                            </label>
                            <label className="resident-form-field">
                              <span>City</span>
                              <input value={stateAssessmentBioForm.city || ""} onChange={(event) => updateStateAssessmentBioField("city", event.target.value)} />
                            </label>
                            <label className="resident-form-field">
                              <span>County</span>
                              <input value={stateAssessmentBioForm.county || ""} onChange={(event) => updateStateAssessmentBioField("county", event.target.value)} />
                            </label>
                            <label className="resident-form-field">
                              <span>State</span>
                              <input value={stateAssessmentBioForm.state || ""} onChange={(event) => updateStateAssessmentBioField("state", event.target.value)} />
                            </label>
                            <label className="resident-form-field">
                              <span>Zip code</span>
                              <input value={stateAssessmentBioForm.zip || ""} onChange={(event) => updateStateAssessmentBioField("zip", event.target.value)} />
                            </label>
                            <label className="resident-form-field">
                              <span>Country</span>
                              <input value={stateAssessmentBioForm.country || ""} onChange={(event) => updateStateAssessmentBioField("country", event.target.value)} />
                            </label>
                          </div>
                        </section>

                        <section className="state-assessment-bio-card tone-white state-assessment-bio-notes">
                          <h4>Background Notes</h4>
                          <CareRichTextEditor
                            height={220}
                            onChange={(value) => updateStateAssessmentBioField("backgroundNotes", value)}
                            value={stateAssessmentBioForm.backgroundNotes || ""}
                          />
                        </section>
                      </div>

                      <div className="state-assessment-bio-column state-assessment-contact-column">
                        <div className="resident-contact-toolbar state-assessment-contact-toolbar">
                          <span>
                            <strong>Collateral Contacts</strong>
                            <small>Add emergency contacts, care coordinators, family, guardian, PoA, providers, pharmacy, and specialists.</small>
                          </span>
                          <button className="btn primary" type="button" onClick={addStateAssessmentContact}>
                            <i className="ti ti-plus" /> Add Contact
                          </button>
                        </div>
                        <section className="state-assessment-bio-card tone-blue state-assessment-contact-section">
                          <div className="state-assessment-contact-list">
                            {stateAssessmentContacts.map((contact, index) => (
                              <article className="state-assessment-contact-card" key={contact.id}>
                                <div className="state-assessment-contact-card-title">
                                  <strong>Contact {index + 1}</strong>
                                  <button aria-label="Remove contact" type="button" onClick={() => removeStateAssessmentContact(contact.id)}>
                                    <i className="ti ti-trash" />
                                  </button>
                                </div>
                                <div className="resident-form-grid compact">
                                  <label className="resident-form-field">
                                    <span>Relationship</span>
                                    <select value={contact.relationship || ""} onChange={(event) => updateStateAssessmentContact(contact.id, "relationship", event.target.value)}>
                                      <option value="">Select relationship</option>
                                      {stateAssessmentContactRelationships.map((option) => (
                                        <option value={option} key={option}>{option}</option>
                                      ))}
                                    </select>
                                  </label>
                                  {contact.relationship === "Other" ? (
                                    <label className="resident-form-field">
                                      <span>Specify relationship</span>
                                      <input value={contact.relationshipOther || ""} onChange={(event) => updateStateAssessmentContact(contact.id, "relationshipOther", event.target.value)} />
                                    </label>
                                  ) : null}
                                  <label className="resident-form-field">
                                    <span>Name</span>
                                    <input value={contact.name || ""} onChange={(event) => updateStateAssessmentContact(contact.id, "name", event.target.value)} />
                                  </label>
                                  <label className="resident-form-field">
                                    <span>Organization</span>
                                    <input value={contact.organizationName || ""} onChange={(event) => updateStateAssessmentContact(contact.id, "organizationName", event.target.value)} />
                                  </label>
                                  {stateAssessmentPhoneFields.map(([name, label]) => (
                                    <label className="resident-form-field" key={name}>
                                      <span>{label}</span>
                                      <input type="tel" value={contact[name] || ""} onChange={(event) => updateStateAssessmentContact(contact.id, name, event.target.value)} />
                                    </label>
                                  ))}
                                  <label className="resident-form-field">
                                    <span>Preferred phone</span>
                                    <select value={contact.preferredPhone || ""} onChange={(event) => updateStateAssessmentContact(contact.id, "preferredPhone", event.target.value)}>
                                      <option value="">Select preferred</option>
                                      {stateAssessmentPreferredPhoneOptions.map(([value, label]) => (
                                        <option value={value} key={value}>{label}</option>
                                      ))}
                                    </select>
                                  </label>
                                  <label className="resident-form-field">
                                    <span>Email</span>
                                    <input type="email" value={contact.email || ""} onChange={(event) => updateStateAssessmentContact(contact.id, "email", event.target.value)} />
                                  </label>
                                  <label className="resident-form-field full-span resident-address-search">
                                    <span>Location search</span>
                                    {appConfigLoading && !googleMapsApiKey ? (
                                      <input type="text" disabled placeholder="Loading map address search..." />
                                    ) : (
                                      <GooglePlacesAutocompleteInput
                                        googleMapsApiKey={googleMapsApiKey}
                                        placeholder={googleMapsApiKey ? "Search address e.g. 100 Main St, Seattle" : "Enter address"}
                                        types={["address"]}
                                        value={contact.addressSearch || contact.address || ""}
                                        onChange={(value) => {
                                          updateStateAssessmentContact(contact.id, "addressSearch", value);
                                          if (!googleMapsApiKey) updateStateAssessmentContact(contact.id, "address", value);
                                        }}
                                        onResolvePlace={async ({ details, prediction }) => {
                                          const resolvedAddress = details?.formatted_address || prediction?.description || contact.addressSearch || "";
                                          updateStateAssessmentContact(contact.id, "addressSearch", resolvedAddress);
                                          updateStateAssessmentContact(contact.id, "address", resolvedAddress);
                                          if (details) {
                                            const fields = addressFieldsFromPlaceDetails(details);
                                            updateStateAssessmentContact(contact.id, "address", fields.addressLine1 || resolvedAddress);
                                            updateStateAssessmentContact(contact.id, "city", fields.city || "");
                                            updateStateAssessmentContact(contact.id, "county", fields.county || "");
                                            updateStateAssessmentContact(contact.id, "state", fields.state || "");
                                            updateStateAssessmentContact(contact.id, "country", fields.country || "");
                                            updateStateAssessmentContact(contact.id, "zip", fields.zipCode || "");
                                            updateStateAssessmentContact(contact.id, "latitude", fields.latitude || "");
                                            updateStateAssessmentContact(contact.id, "longitude", fields.longitude || "");
                                            updateStateAssessmentContact(contact.id, "placeId", fields.placeId || "");
                                          }
                                        }}
                                      />
                                    )}
                                  </label>
                                  <label className="resident-form-field">
                                    <span>Address type</span>
                                    <select value={contact.addressType || ""} onChange={(event) => updateStateAssessmentContact(contact.id, "addressType", event.target.value)}>
                                      <option value="">Select address type</option>
                                      {addressTypeOptions.map((option) => (
                                        <option value={option} key={option}>{option}</option>
                                      ))}
                                    </select>
                                  </label>
                                  {contact.addressType === "Other" ? (
                                    <label className="resident-form-field">
                                      <span>Specify address type</span>
                                      <input value={contact.addressTypeOther || ""} onChange={(event) => updateStateAssessmentContact(contact.id, "addressTypeOther", event.target.value)} />
                                    </label>
                                  ) : null}
                                  <label className="resident-form-field">
                                    <span>Address line 1</span>
                                    <input value={contact.address || ""} onChange={(event) => updateStateAssessmentContact(contact.id, "address", event.target.value)} />
                                  </label>
                                  <label className="resident-form-field">
                                    <span>Address line 2</span>
                                    <input value={contact.addressLine2 || ""} onChange={(event) => updateStateAssessmentContact(contact.id, "addressLine2", event.target.value)} />
                                  </label>
                                  <label className="resident-form-field">
                                    <span>City</span>
                                    <input value={contact.city || ""} onChange={(event) => updateStateAssessmentContact(contact.id, "city", event.target.value)} />
                                  </label>
                                  <label className="resident-form-field">
                                    <span>County</span>
                                    <input value={contact.county || ""} onChange={(event) => updateStateAssessmentContact(contact.id, "county", event.target.value)} />
                                  </label>
                                  <label className="resident-form-field">
                                    <span>State</span>
                                    <input value={contact.state || ""} onChange={(event) => updateStateAssessmentContact(contact.id, "state", event.target.value)} />
                                  </label>
                                  <label className="resident-form-field">
                                    <span>Zip code</span>
                                    <input value={contact.zip || ""} onChange={(event) => updateStateAssessmentContact(contact.id, "zip", event.target.value)} />
                                  </label>
                                  <label className="resident-form-field">
                                    <span>Country</span>
                                    <input value={contact.country || ""} onChange={(event) => updateStateAssessmentContact(contact.id, "country", event.target.value)} />
                                  </label>
                                  <label className="resident-form-field full-span">
                                    <span>Notes</span>
                                    <textarea rows={2} value={contact.notes || ""} onChange={(event) => updateStateAssessmentContact(contact.id, "notes", event.target.value)} />
                                  </label>
                                </div>
                              </article>
                            ))}
                            {!stateAssessmentContacts.length ? (
                              <div className="assessment-empty-state">No contacts added yet.</div>
                            ) : null}
                          </div>
                        </section>
                      </div>
                    </div>
                  </section>
                ) : null}
                {showFinalAssessmentUpload ? (
                  <section className="assessment-drawer-section state-assessment-upload-details">
                    <h3>Final Assessment Details</h3>
                    <div className="assessment-detail-grid">
                      <div><span>Resident</span><strong>{residentDisplayName}</strong></div>
                      <div><span>Assessment type</span><strong>Final assessment update</strong></div>
                      <div><span>Assessment date</span><strong>{formatDate(assessment.processEndDate || assessment.endDate)}</strong></div>
                      <div className="wide"><span>Notes</span><strong>Upload final assessment pages, state approval files, and confirmation notes prepared after NCP review.</strong></div>
                    </div>
                  </section>
                ) : null}
                {showFinalAssessmentUpload ? (
                  <section className="assessment-drawer-section state-assessment-approval">
                    <h3>Approval Data</h3>
                    <div className="resident-form-grid compact">
                      <label className="ncp-form-field">
                        <span>Status</span>
                        <select value={finalAssessmentApproval.approvalStatus} onChange={(event) => updateFinalAssessmentApproval("approvalStatus", event.target.value)}>
                          <option value="Draft">Draft</option>
                          <option value="Submitted">Submitted</option>
                          <option value="Approved">Approved</option>
                          <option value="Returned for updates">Returned for updates</option>
                          <option value="Confirmed by state">Confirmed by state</option>
                        </select>
                      </label>
                      <label className="ncp-form-field">
                        <span>Approved by</span>
                        <input value={finalAssessmentApproval.approvedBy} onChange={(event) => updateFinalAssessmentApproval("approvedBy", event.target.value)} placeholder="Approver name" />
                      </label>
                      <label className="ncp-form-field">
                        <span>Approved at</span>
                        <input type="date" value={finalAssessmentApproval.approvedAt} onChange={(event) => updateFinalAssessmentApproval("approvedAt", event.target.value)} />
                      </label>
                      <label className="ncp-form-field">
                        <span>Confirmation reference</span>
                        <input value={finalAssessmentApproval.stateConfirmationReference} onChange={(event) => updateFinalAssessmentApproval("stateConfirmationReference", event.target.value)} placeholder="State confirmation or document reference" />
                      </label>
                      <label className="ncp-form-field ncp-form-field-wide">
                        <span>Approval notes</span>
                        <textarea rows={4} value={finalAssessmentApproval.approvalNotes} onChange={(event) => updateFinalAssessmentApproval("approvalNotes", event.target.value)} placeholder="Capture approval details, required updates, and confirmation notes." />
                      </label>
                    </div>
                  </section>
                ) : null}
              </div>
            </div>
          </div>
        </Panel>
      ) : null}

      {showMeetAndGreetForm ? (
        <Panel title="Meet and Greet Details" actionNode={stageCompleteAction()}>
          <div className="meet-greet-process-form">
            <div className="meet-greet-process-main">
              <section className="assessment-drawer-section">
                <h3>Meeting Details</h3>
                <div className="resident-form-grid compact">
                  <label className="ncp-form-field">
                    <span>Date</span>
                    <input
                      type="date"
                      value={meetAndGreetForm.meetingDate}
                      onChange={(event) => updateMeetAndGreetForm("meetingDate", event.target.value)}
                    />
                  </label>
                  <label className="ncp-form-field">
                    <span>Meeting location</span>
                    {appConfigLoading && !googleMapsApiKey ? (
                      <input type="text" disabled placeholder="Loading location search..." />
                    ) : (
                      <GooglePlacesAutocompleteInput
                        googleMapsApiKey={googleMapsApiKey}
                        placeholder={googleMapsApiKey ? "Search or enter meeting location" : "Enter meeting location"}
                        value={meetAndGreetForm.location}
                        onChange={(value) => updateMeetAndGreetForm("location", value)}
                        onResolvePlace={({ details, prediction }) => {
                          updateMeetAndGreetForm(
                            "location",
                            details?.formatted_address || prediction?.description || meetAndGreetForm.location,
                          );
                        }}
                      />
                    )}
                  </label>
                  <label className="ncp-form-field">
                    <span>Facilitator</span>
                    <select
                      value={meetAndGreetForm.facilitator}
                      onChange={(event) => {
                        const option = facilitatorOptions.find((item) => item.value === event.target.value);
                        setMeetAndGreetForm((current) => ({
                          ...current,
                          facilitator: event.target.value,
                          facilitatorUserId: option?.userId || "",
                        }));
                      }}
                      disabled={workspaceMembersLoading && !facilitatorOptions.length}
                    >
                      <option value="">
                        {workspaceMembersLoading ? "Loading workspace members..." : "Select facilitator"}
                      </option>
                      {facilitatorOptions.map((option) => (
                        <option value={option.value} key={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </section>

              <section className="assessment-drawer-section">
                <div className="assessment-section-heading-row">
                  <h3>Collateral Contacts Present</h3>
                  <button className="mini-btn" type="button" onClick={() => addMeetAndGreetPerson(setMeetAndGreetCareCircle)}>
                    <i className="ti ti-plus" /> Add contact
                  </button>
                </div>
                <div className="meet-greet-person-list">
                  {meetAndGreetCareCircle.map((person, index) => {
                    const rowOptions = meetAndGreetOptionsWithCurrent(collateralContactOptions, person, index, "collateral-contact");
                    return (
                      <div className="meet-greet-person-row" key={`care-circle-${index}`}>
                        <select
                          value={selectedMeetAndGreetPersonValue(person, rowOptions)}
                          onChange={(event) => {
                            const option = rowOptions.find((item) => item.value === event.target.value);
                            selectMeetAndGreetPerson(setMeetAndGreetCareCircle, index, option);
                          }}
                        >
                          <option value="">Select collateral contact</option>
                          {rowOptions.map((option) => (
                            <option value={option.value} key={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <input value={person.relationship || person.relationshipOther || ""} readOnly placeholder="Relationship" />
                        <button type="button" onClick={() => removeMeetAndGreetPerson(setMeetAndGreetCareCircle, index)} aria-label="Remove contact">
                          <i className="ti ti-trash" />
                        </button>
                      </div>
                    );
                  })}
                  {!meetAndGreetCareCircle.length ? <p className="assessment-empty-state">No collateral contacts added yet.</p> : null}
                  {!collateralContactOptions.length ? <p className="assessment-empty-state">No collateral contacts are available for this resident.</p> : null}
                </div>
              </section>

              <section className="assessment-drawer-section">
                <div className="assessment-section-heading-row">
                  <h3>Care Provider Staff Present</h3>
                  <button className="mini-btn" type="button" onClick={() => addMeetAndGreetPerson(setMeetAndGreetStaff, { relationship: "Facility Staff" })}>
                    <i className="ti ti-plus" /> Add staff
                  </button>
                </div>
                <div className="meet-greet-person-list">
                  {meetAndGreetStaff.map((person, index) => {
                    const rowOptions = meetAndGreetOptionsWithCurrent(workspaceStaffOptions, person, index, "workspace-staff", "Facility Staff");
                    return (
                      <div className="meet-greet-person-row" key={`staff-${index}`}>
                        <select
                          value={selectedMeetAndGreetPersonValue(person, rowOptions)}
                          onChange={(event) => {
                            const option = rowOptions.find((item) => item.value === event.target.value);
                            selectMeetAndGreetPerson(setMeetAndGreetStaff, index, option, "Facility Staff");
                          }}
                          disabled={workspaceMembersLoading && !rowOptions.length}
                        >
                          <option value="">
                            {workspaceMembersLoading ? "Loading workspace members..." : "Select staff attendee"}
                          </option>
                          {rowOptions.map((option) => (
                            <option value={option.value} key={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <input value={person.relationship || "Facility Staff"} readOnly placeholder="Role" />
                        <button type="button" onClick={() => removeMeetAndGreetPerson(setMeetAndGreetStaff, index)} aria-label="Remove staff">
                          <i className="ti ti-trash" />
                        </button>
                      </div>
                    );
                  })}
                  {!meetAndGreetStaff.length ? <p className="assessment-empty-state">No provider staff added yet.</p> : null}
                  {!workspaceMembersLoading && !workspaceStaffOptions.length ? <p className="assessment-empty-state">No facility staff are available.</p> : null}
                </div>
              </section>

              <section className="assessment-drawer-section">
                <div className="assessment-section-heading-row">
                  <h3>Questions and Responses</h3>
                  <span>{meetAndGreetQuestions.length + meetAndGreetCustomQuestions.length} questions</span>
                </div>
                <div className="meet-greet-question-list">
                  {[...meetAndGreetQuestions, ...meetAndGreetCustomQuestions].map((question) => (
                    <label className="ncp-form-field ncp-form-field-wide" key={question}>
                      <span>{question}</span>
                      <textarea
                        rows={2}
                        value={meetAndGreetResponses[question] || ""}
                        onChange={(event) => setMeetAndGreetResponses((current) => ({ ...current, [question]: event.target.value }))}
                      />
                    </label>
                  ))}
                </div>
                <div className="assessment-custom-question">
                  <input
                    value={meetAndGreetCustomQuestion}
                    placeholder="Add custom question"
                    onChange={(event) => setMeetAndGreetCustomQuestion(event.target.value)}
                  />
                  <button className="mini-btn" type="button" onClick={addMeetAndGreetCustomQuestion}>
                    <i className="ti ti-plus" /> Add question
                  </button>
                </div>
              </section>
            </div>

            <aside className="assessment-drawer-section meet-greet-process-notes">
              <h3>Notes</h3>
              <CareRichTextEditor
                height={360}
                onChange={(value) => updateMeetAndGreetForm("notes", value)}
                value={meetAndGreetForm.notes}
              />
              {meetAndGreetError ? <p className="form-error">{meetAndGreetError}</p> : null}
              {meetAndGreetSuccess ? <SavedNotice>{meetAndGreetSuccess}</SavedNotice> : null}
              <div className="assessment-drawer-actions">
                <button className="btn secondary" type="button" disabled={meetAndGreetSaving} onClick={() => setSelectedStageKey("")}>
                  <i className="ti ti-x" /> Cancel
                </button>
                <button className="btn primary" type="button" disabled={meetAndGreetSaving} onClick={saveMeetAndGreet}>
                  {meetAndGreetSaving ? <><i className="ti ti-loader-2" /> Saving...</> : <><i className="ti ti-device-floppy" /> Save Meet and Greet</>}
                </button>
              </div>
            </aside>
          </div>
        </Panel>
      ) : null}

      {showProviderReview ? (
        <Panel title="Provider Review" actionNode={providerDecisionAction()}>
          <div className="provider-review-workspace">
            <section className="assessment-drawer-section provider-review-decision">
              <div>
                <h3>Provider Decision</h3>
                <p>Review the state assessment pages and Meet and Greet data, then decide whether to take on this prospective resident.</p>
                {assessment.providerDecision ? (
                  <strong className={`provider-current-decision ${providerDecisionKey === "DECLINED" ? "declined" : providerDecisionKey === "ACCEPTED" ? "accepted" : ""}`}>
                    Current decision: {String(assessment.providerDecision).replace(/_/g, " ")} {assessment.providerDecisionDate ? `on ${formatDate(assessment.providerDecisionDate)}` : ""}
                  </strong>
                ) : null}
                {assessment.providerDecisionNotes ? <p>{assessment.providerDecisionNotes}</p> : null}
              </div>
              {providerReviewError ? <p className="form-error">{providerReviewError}</p> : null}
              {providerReviewSuccess ? <SavedNotice>{providerReviewSuccess}</SavedNotice> : null}
            </section>

            <section className="assessment-drawer-section provider-review-pages">
              <div className="assessment-section-heading-row">
                <h3>Uploaded Assessment Pages</h3>
                <span>{reviewAssessmentPages.length} pages</span>
              </div>
              <div className="provider-review-page-list">
                {reviewAssessmentPages.map((page, index) => (
                  <button className="provider-review-page-tile" type="button" key={page.id || `${assessmentFileName(page)}-${index}`} onClick={() => openFilePreview(page)}>
                    <span className={`assessment-attachment-thumb ${isImageAssessmentFile(page) ? "photo" : "document"}`}>
                      {assessmentThumbnail(page)}
                    </span>
                    <strong>{assessmentPageTitle(page, index)}</strong>
                    <small>{assessmentSourceName(page)}</small>
                  </button>
                ))}
                {!reviewAssessmentPages.length ? <p className="assessment-empty-state">No state assessment pages have been uploaded yet.</p> : null}
              </div>
            </section>

            <div className="provider-review-summary-grid">
              <section className="assessment-drawer-section">
                <h3>Prospective Resident Details</h3>
                <div className="assessment-detail-grid">
                  <div><span>Name</span><strong>{processResidentName(assessment)}</strong></div>
                  <div><span>Resident type</span><strong>{patientType === "NEW" ? "New Resident" : "Current Resident"}</strong></div>
                  <div><span>Assessment type</span><strong>{assessment.type || "Not set"}</strong></div>
                  <div><span>Status</span><strong><em className={`pill ${processStatusClass(assessmentWorkflowStatus)}`}>{assessmentWorkflowStatus || "Open"}</em></strong></div>
                  <div><span>Start date</span><strong>{formatDate(assessment.startDate || assessment.processStartDate)}</strong></div>
                  <div><span>Assessment date</span><strong>{formatDate(assessment.assessmentDate)}</strong></div>
                  <div className="wide"><span>Assessment notes</span><strong>{stateAssessmentNotes(assessment, reviewAssessmentPages.length)}</strong></div>
                </div>
              </section>

              <section className="assessment-drawer-section">
                <h3>Social Worker</h3>
                <div className="assessment-detail-grid">
                  <div><span>Name</span><strong>{socialWorkerDetails.name}</strong></div>
                  <div><span>Relationship</span><strong>{socialWorkerDetails.relationship}</strong></div>
                  <div><span>Phone</span><strong>{socialWorkerDetails.phone}</strong></div>
                  <div><span>Email</span><strong>{socialWorkerDetails.email}</strong></div>
                  <div><span>Agency</span><strong>{socialWorkerDetails.agency}</strong></div>
                  <div><span>Source</span><strong>{socialWorkerDetails.source}</strong></div>
                  <div className="wide"><span>Address</span><strong>{socialWorkerDetails.address}</strong></div>
                </div>
              </section>
            </div>

            <section className="assessment-drawer-section">
              <div className="assessment-section-heading-row">
                <h3>Meet and Greet Summary</h3>
                <span>{meetAndGreetForm.meetingDate ? formatDate(meetAndGreetForm.meetingDate) : "Not dated"}</span>
              </div>
              <div className="provider-review-mag-grid">
                <div className="assessment-detail-grid">
                  <div><span>Date</span><strong>{formatDate(meetAndGreetForm.meetingDate)}</strong></div>
                  <div><span>Location</span><strong>{meetAndGreetForm.location || "Not set"}</strong></div>
                  <div><span>Facilitator</span><strong>{meetAndGreetForm.facilitator || "Not set"}</strong></div>
                </div>
                <div className="provider-review-list-block">
                  <h4>Care Circle</h4>
                  {reviewCareCircle.map((person, index) => (
                    <div key={`${person.name}-${index}`}><strong>{person.name || "Unnamed"}</strong><span>{person.relationship || "Relationship not set"}</span></div>
                  ))}
                  {!reviewCareCircle.length ? <p>No care circle members captured.</p> : null}
                </div>
                <div className="provider-review-list-block">
                  <h4>Care Provider Staff Present</h4>
                  {reviewProviderStaff.map((person, index) => (
                    <div key={`${person.name}-${index}`}><strong>{person.name || "Unnamed"}</strong><span>{person.relationship || "Role not set"}</span></div>
                  ))}
                  {!reviewProviderStaff.length ? <p>No care provider staff captured.</p> : null}
                </div>
              </div>
              <div className="provider-review-qa-list">
                {reviewQuestions.map((item) => (
                  <div key={item.question}>
                    <strong>{item.question}</strong>
                    <p>{item.response || "No response captured."}</p>
                  </div>
                ))}
              </div>
              <div className="provider-review-rich-text">
                <h4>Notes</h4>
                {meetAndGreetForm.notes ? (
                  <div className="assessment-record-notes" dangerouslySetInnerHTML={{ __html: meetAndGreetForm.notes }} />
                ) : (
                  <p>No Meet and Greet notes captured.</p>
                )}
              </div>
            </section>
          </div>
        </Panel>
      ) : null}

      {showOnboardingStage ? (
        <Panel title="Onboarding" actionNode={stageCompleteAction()}>
          {residentId && residentOnboardingPreviewLoaded ? (
            <NewResidentPage
              key={`onboarding-${extractedDocument ? "with-extraction" : "without-extraction"}`}
              embedded
              initialAssessmentBioData={stateAssessmentBioForm}
              initialAssessmentContacts={stateAssessmentContacts}
              initialAssessmentExtraction={extractedDocument}
              initialOnboardingPreview={residentOnboardingPreview}
              initialOnboardingPreviewLoaded={residentOnboardingPreviewLoaded}
              residentId={residentId}
              sourceAssessmentPages={reviewAssessmentPages}
            />
          ) : residentId ? (
            <div className="assessment-empty-state">Loading resident onboarding details...</div>
          ) : (
            <div className="assessment-empty-state">Resident details are required before onboarding can be loaded.</div>
          )}
        </Panel>
      ) : null}

      {showPreliminaryPlan ? (
        <Panel
          title="Preliminary Plan"
          actionNode={(
            <div className="assessment-drawer-action-links assessment-page-action-links probation-action-buttons preliminary-plan-actions">
              <button type="button" onClick={savePreliminaryPlan} disabled={preliminarySaving}>
                <i className="ti ti-device-floppy" /> {preliminarySaving ? "Saving..." : "Save"}
              </button>
              {stageCompleteAction()}
              <button className="danger-outline" type="button" onClick={() => setPreliminaryGenerationLocked(true)}>
                <i className="ti ti-lock" /> Prevent Further Generation
              </button>
              <button type="button" onClick={() => setPreliminaryPreviewOpen(true)}><i className="ti ti-eye" /> Preview</button>
              <button type="button" onClick={printPreliminaryPlan}><i className="ti ti-printer" /> Print</button>
            </div>
          )}
        >
          {preliminaryError ? <p className="form-error">{preliminaryError}</p> : null}
          {preliminarySuccess ? <SavedNotice>{preliminarySuccess}</SavedNotice> : null}
          <div className="preliminary-plan-grid">
            <div className="preliminary-plan-main">
              <div className="preliminary-plan-form-sections">
                {preliminaryPlanFieldSections.map((section) => (
                  <section className={`assessment-drawer-section preliminary-form-section tone-${section.tone}`} key={section.title}>
                    <div className="assessment-section-heading-row">
                      <h3>{section.title}</h3>
                      <span>{section.fields.length} fields</span>
                    </div>
                    <div className="preliminary-form-field-grid">
                      {section.fields.map((fieldConfig) => {
                        const field = preliminaryFieldKey(fieldConfig);
                        const label = preliminaryFieldLabel(fieldConfig);
                        const helper = preliminaryFieldHelper(fieldConfig);
                        const type = Array.isArray(fieldConfig) ? "textarea" : fieldConfig.type || "text";
                        const wide = type === "textarea" || fieldConfig.wide;
                        return (
                          <label className={`ncp-form-field preliminary-summary-field ${wide ? "ncp-form-field-wide" : ""}`} key={field}>
                            <span>{label}</span>
                            {type === "select" ? (
                              <select
                                value={preliminaryPlanForm[field] || ""}
                                onChange={(event) => updatePreliminaryPlanField(field, event.target.value)}
                              >
                                <option value="">Select</option>
                                {(fieldConfig.options || []).map((option) => (
                                  <option value={option} key={option}>{option}</option>
                                ))}
                              </select>
                            ) : type === "textarea" ? (
                              <textarea
                                rows={fieldConfig.rows || 3}
                                value={preliminaryPlanForm[field] || ""}
                                onChange={(event) => updatePreliminaryPlanField(field, event.target.value)}
                                placeholder={helper}
                              />
                            ) : (
                              <input
                                type={type}
                                value={preliminaryPlanForm[field] || ""}
                                onChange={(event) => updatePreliminaryPlanField(field, event.target.value)}
                                placeholder={helper}
                              />
                            )}
                            {helper ? <small>{helper}</small> : null}
                          </label>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>

              <section className="assessment-drawer-section preliminary-generated-section">
                <div className="assessment-section-heading-row">
                  <h3>Generated preliminary service plan summary</h3>
                  <span>{preliminaryGenerationLocked ? "Generation locked" : "Generated from form fields"}</span>
                </div>
                <div className="preliminary-ai-actions">
                  <button type="button" onClick={refinePreliminaryPlan}><i className="ti ti-wand" /> Refine</button>
                  <button type="button" onClick={summarizePreliminaryPlan}><i className="ti ti-list-details" /> Summarize</button>
                  <button type="button" onClick={formatPreliminaryPlan}><i className="ti ti-brush" /> Format</button>
                  <button type="button" disabled={!preliminaryPlanHistory.length} onClick={undoPreliminaryPlanAction}><i className="ti ti-arrow-back-up" /> Undo</button>
                </div>
                <CareRichTextEditor autoGrow height={260} onChange={setPreliminaryPlanDescription} value={preliminaryPlanDescription} />
              </section>
            </div>
            <aside className="preliminary-plan-side">
              <section className="assessment-drawer-section">
                <h3>Plan Metadata</h3>
                <div className="assessment-detail-grid">
                  <div><span>Latest generated</span><strong>{formatDate(new Date().toISOString())}</strong></div>
                  <div><span>Last updated</span><strong>{formatDate(new Date().toISOString())}</strong></div>
                  <div><span>Created by</span><strong>Provider</strong></div>
                  <div><span>Status</span><strong>{preliminaryGenerationLocked ? "Locked" : "Draft"}</strong></div>
                </div>
              </section>
              <section className="assessment-drawer-section">
                <div className="assessment-section-heading-row">
                  <h3>Attached Files</h3>
                  <label className="mini-btn preliminary-upload-button">
                    <input className="assessment-file-input" multiple onChange={addPreliminaryAttachedFiles} type="file" />
                    <i className="ti ti-paperclip" /> Add files
                  </label>
                </div>
                <div className="assessment-uploaded-doc-list">
                  {preliminaryAttachedFiles.map((file, index) => (
                    <div className="assessment-uploaded-doc-row preliminary-uploaded-file-row" key={`${file.name}-${file.addedAt || index}`}>
                      <i className={`ti ${String(file.type || "").startsWith("image/") ? "ti-photo" : "ti-file-description"}`} />
                      <div className="preliminary-uploaded-file-fields">
                        <label>
                          <span>Name</span>
                          <input value={file.name || ""} onChange={(event) => updatePreliminaryAttachedFile(index, "name", event.target.value)} />
                        </label>
                        <label>
                          <span>Caption</span>
                          <input value={file.caption || ""} onChange={(event) => updatePreliminaryAttachedFile(index, "caption", event.target.value)} />
                        </label>
                        <small className="preliminary-file-added-at">
                          <i className="ti ti-clock" /> Added {file.addedAt ? new Date(file.addedAt).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "just now"}
                        </small>
                      </div>
                      <button aria-label={`Remove ${file.name}`} type="button" onClick={() => removePreliminaryAttachedFile(index)}>
                        <i className="ti ti-trash" />
                      </button>
                    </div>
                  ))}
                  {!preliminaryAttachedFiles.length ? <div className="assessment-empty-state">No files attached yet.</div> : null}
                </div>
              </section>
            </aside>
          </div>
        </Panel>
      ) : null}

      {showProbationNotes ? (
        <Panel
          title={patientType === "RESIDENT" ? "Annual Progress Notes" : "30 Days Probation"}
          actionNode={(
            <div className="assessment-drawer-action-links assessment-page-action-links probation-action-buttons">
              {canManageDailyNotes ? (
                <button className="primary-action" type="button" onClick={openAddDailyNoteDrawer}><i className="ti ti-note" /> Add Daily Note</button>
              ) : null}
              <button type="button"><i className="ti ti-edit" /> Edit</button>
              {stageCompleteAction()}
            </div>
          )}
        >
          {probationError ? <p className="form-error">{probationError}</p> : null}
          <div className="probation-page-grid">
            <div className="probation-main-column">
              <section className="assessment-drawer-section">
                <h3>{patientType === "RESIDENT" ? "Annual Progress Period" : "Probation Period"}</h3>
                <div className="probation-summary-grid">
                  <div><span>Created at</span><strong>{formatDateTime(probationCreatedAtDisplay)}</strong></div>
                  <div><span>Start date</span><strong>{formatDate(probationStartDateDisplay)}</strong></div>
                  <div><span>Estimated end date</span><strong>{formatDate(probationEndDateDisplay)}</strong></div>
                  <div><span>Status</span><strong><em className="pill blue">{probationRecord?.status || assessment.status || "Active"}</em></strong></div>
                </div>
                <p className="probation-description">{probationRecord?.description || "Capture behavior, health, welfare, care updates, and discrepancies observed during this assessment period."}</p>
                {patientType === "RESIDENT" && probationRecord?.evaluationNotes ? (
                  <div className="annual-review-evaluation">
                    <h4>Overall Evaluation / Status Notes</h4>
                    <div className="assessment-record-notes" dangerouslySetInnerHTML={{ __html: probationRecord.evaluationNotes }} />
                  </div>
                ) : null}
              </section>

              <section className="assessment-drawer-section">
                <div className="assessment-section-heading-row">
                  <h3>Daily Notes</h3>
                  <div className="daily-note-view-actions">
                    <div className="daily-note-view-toggle" data-readonly-allowed="true" role="group" aria-label="Daily note view">
                      <button className={probationNotesView === "LIST" ? "active" : ""} type="button" onClick={() => setProbationNotesView("LIST")}>
                        <i className="ti ti-list" /> List
                      </button>
                      <button className={probationNotesView === "CALENDAR" ? "active" : ""} type="button" onClick={() => setProbationNotesView("CALENDAR")}>
                        <i className="ti ti-calendar" /> Calendar
                      </button>
                    </div>
                    <button className="mini-btn" data-readonly-allowed="true" type="button" onClick={openWeeklyDailyNoteReportsPage}>
                      <i className="ti ti-folder-open" /> View generated reports
                    </button>
                    {canManageDailyNotes ? (
                      <button className="mini-btn" type="button" onClick={() => openAddDailyNoteDrawer()}><i className="ti ti-plus" /> Add daily note</button>
                    ) : null}
                  </div>
                </div>
                <div className="weekly-report-controls">
                  <div className="weekly-report-controls-title">
                    <strong>Weekly report dates</strong>
                    <span>{weeklyReportRangeNotes.length} daily note{weeklyReportRangeNotes.length === 1 ? "" : "s"} in this range</span>
                  </div>
                  <label className="ncp-form-field">
                    <span>Report from</span>
                    <input
                      onChange={(event) => {
                        setWeeklyReportRange((current) => ({ ...current, start: event.target.value }));
                        setWeeklyDailyNoteReport(null);
                        setWeeklyDailyNoteReportEditing(false);
                        setWeeklyDailyNoteReportEditValues(null);
                        setWeeklyDailyNoteReportError("");
                      }}
                      type="date"
                      value={weeklyReportRange.start}
                    />
                  </label>
                  <label className="ncp-form-field">
                    <span>Report to</span>
                    <input
                      onChange={(event) => {
                        setWeeklyReportRange((current) => ({ ...current, end: event.target.value }));
                        setWeeklyDailyNoteReport(null);
                        setWeeklyDailyNoteReportEditing(false);
                        setWeeklyDailyNoteReportEditValues(null);
                        setWeeklyDailyNoteReportError("");
                      }}
                      type="date"
                      value={weeklyReportRange.end}
                    />
                  </label>
                  <button
                    className="weekly-report-generate-btn"
                    disabled={weeklyDailyNoteReportLoading}
                    type="button"
                    onClick={generateWeeklyDailyNoteReport}
                  >
                    <i className={`ti ${weeklyDailyNoteReportLoading ? "ti-loader-2" : "ti-file-analytics"}`} /> {weeklyDailyNoteReportLoading ? "Generating report..." : "Generate weekly report"}
                  </button>
                </div>
                {weeklyDailyNoteReportLoading ? (
                  <div className="weekly-report-generating" role="status">
                    <i className="ti ti-loader-2" />
                    <span>Generating the weekly report from the selected daily notes. This may take a moment.</span>
                  </div>
                ) : null}
                {weeklyDailyNoteReportError ? <p className="form-error">{weeklyDailyNoteReportError}</p> : null}
                {weeklyDailyNoteReport ? (
                  <div className="weekly-daily-note-report">
                    <div className="assessment-section-heading-row">
                      <div>
                        <h4>{weeklyReportTitle}</h4>
                        <span>Week covered: {weeklyReportRangeLabel}</span>
                      </div>
                      <div className="assessment-drawer-action-links">
                        <span>{weeklyDailyNoteReport.noteCount} notes reviewed</span>
                        {weeklyDailyNoteReportEditing ? (
                          <>
                            <button className="mini-btn" type="button" onClick={saveWeeklyDailyNoteReportEdit} disabled={weeklyDailyNoteReportSaving}>
                              <i className={`ti ${weeklyDailyNoteReportSaving ? "ti-loader-2" : "ti-device-floppy"}`} /> {weeklyDailyNoteReportSaving ? "Saving..." : "Save"}
                            </button>
                            <button className="mini-btn" type="button" onClick={cancelWeeklyDailyNoteReportEdit} disabled={weeklyDailyNoteReportSaving}>
                              <i className="ti ti-x" /> Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="mini-btn" type="button" onClick={startWeeklyDailyNoteReportEdit}>
                              <i className="ti ti-edit" /> Edit
                            </button>
                            <button className="mini-btn" data-readonly-allowed="true" type="button" onClick={() => downloadWeeklyDailyNoteReport()}>
                              <i className="ti ti-download" /> Download
                            </button>
                            <button className="mini-btn" data-readonly-allowed="true" type="button" onClick={() => {
                              setWeeklyDailyNoteReport(null);
                              setWeeklyDailyNoteReportEditing(false);
                              setWeeklyDailyNoteReportEditValues(null);
                            }}>
                              <i className="ti ti-x" /> Close
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {weeklyDailyNoteReport.generatedAt ? <small>Generated {formatDateTime(weeklyDailyNoteReport.generatedAt)}</small> : null}
                    {weeklyDailyNoteReportEditing ? (
                      <div className="weekly-report-editor">
                        <label className="ncp-form-field">
                          <span>Report title</span>
                          <input
                            type="text"
                            value={weeklyDailyNoteReportEditValues?.title || ""}
                            onChange={(event) => setWeeklyDailyNoteReportEditValues((current) => ({ ...(current || {}), title: event.target.value }))}
                          />
                        </label>
                        <label className="ncp-form-field">
                          <span>Main paragraph</span>
                          <textarea
                            rows={5}
                            value={weeklyDailyNoteReportEditValues?.reportParagraph || ""}
                            onChange={(event) => setWeeklyDailyNoteReportEditValues((current) => ({ ...(current || {}), reportParagraph: event.target.value }))}
                          />
                        </label>
                        <div className="weekly-report-editor-grid">
                          {weeklyReportSectionDefinitions.map(([title, sectionKey]) => (
                            <label className="ncp-form-field" key={sectionKey}>
                              <span>{title}</span>
                              <textarea
                                rows={5}
                                value={weeklyDailyNoteReportEditValues?.sections?.[sectionKey] || ""}
                                onChange={(event) => updateWeeklyDailyNoteReportSectionEdit(sectionKey, event.target.value)}
                              />
                            </label>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <>
                        {weeklyReportParagraph ? <p className="weekly-report-paragraph">{weeklyReportParagraph}</p> : null}
                        {weeklyReportLists.length ? (
                          <details className="weekly-report-details">
                            <summary>Supporting details</summary>
                            <div className="weekly-report-grid">
                              {weeklyReportLists.map(([title, items]) => (
                                <div className="weekly-report-section" key={title}>
                                  <strong>{title}</strong>
                                  <ul>
                                    {items.map((item, index) => <li key={`${title}-${index}`}>{item}</li>)}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          </details>
                        ) : null}
                      </>
                    )}
                  </div>
                ) : null}
                {probationNotesView === "CALENDAR" ? (
                  <div className="daily-note-calendar-wrap">
                    <div className="daily-note-calendar-toolbar">
                      <strong>{monthLabel(probationCalendarMonth)}</strong>
                      <label className="ncp-form-field" data-readonly-allowed="true">
                        <span>Month</span>
                        <input
                          type="month"
                          value={probationCalendarMonth}
                          onChange={(event) => setProbationCalendarMonth(event.target.value || monthInputValue(new Date()))}
                        />
                      </label>
                    </div>
                    <div className="daily-note-calendar-weekdays">
                      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => <span key={day}>{day}</span>)}
                    </div>
                    <div className="daily-note-calendar-grid">
                      {probationCalendarDays.map((day) => (
                        <div className={`daily-note-calendar-day ${day.inMonth ? "" : "muted"} ${day.today ? "today" : ""}`} key={day.date}>
                          <div className="daily-note-calendar-day-head">
                            <span>{day.dayNumber}</span>
                            {canManageDailyNotes ? (
                              <button aria-label={`Add note for ${formatDate(day.date)}`} type="button" onClick={() => openAddDailyNoteDrawer(day.date)}>
                                <i className="ti ti-plus" />
                              </button>
                            ) : null}
                          </div>
                          <div className="daily-note-calendar-events">
                            {day.notes.slice(0, 3).map((note) => (
                              <button data-readonly-allowed="true" type="button" onClick={() => setSelectedDailyNote(note)} key={dailyNoteKey(note)}>
                                <strong>{note.category || "Note"}</strong>
                                <span>{note.title || note.summary || "Daily note"}</span>
                              </button>
                            ))}
                            {day.notes.length > 3 ? (
                              <button
                                className="daily-note-calendar-more"
                                data-readonly-allowed="true"
                                type="button"
                                onClick={() => setCalendarDayNotes(day)}
                              >
                                +{day.notes.length - 3} more
                              </button>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="probation-note-toolbar">
                      <label className="ncp-form-field" data-readonly-allowed="true">
                        <span>List from</span>
                        <input
                          max={probationNoteRange.end}
                          onChange={(event) => {
                            setProbationNoteRange((current) => ({ ...current, start: event.target.value }));
                            setVisibleProbationNoteCount(10);
                          }}
                          type="date"
                          value={probationNoteRange.start}
                        />
                      </label>
                      <label className="ncp-form-field" data-readonly-allowed="true">
                        <span>List to</span>
                        <input
                          min={probationNoteRange.start}
                          onChange={(event) => {
                            setProbationNoteRange((current) => ({ ...current, end: event.target.value }));
                            setVisibleProbationNoteCount(10);
                          }}
                          type="date"
                          value={probationNoteRange.end}
                        />
                      </label>
                    </div>
                    <>
                      <DailyNoteList
                        notes={visibleProbationNotes}
                        onSelect={setSelectedDailyNote}
                        emptyMessage="No daily notes were found for the selected date range."
                      />
                      {probationNotes.length > visibleProbationNoteCount ? (
                        <button className="probation-view-more" data-readonly-allowed="true" type="button" onClick={() => setVisibleProbationNoteCount((current) => current + 10)}>
                          View more notes ({probationNotes.length - visibleProbationNoteCount} remaining)
                        </button>
                      ) : null}
                    </>
                  </>
                )}
              </section>
            </div>

            <aside className="assessment-drawer-section probation-staff-panel">
              <div className="assessment-section-heading-row">
                <h3>Assigned Care Provider Staff</h3>
                <button className="mini-btn" type="button" onClick={addProbationStaff}><i className="ti ti-plus" /> Add staff</button>
              </div>
              <div className="probation-staff-list">
                {assignedProbationStaff.map((staff, index) => (
                  <article className="probation-staff-edit-card" key={`${staff.name}-${staff.from}-${index}`}>
                    <span className="tiny-icon"><i className="ti ti-user-heart" /></span>
                    <div className="probation-staff-readonly">
                      <div className="probation-staff-title-row">
                        <strong>{staff.name || "Unnamed staff member"}</strong>
                        <small>{staff.role || "Role not set"}</small>
                      </div>
                      <div className="probation-staff-time-list">
                        <p><i className="ti ti-clock-up" /> <span>From</span> {staff.from ? formatDateTime(staff.from) : "Not set"}</p>
                        <p><i className="ti ti-clock-down" /> <span>To</span> {staff.to ? formatDateTime(staff.to) : "Not set"}</p>
                      </div>
                    </div>
                    <div className="probation-staff-row-actions">
                      <button aria-label={`Edit ${staff.name || "staff assignment"}`} type="button" onClick={() => editProbationStaff(staff)}><i className="ti ti-edit" /></button>
                      <button aria-label={`Remove ${staff.name || "staff assignment"}`} type="button" onClick={() => removeProbationStaff(staff.originalIndex)}><i className="ti ti-trash" /></button>
                    </div>
                  </article>
                ))}
                {!assignedProbationStaff.length ? <p className="probation-empty">No assigned care provider staff recorded yet.</p> : null}
              </div>
            </aside>
          </div>
        </Panel>
      ) : null}

      {showNegotiatedCarePlan ? (
        <div className="assessment-stage-embedded-record negotiated-care-plan-stage">
          <div className="assessment-section-heading-row assessment-section-heading-row--actions assessment-panel-heading-actions">
            <h2>Negotiated Care Plan</h2>
            <div className="assessment-drawer-action-links assessment-page-action-links probation-action-buttons">
              <button className="primary-action" type="button" onClick={() => setBehaviorAnalysisDrawerOpen(true)}>
                <i className="ti ti-chart-dots" /> Behavior Analysis
              </button>
              {stageCompleteAction()}
            </div>
          </div>
          <AssessmentRecordDetailPage
            embedded
            assessmentId={assessmentProcessId}
            assessmentOverride={assessment}
            extractedAssessmentDocument={extractedDocument}
            dailyNotesAnalysis={probationRecord?.dailyNotes || []}
            weeklyReports={weeklyReportsForNcp}
            recordId="negotiated-plan"
          />
        </div>
      ) : null}
      </div>

      <Drawer
        className="resident-action-drawer behavior-analysis-drawer"
        onClose={() => setBehaviorAnalysisDrawerOpen(false)}
        open={behaviorAnalysisDrawerOpen}
        placement="right"
        extra={(
          <button className="mini-btn action-print behavior-analysis-print-button" type="button" onClick={printBehaviorAnalysisReport}>
            <i className="ti ti-printer" /> Print Score
          </button>
        )}
        title="Behavior Analysis"
        width={960}
      >
        <div className="behavior-analysis-summary">
          <div className="behavior-analysis-score-card">
            <span>CBHS evidence score</span>
            <strong>{behaviorTierAnalysis.score}<small>/100</small></strong>
            <div className="behavior-analysis-score-meter">
              <i style={{ width: `${behaviorTierAnalysis.scorePercent}%` }} />
            </div>
          </div>
          <div className="behavior-analysis-tier-card">
            <span>Suggested tier</span>
            <strong>{behaviorTierAnalysis.tier ? `Tier ${behaviorTierAnalysis.tier}` : "Not enough evidence"}</strong>
            <small>{behaviorTierAnalysis.tierStandard?.hours || "More qualifying documentation needed"}</small>
          </div>
          <div className="behavior-analysis-tier-card">
            <span>Confidence</span>
            <strong>{behaviorTierAnalysis.confidence}</strong>
            <small>{behaviorTierAnalysis.noteCount} behavior note{behaviorTierAnalysis.noteCount === 1 ? "" : "s"} across {behaviorTierAnalysis.dayCount} day{behaviorTierAnalysis.dayCount === 1 ? "" : "s"}</small>
          </div>
        </div>

        <section className="assessment-drawer-section behavior-analysis-section behavior-analysis-evidence-summary">
          <div className="assessment-section-heading-row">
            <h3>Evidence Summary</h3>
            <span>
              {behaviorTierAnalysis.dateRange.start
                ? `${formatDate(behaviorTierAnalysis.dateRange.start)} - ${formatDate(behaviorTierAnalysis.dateRange.end)}`
                : "No dated behavior notes"}
            </span>
          </div>
          <div className="behavior-analysis-summary-grid">
            <div>
              <span>Top categories</span>
              <div className="behavior-analysis-compact-chips">
                {behaviorTierAnalysis.topCategories.length ? behaviorTierAnalysis.topCategories.map((item) => (
                  <b key={item.label}>{item.label}<small>{item.count}</small></b>
                )) : <em>No category summary</em>}
              </div>
            </div>
            <div>
              <span>Top qualifying selections</span>
              <div className="behavior-analysis-compact-chips">
                {behaviorTierAnalysis.topBehaviorSelections.length ? behaviorTierAnalysis.topBehaviorSelections.map((item) => (
                  <b key={item.label}>{item.label}<small>{item.count}</small></b>
                )) : <em>No qualifying selections</em>}
              </div>
            </div>
            <div>
              <span>All behavior selections captured</span>
              <div className="behavior-analysis-compact-chips">
                {behaviorTierAnalysis.topAllBehaviorSelections.length ? behaviorTierAnalysis.topAllBehaviorSelections.map((item) => (
                  <b key={item.label}>{item.label}<small>{item.count}</small></b>
                )) : <em>No behavior selections</em>}
              </div>
            </div>
          </div>
        </section>

        <section className="assessment-drawer-section behavior-analysis-section">
          <h3>Why This Score</h3>
          <div className="behavior-analysis-metrics">
            <div><span>Qualifying behavior types</span><strong>{behaviorTierAnalysis.qualifyingBehaviorCount}</strong></div>
            <div><span>Behavior selections captured</span><strong>{behaviorTierAnalysis.behaviorSelectionCount}</strong></div>
            <div><span>High-risk behavior selections</span><strong>{behaviorTierAnalysis.highRiskCount}</strong></div>
            <div><span>Significant/severe or worsening</span><strong>{behaviorTierAnalysis.severeCount}</strong></div>
            <div><span>One-to-one support language</span><strong>{behaviorTierAnalysis.oneToOneHits}</strong></div>
            <div><span>Multiple-staff language</span><strong>{behaviorTierAnalysis.multipleStaffHits}</strong></div>
            <div><span>Imminent-risk language</span><strong>{behaviorTierAnalysis.imminentRiskHits}</strong></div>
            <div><span>Documented support hours/day</span><strong>{behaviorTierAnalysis.documentedSupportHours.toFixed(1)}</strong></div>
          </div>
          {behaviorTierAnalysis.tierStandard ? (
            <div className="behavior-analysis-standard">
              <strong>{behaviorTierAnalysis.tierStandard.label}</strong>
              <p>{behaviorTierAnalysis.tierStandard.language}</p>
            </div>
          ) : (
            <p className="probation-empty">Add more behavior notes with qualifying behaviors, frequency, magnitude, trend, intervention, and staffing support details to estimate a CBHS tier.</p>
          )}
        </section>

        <section className="assessment-drawer-section behavior-analysis-section">
          <div className="assessment-section-heading-row">
            <h3>Tier-Specific Indicator Scores</h3>
          </div>
          <div className="behavior-analysis-tier-score-list">
            {behaviorTierAnalysis.tierScores.map((tierScore) => {
              const standard = cbhsTierStandards.find((item) => item.tier === tierScore.tier);
              return (
                <article className={`behavior-analysis-tier-score ${behaviorTierAnalysis.tier === tierScore.tier ? "selected" : ""}`} key={tierScore.tier}>
                  <header>
                    <div>
                      <strong>Tier {tierScore.tier}</strong>
                      <span>{standard?.hours}</span>
                    </div>
                    <b>{tierScore.score}%</b>
                  </header>
                  <div className="behavior-analysis-score-meter">
                    <i style={{ width: `${tierScore.score}%` }} />
                  </div>
                  <ul>
                    {tierScore.indicators.map((indicator) => (
                      <li className={indicator.matched ? "matched" : ""} key={indicator.key}>
                        <i className={`ti ${indicator.matched ? "ti-circle-check" : "ti-circle"}`} />
                        <span>{indicator.label}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
        </section>

        <section className="assessment-drawer-section behavior-analysis-section behavior-analysis-audit-summary">
          <div>
            <h3>Need The Source Factors?</h3>
            <p>
              The score above is calculated from selected behavior categories, frequency/intensity indicators,
              dated behavior notes, support hours, and tier-specific documentation matches.
            </p>
          </div>
          <button className="mini-btn" type="button" onClick={() => setBehaviorAnalysisDetailsOpen(true)}>
            <i className="ti ti-list-details" /> View score factors
          </button>
        </section>
      </Drawer>

      <Modal
        className="behavior-analysis-details-modal"
        footer={null}
        onCancel={() => setBehaviorAnalysisDetailsOpen(false)}
        open={behaviorAnalysisDetailsOpen}
        title="How This Score Was Calculated"
        width={860}
      >
        <div className="behavior-analysis-factor-explainer">
          <p>
            These details are shown only for audit and review. They explain which behavior selections,
            note dates, support indicators, and tier rules contributed to the score.
          </p>
          <div className="behavior-analysis-factor-grid">
            <div><span>Qualifying behavior types</span><strong>{behaviorTierAnalysis.qualifyingBehaviorCount}</strong></div>
            <div><span>Behavior selections captured</span><strong>{behaviorTierAnalysis.behaviorSelectionCount}</strong></div>
            <div><span>High-risk behavior selections</span><strong>{behaviorTierAnalysis.highRiskCount}</strong></div>
            <div><span>Significant/severe or worsening</span><strong>{behaviorTierAnalysis.severeCount}</strong></div>
            <div><span>One-to-one support language</span><strong>{behaviorTierAnalysis.oneToOneHits}</strong></div>
            <div><span>Multiple-staff language</span><strong>{behaviorTierAnalysis.multipleStaffHits}</strong></div>
            <div><span>Imminent-risk language</span><strong>{behaviorTierAnalysis.imminentRiskHits}</strong></div>
            <div><span>Documented support hours/day</span><strong>{behaviorTierAnalysis.documentedSupportHours.toFixed(1)}</strong></div>
          </div>
        </div>

        <section className="behavior-analysis-details-section">
          <div className="assessment-section-heading-row">
            <h3>Supporting Observation Details</h3>
            <span>{behaviorTierAnalysis.observations.length} summarized</span>
          </div>
          <div className="behavior-analysis-observation-list">
            {behaviorTierAnalysis.observations.map((item, index) => {
              const note = item.note || {};
              const noteSummary = stripHtmlText(note.summary || note.notes || "") || "No written note summary added.";
              const intervention = stripHtmlText(note.interventionAction || note.intervention || note.actionTaken || "");
              const keywordHitList = Object.entries(item.keywordHits || {})
                .flatMap(([key, hits]) => hits.map((hit) => `${key.replace(/([A-Z])/g, " $1").toLowerCase()}: ${hit}`));
              return (
                <article className="behavior-analysis-observation" key={dailyNoteKey(note) || `${noteSummary}-${index}`}>
                  <header>
                    <strong>{note.category || "Behavior"} note</strong>
                    <span>{dailyNoteTimeRangeLabel(note)}</span>
                  </header>
                  <p>{noteSummary}</p>
                  {item.behaviorSelections.length ? (
                    <div className="behavior-analysis-chip-row">
                      {item.behaviorSelections.map((label) => <span key={label}>{label}</span>)}
                    </div>
                  ) : null}
                  {intervention ? <p><b>Intervention:</b> {intervention}</p> : null}
                  {keywordHitList.length ? (
                    <small>Matched evidence: {keywordHitList.join("; ")}</small>
                  ) : null}
                </article>
              );
            })}
            {!behaviorTierAnalysis.observations.length ? (
              <p className="probation-empty">No behavior evidence was found in the daily notes for this assessment.</p>
            ) : null}
          </div>
        </section>
      </Modal>

      <Modal
        className="assessment-document-preview-modal"
        footer={null}
        onCancel={() => setPreliminaryPreviewOpen(false)}
        open={preliminaryPreviewOpen}
        title="Preliminary Service Plan Preview"
        width={980}
      >
        <div className="assessment-document-preview-window preliminary-plan-preview-window">
          <div className="assessment-document-preview-toolbar">
            <div>
              <span>Preliminary Plan</span>
              <strong>{processResidentName(assessment)}</strong>
            </div>
            <button className="mini-btn" type="button" onClick={printPreliminaryPlan}><i className="ti ti-printer" /> Print</button>
          </div>
          <div className="assessment-document-preview-body">
            <div className="assessment-record-notes preliminary-plan-preview-content" dangerouslySetInnerHTML={{ __html: preliminaryFormToHtml(preliminaryPlanForm) }} />
          </div>
        </div>
      </Modal>
      <Modal
        className="assessment-document-preview-modal"
        footer={null}
        onCancel={closeFilePreview}
        open={Boolean(previewFile)}
        title={previewName}
        width={1040}
      >
        <div className="assessment-document-preview-window state-assessment-preview-window">
          <div className="assessment-document-preview-toolbar">
            <div>
              <span>{previewGalleryTitle}</span>
              <strong>{previewName}</strong>
              {previewDisplayIndex >= 0 ? (
                <small>Page {previewDisplayIndex + 1} of {previewGalleryCount}</small>
              ) : null}
            </div>
            <div className="state-assessment-preview-actions">
              {canNavigatePreviewGallery ? (
                <>
                  <button
                    aria-label="Previous assessment page"
                    className="mini-btn icon-only"
                    type="button"
                    onClick={() => showPreviewAt(previewDisplayIndex - 1)}
                  >
                    <i className="ti ti-chevron-left" />
                  </button>
                  <button
                    aria-label="Next assessment page"
                    className="mini-btn icon-only"
                    type="button"
                    onClick={() => showPreviewAt(previewDisplayIndex + 1)}
                  >
                    <i className="ti ti-chevron-right" />
                  </button>
                </>
              ) : null}
              <button
                aria-label="Rotate assessment page left"
                className="mini-btn icon-only"
                type="button"
                onClick={() => rotatePreview(-90)}
              >
                <i className="ti ti-rotate-2" />
              </button>
              <button
                aria-label="Rotate assessment page right"
                className="mini-btn icon-only"
                type="button"
                onClick={() => rotatePreview(90)}
              >
                <i className="ti ti-rotate-clockwise-2" />
              </button>
              {previewUrl ? (
                <a className="mini-btn" href={previewUrl} target="_blank" rel="noreferrer">
                  <i className="ti ti-external-link" /> Open
                </a>
              ) : null}
            </div>
          </div>
          <div className={`state-assessment-preview-body ${previewRotatedSideways ? "rotated-sideways" : ""}`}>
            {previewFile && canRenderAssessmentImage(previewFile) && previewUrl ? (
              <img
                alt={previewName}
                src={previewUrl}
                style={{ transform: `rotate(${previewRotation}deg)` }}
                onError={(event) => {
                  event.currentTarget.hidden = true;
                  event.currentTarget.parentElement?.classList.add("state-assessment-preview-failed");
                }}
              />
            ) : previewFile && isPdfAssessmentFile(previewFile) && previewUrl ? (
              <iframe
                title={previewName}
                src={previewUrl}
                style={{ transform: `rotate(${previewRotation}deg)` }}
              />
            ) : (
              <div className="state-assessment-preview-placeholder">
                <i className="ti ti-file-description" />
                <strong>{previewName}</strong>
                <p>This file type cannot be previewed directly here. Use Open to view it in a new browser tab if a file URL is available.</p>
              </div>
            )}
          </div>
        </div>
      </Modal>
      <AssessmentExtractionDrawer
        assessmentId={assessmentResourceType === "residentAssessment" ? extractedAssessmentId : ""}
        initialDocument={extractedDocument}
        open={extractedDocumentDrawerOpen}
        onClose={() => setExtractedDocumentDrawerOpen(false)}
        onUseInCarePlan={continueExtractionToCarePlan}
      />
      <Drawer
        title={dailyNoteEditingNote ? "Edit Daily Note" : "Add Daily Note"}
        placement="right"
        open={dailyNoteDrawerOpen}
        onClose={() => {
          setDailyNoteDrawerOpen(false);
          setDailyNoteEditingNote(null);
          setShowDailyNoteResidentDob(false);
        }}
        width={760}
        className="resident-action-drawer daily-note-drawer"
      >
        <form
          className="assessment-drawer-form"
          onSubmit={(event) => {
            event.preventDefault();
            saveDailyNote();
          }}
        >
          <section className="assessment-drawer-section daily-note-resident-context">
            <div>
              <span>Resident</span>
              <strong>{residentDisplayName}</strong>
            </div>
            <div>
              <span>Date of birth</span>
              <strong>{showDailyNoteResidentDob && residentDateOfBirth ? formatMonthDay(residentDateOfBirth) : residentDateOfBirth ? "**/**" : "Not added"}</strong>
            </div>
            {residentDateOfBirth ? (
              <button type="button" onClick={() => setShowDailyNoteResidentDob((current) => !current)}>
                {showDailyNoteResidentDob ? "Hide" : "Show"}
              </button>
            ) : null}
          </section>

          <section className="assessment-drawer-section">
            <h3>Note Details</h3>
            <div className="assessment-drawer-grid daily-note-details-grid">
              <label className="ncp-form-field">
                <span>Note date</span>
                <input name="noteDate" type="date" value={dailyNoteDate} onChange={(event) => setDailyNoteDate(event.target.value)} />
              </label>
              <label className="ncp-form-field">
                <span>Start time</span>
                <input
                  name="startTime"
                  type="time"
                  value={dailyNoteApproximateTime}
                  onChange={(event) => setDailyNoteApproximateTime(event.target.value)}
                />
              </label>
              <label className="ncp-form-field">
                <span>End time</span>
                <input
                  name="endTime"
                  type="time"
                  value={dailyNoteEndTime}
                  onChange={(event) => setDailyNoteEndTime(event.target.value)}
                />
              </label>
            </div>
          </section>

          <section className="assessment-drawer-section">
            <div className="assessment-section-heading-row">
              <h3>Quick Selections</h3>
              <span>{dailyNoteQuickSelections.length} selected</span>
            </div>
            <div className="quick-note-mode-tabs" role="tablist" aria-label="Quick selection mode">
              {["SEARCH", "SELECT"].map((mode) => (
                <button
                  aria-selected={dailyNoteSelectionMode === mode}
                  className={dailyNoteSelectionMode === mode ? "active" : ""}
                  key={mode}
                  role="tab"
                  type="button"
                  onClick={() => {
                    setDailyNoteSelectionMode(mode);
                    setDailyNoteSelectionQuery("");
                    setShowAllDailyNoteSelections(false);
                  }}
                >
                  {mode === "SELECT" ? "Select" : "Search"}
                </button>
              ))}
            </div>
            {dailyNoteSelectionMode === "SELECT" ? (
              <div className="quick-note-search-panel quick-note-search-panel-single">
                <label className="quick-note-category-field">
                  <span>Category</span>
                  <select
                    name="category"
                    value={dailyNoteCategory}
                    onChange={(event) => {
                      setDailyNoteCategory(event.target.value);
                      setDailyNoteSelectionQuery("");
                      setShowAllDailyNoteSelections(false);
                      setDailyNoteQuickSelections([]);
                    }}
                  >
                    {dailyNoteCategories.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </label>
              </div>
            ) : (
              <div className="quick-note-search-panel quick-note-search-panel-single">
                <label className="quick-note-search-field">
                  <i className="ti ti-search" />
                  <input
                    value={dailyNoteSelectionQuery}
                    onChange={(event) => {
                      setDailyNoteSelectionQuery(event.target.value);
                      setShowAllDailyNoteSelections(false);
                    }}
                    placeholder="Search category or behavior"
                  />
                </label>
              </div>
            )}
            {dailyNoteCategory === "Other" ? (
              <label className="ncp-form-field ncp-form-field-wide">
                <span>Specify category</span>
                <input name="otherCategory" placeholder="Enter note category" value={dailyNoteOtherCategory} onChange={(event) => setDailyNoteOtherCategory(event.target.value)} />
              </label>
            ) : null}
            {visibleDailyNoteSelectionGroups.length ? (
              <>
                <div className={`quick-note-chip-grid${showAllDailyNoteSelections || dailyNoteSelectionQuery ? " is-expanded" : ""}`}>
                  {visibleDailyNoteSelectionGroups.map((group) => (
                    <div className="quick-note-tree-group" key={group.category}>
                      <strong>{group.category}</strong>
                      <div className="quick-note-tree-children">
                        {group.selections.map((selection) => (
                          <button
                            className={`quick-note-chip ${dailyNoteQuickSelections.some((item) => item.label === selection.label) ? "selected" : ""}`}
                            key={selection.path}
                            type="button"
                            onClick={() => toggleDailyNoteQuickSelection(selection)}
                          >
                            {dailyNoteQuickSelections.some((item) => item.label === selection.label) ? <i className="ti ti-check" /> : null}
                            <span>{selection.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {!dailyNoteSelectionQuery && dailyNoteSelectionTotal > DAILY_NOTE_VISIBLE_SELECTION_COUNT ? (
                  <button className="quick-note-view-more" type="button" onClick={() => setShowAllDailyNoteSelections((current) => !current)}>
                    {showAllDailyNoteSelections ? "Show less" : `View ${dailyNoteSelectionTotal - DAILY_NOTE_VISIBLE_SELECTION_COUNT} more`}
                  </button>
                ) : null}
              </>
            ) : (
              <p className="probation-empty">{dailyNoteSelectionMode === "SEARCH" ? "Search for a category or behavior." : "Use the notes field for this category."}</p>
            )}
            {dailyNoteQuickSelections.length ? (
              <div className="quick-note-detail-list">
                {dailyNoteQuickSelections.map((selection) => (
                  <div className="quick-note-detail-row" key={selection.label}>
                    <strong>{selection.label}</strong>
                    <label>
                      <span>Frequency</span>
                      <select
                        value={selection.frequency || ""}
                        onChange={(event) => updateDailyNoteQuickSelectionDetail(selection.label, "frequency", event.target.value)}
                      >
                        <option value="">Select</option>
                        {dailyNoteFrequencyOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>Magnitude</span>
                      <select
                        value={selection.magnitude || ""}
                        onChange={(event) => updateDailyNoteQuickSelectionDetail(selection.label, "magnitude", event.target.value)}
                      >
                        <option value="">Select</option>
                        {dailyNoteMagnitudeOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>Trend</span>
                      <select
                        value={selection.trend || ""}
                        onChange={(event) => updateDailyNoteQuickSelectionDetail(selection.label, "trend", event.target.value)}
                      >
                        <option value="">Select</option>
                        {dailyNoteTrendOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                ))}
              </div>
            ) : null}
          </section>

          <section className="assessment-drawer-section">
            <h3>Notes</h3>
            <CareRichTextEditor height={76} onChange={setDailyNoteRichText} value={dailyNoteRichText} />
          </section>

          <section className="assessment-drawer-section">
            <h3>Intervention Action</h3>
            <CareRichTextEditor height={76} onChange={setDailyNoteInterventionAction} value={dailyNoteInterventionAction} />
            <input name="interventionAction" readOnly type="hidden" value={dailyNoteInterventionAction} />
          </section>

          <section className="assessment-drawer-section">
            <h3>Attach Photo</h3>
            <div className="privacy-consent-badge">
              <i className="ti ti-shield-lock" />
              <span>Avoid photos that compromise resident privacy. Seek consent when possible.</span>
            </div>
            <label className="assessment-upload assessment-upload-compact">
              <input accept="image/*,.heic,.heif" className="assessment-file-input" disabled={dailyNotePhotoProcessing} multiple name="photos" onChange={addDailyNotePhotos} type="file" />
              <i className="ti ti-photo-plus" />
              <strong>{dailyNotePhotoProcessing ? "Converting HEIC photos..." : "Attach photos"}</strong>
              <small>Add one or more photos related to this daily note.</small>
            </label>
            {dailyNotePhotos.length ? (
              <div className="assessment-uploaded-doc-list">
                {dailyNotePhotos.map((photo, index) => (
                  <div className="assessment-uploaded-doc-row" key={`${photo.fileName}-${index}`}>
                    <button
                      aria-label={`Preview ${photo.fileName || "photo"}`}
                      className="assessment-attachment-thumb photo"
                      disabled={!dailyNotePhotoUrl(photo)}
                      onClick={() => openDailyNotePhotoPreview(photo)}
                      type="button"
                    >
                      {dailyNotePhotoUrl(photo) ? <img alt="" src={dailyNotePhotoUrl(photo)} /> : <i className="ti ti-photo" />}
                    </button>
                      <div>
                        <strong>{photo.fileName}</strong>
                        {photo.convertedFromHeic || photo.conversionWarning ? (
                          <small>
                            {photo.convertedFromHeic ? `Converted from ${photo.originalFileName}` : photo.conversionWarning}
                        </small>
                      ) : null}
                      <label className="ncp-form-field">
                        <span>Photo description</span>
                        <input
                          name={`dailyNotePhotoDescription-${index}`}
                          onChange={(event) => updateDailyNotePhotoDescription(index, event.target.value)}
                          placeholder="Photo description"
                          value={photo.description}
                        />
                      </label>
                    </div>
                    <div className="assessment-uploaded-doc-actions">
                      <button
                        className="preview"
                        type="button"
                        disabled={!dailyNotePhotoUrl(photo)}
                        onClick={() => openDailyNotePhotoPreview(photo)}
                        aria-label={`Preview ${photo.fileName || "photo"}`}
                      >
                        <i className="ti ti-eye" />
                      </button>
                      <button type="button" onClick={() => removeDailyNotePhoto(index)} aria-label={`Remove ${photo.fileName || "photo"}`}><i className="ti ti-x" /></button>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </section>

          <div className="assessment-drawer-actions">
            <button className="btn secondary" type="button" onClick={() => {
              setDailyNoteDrawerOpen(false);
              setDailyNoteEditingNote(null);
            }} disabled={dailyNoteSaving}>Cancel</button>
            <button className="btn primary" type="submit" disabled={dailyNoteSaving || dailyNotePhotoProcessing}>
              {dailyNoteSaving ? <><i className="ti ti-loader-2" /> Saving...</> : dailyNoteEditingNote ? "Save Changes" : "Save Daily Note"}
            </button>
          </div>
        </form>
      </Drawer>
      <Modal
        className="daily-note-calendar-notes-modal"
        footer={null}
        onCancel={() => setCalendarDayNotes(null)}
        open={Boolean(calendarDayNotes)}
        title={calendarDayNotes ? `Daily notes for ${formatDate(calendarDayNotes.date)}` : "Daily notes"}
        width={720}
      >
        <div className="daily-note-calendar-modal-list">
          {(calendarDayNotes?.notes || []).map((note) => (
            <button
              className="probation-note-card"
              key={dailyNoteKey(note)}
              type="button"
              onClick={() => {
                setCalendarDayNotes(null);
                setSelectedDailyNote(note);
              }}
            >
              <div className="probation-note-card-head">
                <div>
                  <strong>{note.title || "Daily note"}</strong>
                  <span>Note date: {formatDate(note.noteDate || dateInputValue(note.date))}</span>
                </div>
                <em>{note.category || "Note"}</em>
              </div>
              <div className="probation-note-card-meta">
                <span><i className="ti ti-user-plus" /> Added by {dailyNoteReporterName(note)}</span>
                <span><i className="ti ti-clock" /> {dailyNoteTimeRangeLabel(note)}</span>
                {note.photos?.length ? <span><i className="ti ti-photo" /> {note.photos.length} photo{note.photos.length === 1 ? "" : "s"}</span> : null}
              </div>
              {note.quickSelections?.length ? (
                <div className="quick-note-selected-list">
                  {normalizeDailyNoteQuickSelections(note.quickSelections).map((selection) => (
                    <span key={selection.label}>{dailyNoteQuickSelectionText(selection)}</span>
                  ))}
                </div>
              ) : null}
              <p>{note.summary}</p>
            </button>
          ))}
        </div>
      </Modal>
      <Drawer
        title="Daily Note Details"
        placement="right"
        open={Boolean(selectedDailyNote)}
        onClose={() => setSelectedDailyNote(null)}
        width={560}
        className="resident-action-drawer daily-note-drawer"
      >
        {selectedDailyNote ? (
          <div className="resident-drawer-content">
            <div className="assessment-drawer-action-links">
              <button disabled={assessmentIsCompleted} type="button" onClick={() => openEditDailyNoteDrawer(selectedDailyNote)}><i className="ti ti-edit" /> Edit</button>
              <button className="danger" disabled={assessmentIsCompleted} type="button" onClick={() => deleteDailyNote(selectedDailyNote)}><i className="ti ti-trash" /> Delete</button>
            </div>
            <section className="assessment-drawer-section">
              <h3>Note Details</h3>
              <div className="assessment-detail-grid">
                <div className="wide"><span>Title</span><strong>{selectedDailyNote.title}</strong></div>
                <div><span>Category</span><strong>{selectedDailyNote.category}</strong></div>
                <div><span>Note date</span><strong>{formatDate(selectedDailyNote.noteDate || dateInputValue(selectedDailyNote.date))}</strong></div>
                <div className="wide"><span>Time range</span><strong>{dailyNoteTimeRangeLabel(selectedDailyNote)}</strong></div>
                <div className="wide audit-pair"><span>Added</span><strong>{dailyNoteReporterName(selectedDailyNote)} at {formatDateTime(selectedDailyNote.date)}</strong></div>
                <div className="wide audit-pair"><span>Updated</span><strong>{dailyNoteUpdaterName(selectedDailyNote)} at {formatDateTime(selectedDailyNote.updatedAt || selectedDailyNote.date)}</strong></div>
              </div>
            </section>
            {selectedDailyNote.quickSelections?.length ? (
              <section className="assessment-drawer-section">
                <h3>Quick Selections</h3>
                <div className="quick-note-selected-list">
                  {normalizeDailyNoteQuickSelections(selectedDailyNote.quickSelections).map((selection) => (
                    <span key={selection.label}>{dailyNoteQuickSelectionText(selection)}</span>
                  ))}
                </div>
              </section>
            ) : null}
            <section className="assessment-drawer-section">
              <h3>Notes</h3>
              <div className="assessment-record-notes" dangerouslySetInnerHTML={{ __html: selectedDailyNote.notes || `<p>${selectedDailyNote.summary || ""}</p>` }} />
            </section>
            {selectedDailyNote.interventionAction || selectedDailyNote.intervention || selectedDailyNote.actionTaken ? (
              <section className="assessment-drawer-section">
                <h3>Intervention Action</h3>
                <div
                  className="assessment-record-notes"
                  dangerouslySetInnerHTML={{ __html: selectedDailyNote.interventionAction || selectedDailyNote.intervention || selectedDailyNote.actionTaken }}
                />
              </section>
            ) : null}
            <section className="assessment-drawer-section">
              <h3>Attached Photos</h3>
              {selectedDailyNote.photos?.length ? (
                <div className="assessment-uploaded-doc-list">
                  {selectedDailyNote.photos.map((photo, index) => (
                    <div className="assessment-uploaded-doc-row" key={`${photo.fileName}-${index}`}>
                      <button
                        aria-label={`Preview ${photo.fileName || "photo"}`}
                        className="assessment-attachment-thumb photo"
                        disabled={!dailyNotePhotoUrl(photo)}
                        onClick={() => openDailyNotePhotoPreview(photo)}
                        type="button"
                      >
                        {dailyNotePhotoUrl(photo) ? <img alt="" src={dailyNotePhotoUrl(photo)} /> : <i className="ti ti-photo" />}
                      </button>
                      <div>
                        <strong>{photo.fileName}</strong>
                        <small>{photo.description || photo.fileName}</small>
                        {photo.convertedFromHeic || photo.conversionWarning ? (
                          <small>
                            {photo.convertedFromHeic ? `Converted from ${photo.originalFileName}` : photo.conversionWarning}
                          </small>
                        ) : null}
                      </div>
                      <button
                        className="preview"
                        type="button"
                        disabled={!dailyNotePhotoUrl(photo)}
                        onClick={() => openDailyNotePhotoPreview(photo)}
                        aria-label={`Preview ${photo.fileName || "photo"}`}
                      >
                        <i className="ti ti-eye" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="probation-empty">No photos attached to this note.</p>
              )}
            </section>
          </div>
        ) : null}
      </Drawer>
      <Modal
        className="daily-note-photo-preview-modal"
        footer={null}
        onCancel={() => setDailyNotePhotoPreview(null)}
        open={Boolean(dailyNotePhotoPreview)}
        title={dailyNotePhotoPreview?.title || "Daily note photo"}
        width={760}
        zIndex={1600}
      >
        {dailyNotePhotoPreview ? (
          <div className="daily-note-photo-preview">
            <div className="assessment-document-preview-toolbar">
              <div>
                <span>Daily Note Photo</span>
                <strong>{dailyNotePhotoPreview.fileName || dailyNotePhotoPreview.originalFileName || "Photo"}</strong>
              </div>
              <a className="mini-btn" href={dailyNotePhotoPreview.url} target="_blank" rel="noreferrer">
                <i className="ti ti-external-link" /> Open
              </a>
            </div>
            <img alt={dailyNotePhotoPreview.title || "Daily note photo"} src={dailyNotePhotoPreview.url} />
            {dailyNotePhotoPreview.description ? <p>{dailyNotePhotoPreview.description}</p> : null}
          </div>
        ) : null}
      </Modal>
      <Drawer
        title={probationStaffDrawer?.index === null ? "Add Assigned Staff" : "Edit Assigned Staff"}
        placement="right"
        open={Boolean(probationStaffDrawer)}
        onClose={() => setProbationStaffDrawer(null)}
        width={560}
        className="resident-action-drawer daily-note-drawer"
      >
        {probationStaffDrawer ? (
          <form className="assessment-drawer-form">
            <section className="assessment-drawer-section">
              <h3>Staff Assignment</h3>
              <div className="assessment-drawer-grid">
                <label className="ncp-form-field">
                  <span>Name</span>
                  <input
                    value={probationStaffDrawer.values.name}
                    onChange={(event) => updateProbationStaffDrawerField("name", event.target.value)}
                    placeholder="Staff name"
                  />
                </label>
                <label className="ncp-form-field">
                  <span>Role</span>
                  <input
                    value={probationStaffDrawer.values.role}
                    onChange={(event) => updateProbationStaffDrawerField("role", event.target.value)}
                    placeholder="Care provider role"
                  />
                </label>
                <label className="ncp-form-field ncp-form-field-wide">
                  <span>From</span>
                  <input
                    type="datetime-local"
                    value={(probationStaffDrawer.values.from || "").slice(0, 16)}
                    onChange={(event) => updateProbationStaffDrawerField("from", event.target.value)}
                  />
                </label>
                <label className="ncp-form-field ncp-form-field-wide">
                  <span>To</span>
                  <input
                    type="datetime-local"
                    value={(probationStaffDrawer.values.to || "").slice(0, 16)}
                    onChange={(event) => updateProbationStaffDrawerField("to", event.target.value)}
                  />
                </label>
              </div>
            </section>
            <div className="assessment-drawer-actions">
              <button className="btn secondary" type="button" disabled={probationStaffSaving} onClick={() => setProbationStaffDrawer(null)}>Cancel</button>
              <button className="btn primary" type="button" disabled={probationStaffSaving} onClick={saveProbationStaffDrawer}>
                {probationStaffSaving ? <><i className="ti ti-loader-2" /> Saving...</> : "Save Staff"}
              </button>
            </div>
          </form>
        ) : null}
      </Drawer>
    </CareShell>
  );
}
